import React, { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect, useImperativeHandle } from 'react';
import { Trash2, FilePenLine, ArrowUpRight, ArrowDownRight, X, Plus, MessageSquarePlus, Tag, Lock, ChevronDown } from 'lucide-react';
import ReactDOM from 'react-dom';
import { privateTradingScopeChangedEventName, readPrivateTradingJson, writePrivateTradingJson } from '../../lib/storage/privateTradingStorage';
import { ImBold, ImStrikethrough } from 'react-icons/im';
import PnLChart from '../charts/PnLChart';
import { formatNyDateTime } from '../../utils/formatters';
import { formatDateTimeInTimezone } from '../../lib/time/displayTimezone';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import { useImportedTradesRecords } from '../../hooks/useImportedTrades';
import { saveImportedTrades, IMPORTED_TRADES_UPDATED_EVENT } from '../../utils/importedTradesStorage';
import CreateCustomEntryModal, { type ManualEntryPayload } from '../ui/CreateCustomEntryModal';
import { listCsvSourceAccounts, type CsvSourceAccountOption } from '../../lib/import-engine/csvImportPersistenceSync';
import { readJournalManualTradeDraft, retryJournalManualTrade, saveJournalManualTrade } from '../../lib/trades/journalManualTradeDraft';
import {
    getTradeDateKey,
    getTradeTimestampMs,
    getTradeVolume,
    loadCustomTrades,
    saveCustomTrades as persistCustomTrades,
} from '../../lib/trades/authoritativeTradeData';
import { getCanonicalAssetClass } from '../../lib/trades/canonicalAssetClass';
import { getTenantAssetBlockedMessage } from '../../lib/trades/tenantTradeScope';
import { loadTrades } from '../../lib/trades/tradeRepository';
import { normalizeTimestampToMs } from '../../lib/time/normalizeTimestampToMs';
import type { Trade } from '../../types';
import { buildTenantStorageKey, readTenantStorageItem, writeTenantStorageItem } from '../../lib/storage/tenantStorage';
import { calculateWinRatePercent } from '../../lib/trades/winRate';
import { readTradingSettings } from '../../config/tradingSettingsDefaults';
import { resolveTradeInitialRiskValue } from '../../lib/trades/tradeInitialRisk';
import { computeDirectionalTicks, resolveFuturesTickSize } from '../../constants/contractMultipliers';
import { resolveJournalExecutionPnl } from '../../lib/trades/journalPnl';
import { buildJournalTradesByDay } from './journalTradeGrouping';
import { useAssetClassVisibility, type AssetClassVisibility } from '../../lib/assetClassVisibility';
import { useDemoTradesOverride } from '../../demo/demoMode';
import RemovableTag from '../ui/RemovableTag';
import StandardPageHeader from './StandardPageHeader';

// Safe localStorage access helpers (guard for SSR and try/catch around storage access)
function safeGetItem(key: string): string | null {
    return readTenantStorageItem(buildTenantStorageKey(key), [key]);
}
function safeSetItem(key: string, value: string): void {
    writeTenantStorageItem(buildTenantStorageKey(key), value);
}

// The following component implements the new Journal UI as provided, adapted to a named export.

// ----------------------------- ICONS ------------------------------------
// (theme icons removed — light/dark mode handled globally elsewhere)

// Additional icons used throughout the page
const ChevronDownIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m6 9 6 6 6-6" />
    </svg>
);
const BoldIcon = ({ className }: { className?: string }) => (
    <ImBold className={className} />
);
const ItalicIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>
);
const UnderlineIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3"/><line x1="4" y1="21" x2="20" y2="21"/></svg>
);
const StrikethroughIcon = ({ className }: { className?: string }) => (
    <ImStrikethrough className={className} />
);
const UndoIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
);
const RedoIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3-2.3"/></svg>
);
const AlignLeftIcon = ({ className }: { className?: string }) => (
    // Lucide-style 'text-align-start' icon: a left aligned paragraph with a short line at the top
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h14" />
        <path d="M3 10h10" />
        <path d="M3 14h14" />
        <path d="M3 18h10" />
        <rect x="1" y="4" width="0" height="0" fill="none" />
    </svg>
);
const AlignCenterIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="10" x2="3" y2="10"/><line x1="17" y1="6" x2="7" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="7" y2="18"/></svg>
);
const AlignRightIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="10" x2="7" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="7" y2="18"/></svg>
);
const ListIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/></svg>
);
const LinkIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"/></svg>
);
const ImageIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
);
const CalendarIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
);
// Search icon removed
const ChevronLeftIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
);
const ChevronRightIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
);
const CheckSmallIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);

const SettingsIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15-.08a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const XIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);

// (message-share icon removed — not used)

function getJournalTradeKey(trade: Record<string, unknown>): string {
    return String(trade?.storageId ?? trade?.id ?? `${trade?.symbol}-${trade?.timestamp}`);
}

function isJournalDemoTrade(trade: Record<string, unknown> | null | undefined): boolean {
    if (!trade) return false;
    return trade.isDemo === true || trade.source === 'demo' || trade.sourceType === 'demo';
}

const JOURNAL_TRADE_TAG_SUGGESTIONS = ['Trend Day', 'Choppy', 'FOMC', 'Reversal', 'Breakout', 'Range Bound', 'High Volatility', 'Low Volume', 'Long Term'];
// Toolbar for contentEditable editor
const RichTextToolbar = ({ onCommand, activeFormats, isDark = false, currentColor, withSeparators = false, compactSpacing = false }: { onCommand: (command: string) => void; activeFormats: Set<string>; isDark?: boolean; currentColor?: string; withSeparators?: boolean; compactSpacing?: boolean }) => {
    const labels: Record<string, string> = {
        bold: 'Bold (Ctrl+B)',
        italic: 'Italic (Ctrl+I)',
        underline: 'Underline (Ctrl+U)',
        strikeThrough: 'Strikethrough',
        undo: 'Undo (Ctrl+Z)',
        redo: 'Redo (Ctrl+Y / Ctrl+Shift+Z)',
        justifyLeft: 'Align left',
        justifyCenter: 'Align center',
        justifyRight: 'Align right',
        insertUnorderedList: 'Bulleted list',
        insertOrderedList: 'Numbered list',
        createLink: 'Insert link',
        insertImage: 'Insert image',
        fontSize: 'Text size',
        foreColor: 'Text color'
    };
    const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const btnBase = `${dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-700 hover:bg-zinc-100'} inline-flex items-center justify-center w-8 h-8 rounded-md`;
    const btnActive = `${dark ? 'bg-zinc-700 text-zinc-50' : 'bg-zinc-200 text-zinc-900'}`;
    const Section = ({ children, divider = false, mrClass = 'mr-2' }: { children: React.ReactNode; divider?: boolean; mrClass?: string }) => (
        <div className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} flex items-center gap-1 pr-2 ${mrClass} ${divider ? 'border-r' : ''}`}>
            {children}
        </div>
    );
    const [colorOpen, setColorOpen] = useState(false);
    // Guard to prevent immediate re-open if a click closes the popover and then lands on the toggle button underneath
    const suppressColorToggleRef = useRef(false);
    const colorWrapRef = useRef<HTMLDivElement | null>(null);
    const [showAdvancedColor, setShowAdvancedColor] = useState(false);
    const [isPickingColor, setIsPickingColor] = useState(false);
    const [customHex, setCustomHex] = useState<string>('#000000');
    // HSV state for advanced picker
    const [hsv, setHsv] = useState<{ h: number; s: number; v: number }>({ h: 0, s: 0, v: 0 });
    const svRef = useRef<HTMLDivElement | null>(null);
    const hueRef = useRef<HTMLDivElement | null>(null);

    const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
    const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
        const m = normalizeHex(hex);
        if (!m) return null;
        const r = parseInt(m.slice(1, 3), 16);
        const g = parseInt(m.slice(3, 5), 16);
        const b = parseInt(m.slice(5, 7), 16);
        return { r, g, b };
    };
    const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }): string => {
        const to = (x: number) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0');
        return `#${to(r)}${to(g)}${to(b)}`;
    };
    const rgbToHsv = ({ r, g, b }: { r: number; g: number; b: number }): { h: number; s: number; v: number } => {
        const rr = r / 255, gg = g / 255, bb = b / 255;
        const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
        const d = max - min;
        let h = 0;
        if (d !== 0) {
            switch (max) {
                case rr: h = ((gg - bb) / d) % 6; break;
                case gg: h = (bb - rr) / d + 2; break;
                default: h = (rr - gg) / d + 4; break;
            }
            h *= 60; if (h < 0) h += 360;
        }
        const s = max === 0 ? 0 : d / max;
        const v = max;
        return { h, s, v };
    };
    const hsvToRgb = ({ h, s, v }: { h: number; s: number; v: number }): { r: number; g: number; b: number } => {
        const c = v * s;
        const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = v - c;
        let rr = 0, gg = 0, bb = 0;
        if (h >= 0 && h < 60) { rr = c; gg = x; bb = 0; }
        else if (h < 120) { rr = x; gg = c; bb = 0; }
        else if (h < 180) { rr = 0; gg = c; bb = x; }
        else if (h < 240) { rr = 0; gg = x; bb = c; }
        else if (h < 300) { rr = x; gg = 0; bb = c; }
        else { rr = c; gg = 0; bb = x; }
        return { r: (rr + m) * 255, g: (gg + m) * 255, b: (bb + m) * 255 };
    };
    // Keep selection in editor when clicking toolbar buttons
    const keepSelectionMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        // Prevent button focus from stealing selection from contentEditable
        e.preventDefault();
    };

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (!colorOpen) return;
            if (isPickingColor) return; // don't close while dragging in the picker
            const wrap = colorWrapRef.current;
            if (wrap && wrap.contains(e.target as Node)) return;
            setColorOpen(false);
            setShowAdvancedColor(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setColorOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [colorOpen, isPickingColor]);

    useEffect(() => {
        if (colorOpen) {
            // Initialize custom picker with current color when popover opens
            const hx = (currentColor && /^#([0-9a-f]{6})$/i.test(currentColor)) ? currentColor : '#000000';
            setCustomHex(hx);
            const rgb = hexToRgb(hx) || { r: 0, g: 0, b: 0 };
            setHsv(rgbToHsv(rgb));
        } else {
            setShowAdvancedColor(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorOpen]);

    const normalizeHex = (v: string): string | null => {
        try {
            let s = v.trim();
            if (!s) return null;
            if (s[0] !== '#') s = '#' + s;
            if (s.length === 4) {
                // #rgb -> #rrggbb
                const r = s[1], g = s[2], b = s[3];
                s = `#${r}${r}${g}${g}${b}${b}`;
            }
            if (/^#([0-9a-f]{6})$/i.test(s)) return s.toLowerCase();
            return null;
        } catch { return null; }
    };
    return (
        <div className={`p-2 border-b ${dark ? 'border-zinc-800' : 'border-zinc-200'} flex flex-wrap items-center`}>
            <Section divider={withSeparators}>
                <button type="button" title={labels.bold} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('bold')} className={`${btnBase} ${activeFormats.has('bold') ? btnActive : ''}`}><BoldIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.italic} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('italic')} className={`${btnBase} ${activeFormats.has('italic') ? btnActive : ''}`}><ItalicIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.underline} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('underline')} className={`${btnBase} ${activeFormats.has('underline') ? btnActive : ''}`}><UnderlineIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.strikeThrough} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('strikeThrough')} className={`${btnBase} ${activeFormats.has('strikeThrough') ? btnActive : ''}`}><StrikethroughIcon className="w-4 h-4" /></button>
            </Section>
            <Section divider={withSeparators}>
                <button type="button" title={labels.undo} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('undo')} className={btnBase}><UndoIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.redo} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('redo')} className={btnBase}><RedoIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.createLink} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('createLink')} className={`${btnBase} ${activeFormats.has('createLink') ? btnActive : ''}`}><LinkIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.insertImage} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertImage')} className={btnBase}><ImageIcon className="w-4 h-4" /></button>
            </Section>
            <Section divider={withSeparators} mrClass={compactSpacing ? 'mr-0.5' : 'mr-2'}>
                <button type="button" title={labels.justifyLeft} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyLeft')} className={`${btnBase} ${activeFormats.has('justifyLeft') ? btnActive : ''}`}><AlignLeftIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.justifyCenter} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyCenter')} className={`${btnBase} ${activeFormats.has('justifyCenter') ? btnActive : ''}`}><AlignCenterIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.justifyRight} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyRight')} className={`${btnBase} ${activeFormats.has('justifyRight') ? btnActive : ''}`}><AlignRightIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.insertUnorderedList} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertUnorderedList')} className={`${btnBase} ${activeFormats.has('insertUnorderedList') ? btnActive : ''}`}><ListIcon className="w-4 h-4" /></button>
                <button type="button" title={labels.insertOrderedList} onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertOrderedList')} className={`${btnBase} ${activeFormats.has('insertOrderedList') ? btnActive : ''}`}>
                    {/* Simple ordered list icon */}
                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="10" y1="6" x2="21" y2="6" />
                        <line x1="10" y1="12" x2="21" y2="12" />
                        <line x1="10" y1="18" x2="21" y2="18" />
                        <path d="M4 6h.01" />
                        <path d="M4 12h.01" />
                        <path d="M4 18h.01" />
                    </svg>
                </button>
            </Section>
            <Section divider={false}>
                {/* Text size dropdown */}
                <label className="sr-only" htmlFor="rte-font-size">{labels.fontSize}</label>
                <select
                    id="rte-font-size"
                    title={labels.fontSize}
                    onChange={(e) => onCommand(`fontSize|${e.target.value}`)}
                    defaultValue="3"
                    className={`${dark ? 'bg-zinc-900 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-800'} h-8 rounded-md border px-1 text-xs`}
                >
                    <option value="2">Small</option>
                    <option value="3">Normal</option>
                    <option value="4">Medium</option>
                    <option value="5">Large</option>
                </select>
                {/* Text color picker (popover positioned below the icon) */}
                <div ref={colorWrapRef} className="relative inline-block">
                    <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={colorOpen}
                        title={labels.foreColor}
                        onMouseDown={keepSelectionMouseDown}
                        onClick={() => {
                            if (suppressColorToggleRef.current) return;
                            setColorOpen((s) => !s);
                        }}
                        className={`${btnBase} relative`}
                    >
                        {/* square swatch showing current color */}
                        <span
                            aria-hidden
                            className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} inline-block w-5 h-5 rounded-sm border`}
                            style={{ backgroundColor: currentColor || '#000000' }}
                        />
                    </button>
                    {colorOpen && (
                        <div className={`absolute top-full right-0 mt-1 z-50 ${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white border border-zinc-200 text-zinc-900'} rounded-md shadow-lg p-2 min-w-[12rem]`}
                            aria-label="Choose text color"
                        >
                            {!showAdvancedColor ? (
                                <>
                                    <div className="grid grid-cols-8 gap-1 p-1">
                                        {[
                                            '#000000','#4b5563','#6b7280','#9ca3af','#d1d5db','#ffffff',
                                            '#ef4444','#f97316','#f59e0b','#eab308','#84cc16','#22c55e','#10b981','#14b8a6',
                                            '#06b6d4','#0ea5e9','#3b82f6','#6366f1','#8b5cf6','#a855f7','#d946ef','#ec4899'
                                        ].map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                title={c}
                                                onMouseDown={keepSelectionMouseDown}
                                                onClick={() => { onCommand(`foreColor|${c}`); setColorOpen(false); }}
                                                className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} w-5 h-5 rounded-sm border hover:ring-2 hover:ring-blue-500`}
                                                style={{ backgroundColor: c }}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onMouseDown={keepSelectionMouseDown}
                                        className={`${dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-700 hover:bg-zinc-100'} w-full text-left px-2 py-1 rounded-md text-sm`}
                                        onClick={() => {
                                            setShowAdvancedColor(true);
                                            const hx = (currentColor && /^#([0-9a-f]{6})$/i.test(currentColor)) ? currentColor : '#000000';
                                            setCustomHex(hx);
                                            const rgb = hexToRgb(hx) || { r: 0, g: 0, b: 0 };
                                            setHsv(rgbToHsv(rgb));
                                        }}
                                    >
                                        Custom…
                                    </button>
                                </>
                            ) : (
                                <div className="p-2 space-y-3" onMouseDown={(e) => { e.stopPropagation(); }}>
                                    {/* SV square */}
                                    <div
                                        ref={svRef}
                                        className="relative w-48 h-32 cursor-crosshair rounded-sm overflow-hidden"
                                        style={{
                                            background: `linear-gradient(to top, black, transparent), linear-gradient(to right, white, hsl(${hsv.h}, 100%, 50%))`
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsPickingColor(true);
                                            const box = svRef.current!.getBoundingClientRect();
                                            const update = (clientX: number, clientY: number) => {
                                                const s = clamp((clientX - box.left) / box.width, 0, 1);
                                                const v = clamp(1 - (clientY - box.top) / box.height, 0, 1);
                                                const next = { h: hsv.h, s, v };
                                                setHsv(next);
                                                const hex = rgbToHex(hsvToRgb(next));
                                                setCustomHex(hex);
                                                // Apply continuously while dragging
                                                const hx = normalizeHex(hex);
                                                if (hx) onCommand(`foreColor|${hx}`);
                                            };
                                            update(e.clientX, e.clientY);
                                            const onMove = (ev: MouseEvent) => update(ev.clientX, ev.clientY);
                                            const onUp = () => {
                                                document.removeEventListener('mousemove', onMove);
                                                document.removeEventListener('mouseup', onUp);
                                                setIsPickingColor(false);
                                            };
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                        }}
                                    >
                                        {/* SV thumb */}
                                        <div
                                            className="absolute w-3 h-3 rounded-full border border-white shadow"
                                            style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                        />
                                    </div>
                                    {/* Hue slider */}
                                    <div
                                        ref={hueRef}
                                        className="relative w-48 h-3 rounded-sm cursor-pointer"
                                        style={{ background: 'linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)' }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsPickingColor(true);
                                            const box = hueRef.current!.getBoundingClientRect();
                                            const update = (clientX: number) => {
                                                const x = clamp((clientX - box.left) / box.width, 0, 1);
                                                const h = x * 360;
                                                const next = { ...hsv, h };
                                                setHsv(next);
                                                const hex = rgbToHex(hsvToRgb(next));
                                                setCustomHex(hex);
                                                // Apply continuously while dragging
                                                const hx = normalizeHex(hex);
                                                if (hx) onCommand(`foreColor|${hx}`);
                                            };
                                            update(e.clientX);
                                            const onMove = (ev: MouseEvent) => update(ev.clientX);
                                            const onUp = () => {
                                                document.removeEventListener('mousemove', onMove);
                                                document.removeEventListener('mouseup', onUp);
                                                setIsPickingColor(false);
                                            };
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                        }}
                                    >
                                        {/* Hue thumb */}
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border border-white shadow"
                                            style={{ left: `${(hsv.h / 360) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                        />
                                    </div>
                                    {/* Hex input + preview */}
                                    <div className="flex items-center gap-2">
                                        <div className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} w-6 h-6 rounded-sm border`} style={{ backgroundColor: normalizeHex(customHex) || '#000000' }} />
                                        <input
                                            type="text"
                                            value={customHex}
                                            onChange={(e) => {
                                                const v = e.target.value.replace(/[^#0-9a-fA-F]/g, '');
                                                const val = v.startsWith('#') ? v : ('#' + v).replace(/##+/, '#');
                                                setCustomHex(val);
                                                const rgb = hexToRgb(val);
                                                if (rgb) setHsv(rgbToHsv(rgb));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const hx = normalizeHex(customHex);
                                                    if (hx) {
                                                        onCommand(`foreColor|${hx}`);
                                                        setIsPickingColor(false);
                                                        setShowAdvancedColor(false);
                                                        // Defer closing to avoid the click landing on the toggle button underneath
                                                        suppressColorToggleRef.current = true;
                                                        setColorOpen(false);
                                                        setTimeout(() => { suppressColorToggleRef.current = false; }, 0);
                                                    }
                                                }
                                            }}
                                            className={`${dark ? 'bg-zinc-900 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'} flex-1 px-2 py-1 rounded-md border text-sm`}
                                            placeholder="#rrggbb"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            className={`${dark ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-800'} px-2 py-1 rounded-md text-sm`}
                                            onMouseDown={(e) => {
                                                // Close immediately on press to avoid re-open or focus quirks
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsPickingColor(false);
                                                setShowAdvancedColor(false);
                                                suppressColorToggleRef.current = true;
                                                setColorOpen(false);
                                                setTimeout(() => { suppressColorToggleRef.current = false; }, 0);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={`px-2 py-1 rounded-md text-sm ${dark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                            onMouseDown={(e) => {
                                                // Apply and close on press; ensures popover unmounts even if click is canceled
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const hx = normalizeHex(customHex);
                                                if (hx) {
                                                    onCommand(`foreColor|${hx}`);
                                                }
                                                setIsPickingColor(false);
                                                setShowAdvancedColor(false);
                                                suppressColorToggleRef.current = true;
                                                setColorOpen(false);
                                                setTimeout(() => { suppressColorToggleRef.current = false; }, 0);
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Section>

        </div>
    );
};

// ----------------------------- Formatters -----------------------------
function fmtMoney(amount: number): string {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', signDisplay: 'auto' }).format(amount);
    } catch {
        return `${amount < 0 ? '-' : ''}$${Math.abs(amount).toFixed(2)}`;
    }
}

function fmtWeekDay(isoDate: string, format: 'long' | 'short' = 'long'): string {
    const d = new Date(isoDate + 'T00:00:00');
    const options = format === 'short' ? { month: 'short', day: 'numeric' } : { weekday: 'long', month: 'long', day: 'numeric' };
    return d.toLocaleDateString(undefined, options as Intl.DateTimeFormatOptions);
}

function fmtDayNum(isoDate: string): string {
    try {
        const d = new Date(isoDate + 'T00:00:00');
        const day = d.getDate();
        return String(day);
    } catch {
        return '';
    }
}

// Returns epoch ms for a trade's time if available; otherwise null.
function getTradeTimeMs(trade: Partial<Trade> | null | undefined): number | null {
    try {
        const t = trade as Record<string, unknown> | undefined;
        const raw = t?.['time'] ?? t?.['timestamp'] ?? t?.['executedAt'] ?? t?.['filledAt'] ?? t?.['openedAt'] ?? t?.['entryTime'] ?? t?.['dateTime'] ?? null;
        if (raw == null) return null;
        if (typeof raw === 'number') {
            return raw < 1e11 ? raw * 1000 : raw;
        }
        const d = new Date(String(raw));
        return Number.isFinite(d.getTime()) ? d.getTime() : null;
    } catch {
        return null;
    }
}

type JournalTradeRecord = Partial<Trade> & Record<string, unknown>;
type JournalAnyTrade = Parameters<typeof getTradeVolume>[0];

function toAuthoritativeTrade(trade: JournalTradeRecord): JournalAnyTrade {
    return trade as unknown as JournalAnyTrade;
}

function getJournalTradeTimestampMs(trade: JournalTradeRecord | null | undefined): number | null {
    if (!trade) return null;
    return getTradeTimestampMs(toAuthoritativeTrade(trade)) ?? getTradeTimeMs(trade);
}

function getJournalTradeDateKey(trade: JournalTradeRecord | null | undefined): string {
    if (!trade) return '';
    return getTradeDateKey(toAuthoritativeTrade(trade)) ?? String(trade?.date ?? '');
}

function getJournalTradeCloseTimestampMs(trade: JournalTradeRecord | null | undefined): number | null {
    if (!trade) return null;
    return normalizeTimestampToMs(
        trade?.closeTimestampMs
        ?? trade?.closeTime
        ?? trade?.closeDate
        ?? trade?.closedAt
        ?? trade?.closed_at
        ?? trade?.closed_at_utc
        ?? trade?.close_at
        ?? trade?.closeAt
        ?? trade?.exitTimestampMs
        ?? trade?.exitTimestamp
        ?? trade?.exitTime
        ?? trade?.exit_at
        ?? trade?.exitAt
        ?? trade?.exit_ts
    );
}

function hasJournalTradeExitPrice(trade: JournalTradeRecord | null | undefined): boolean {
    const exitCandidates = [trade?.exitPrice, trade?.avgExitPrice, trade?.avgExit, trade?.closePrice, trade?.close];
    for (const candidate of exitCandidates) {
        if (candidate == null || candidate === '') continue;
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return true;
    }
    return false;
}

function getJournalTradeStatus(trade: JournalTradeRecord | null | undefined): 'open' | 'closed' {
    if (!trade) return 'open';

    const executionPnl = resolveJournalExecutionPnl(trade);
    if (executionPnl?.status === 'open') return 'open';
    if (executionPnl?.status === 'closed') return 'closed';

    const explicit = String(trade?.status ?? '').trim().toLowerCase();
    if (explicit === 'open') return 'open';
    if (trade?.isOpen === true || String(trade?.isOpen ?? '').trim().toLowerCase() === 'true') return 'open';

    const closeTimestampMs = getJournalTradeCloseTimestampMs(trade);
    const positionAfter = Number(trade?.positionAfter ?? trade?.position_after);
    if (Number.isFinite(positionAfter) && positionAfter !== 0 && closeTimestampMs == null) {
        return 'open';
    }

    if (explicit === 'closed' && (closeTimestampMs != null || hasJournalTradeExitPrice(trade))) return 'closed';
    if (!hasJournalTradeExitPrice(trade)) return 'open';
    if (closeTimestampMs == null) return 'open';
    return 'closed';
}

function getJournalTradeFeeTotal(trade: JournalTradeRecord | null | undefined): number {
    const candidates = [trade?.commissionAndFees, trade?.feesTotal, trade?.fees, trade?.commission];
    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return Math.abs(numeric);
    }
    return 0;
}

function getJournalTradeNetPnlValue(trade: JournalTradeRecord | null | undefined): number | null {
    if (!trade) return null;

    const executionPnl = resolveJournalExecutionPnl(trade);
    if (executionPnl?.status === 'open') return null;
    if (executionPnl?.netPnl != null) return executionPnl.netPnl;
    if (getJournalTradeStatus(trade) === 'open') return null;

    const candidates = [
        trade?.rowRealizedNet,
        trade?.row_realized_net,
        trade?.realizedNet,
        trade?.realized_net,
        trade?.netPnl,
        trade?.realizedPnl,
        trade?.calculatedPnl,
        trade?.pnl,
        trade?.pnlNet,
        trade?.brokerReportedPnl,
    ];
    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return numeric;
    }
    return null;
}

function getJournalTradeGrossPnlValue(trade: JournalTradeRecord | null | undefined): number | null {
    if (!trade) return null;

    const executionPnl = resolveJournalExecutionPnl(trade);
    if (executionPnl?.status === 'open') return null;
    if (executionPnl?.grossPnl != null) return executionPnl.grossPnl;
    if (getJournalTradeStatus(trade) === 'open') return null;

    const candidates = [trade?.grossPnl, trade?.gross_pnl, trade?.pnlGross];
    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return numeric;
    }

    const netPnl = getJournalTradeNetPnlValue(trade);
    if (netPnl == null) return null;
    return netPnl + getJournalTradeFeeTotal(trade);
}

function readFirstJournalFiniteNumber(trade: JournalTradeRecord | null | undefined, keys: string[]): number | null {
    if (!trade) return null;
    for (const key of keys) {
        const raw = trade[key];
        if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
        if (typeof raw === 'string' && raw.trim() !== '') {
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) return parsed;
        }
    }
    return null;
}

function normalizeJournalTradeSide(value: unknown, fallbackVolume?: unknown): 'buy' | 'sell' | null {
    const raw = String(value ?? '').trim().toLowerCase();
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric !== 0) return numeric < 0 ? 'sell' : 'buy';
    if (raw === '-' || raw === '--') return 'sell';
    if (raw === '+' || raw === '++') return 'buy';
    const compact = raw.replace(/[\s_-]+/g, '');
    if (['s', 'sl', 'sld', 'sell', 'short', 'sellshort', 'shortsell'].includes(compact)) return 'sell';
    if (['b', 'bot', 'buy', 'long', 'buytocover', 'cover', 'buycover'].includes(compact)) return 'buy';
    if (raw.includes('sell') || raw.includes('short')) return 'sell';
    if (raw.includes('buy') || raw.includes('long')) return 'buy';

    const volume = Number(fallbackVolume);
    if (Number.isFinite(volume) && volume !== 0) return volume < 0 ? 'sell' : 'buy';
    return null;
}

function getJournalMovementUnit(trade: JournalTradeRecord): 'ticks' | 'pips' | 'points' {
    const assetClass = getCanonicalAssetClass(trade);
    if (assetClass === 'FUTURES') return 'ticks';
    if (assetClass === 'FOREX') return 'pips';
    return 'points';
}

function getJournalForexPipSize(symbol: unknown): number {
    const normalized = String(symbol ?? '').toUpperCase().replace(/[^A-Z]/g, '');
    return normalized.endsWith('JPY') ? 0.01 : 0.0001;
}

function formatJournalMovementNumber(value: number): string {
    const rounded = Math.round(value * 100) / 100;
    if (Object.is(rounded, -0)) return '0';
    return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    }).format(rounded);
}

function getJournalTotalMovementValue(trade: JournalTradeRecord): number | null {
    const stored = readFirstJournalFiniteNumber(trade, ['pnlTicksGross']);
    if (stored != null) return stored;
    if (getJournalTradeStatus(trade) === 'open') return null;

    const entry = readFirstJournalFiniteNumber(trade, ['entryPrice', 'entry_price', 'avgEntryPrice', 'avgEntry', 'openPrice', 'open']);
    const exit = readFirstJournalFiniteNumber(trade, ['exitPrice', 'exit_price', 'avgExitPrice', 'avgExit', 'closePrice', 'close']);
    if (entry == null || exit == null) return null;

    const side = normalizeJournalTradeSide(trade.side ?? trade.direction ?? trade.action ?? trade.positionSide, trade.volume ?? trade.quantity ?? trade.qty);
    if (!side) return null;

    const assetClass = getCanonicalAssetClass(trade);
    if (assetClass === 'FUTURES') {
        const executionSample = Array.isArray(trade.executionsList) && trade.executionsList.length > 0
            ? trade.executionsList[0]
            : null;
        const tickSize = resolveFuturesTickSize(String(trade.symbol ?? ''), trade.instrument)
            ?? resolveFuturesTickSize(String(trade.symbol ?? ''), executionSample && typeof executionSample === 'object' ? executionSample.instrument : undefined);
        if (tickSize == null || tickSize <= 0) return null;
        const ticks = computeDirectionalTicks(entry, exit, side, tickSize);
        return ticks == null ? null : Math.round(ticks * 100) / 100;
    }

    const signedMove = side === 'sell' ? entry - exit : exit - entry;
    if (!Number.isFinite(signedMove)) return null;
    const divisor = assetClass === 'FOREX' ? getJournalForexPipSize(trade.symbol) : 1;
    return Math.round((signedMove / divisor) * 100) / 100;
}

function formatJournalMovementWithUnit(value: number | null, unit: 'ticks' | 'pips' | 'points'): string {
    if (value == null || !Number.isFinite(value)) return '—';
    const absValue = Math.abs(value);
    const label = unit === 'ticks'
        ? (absValue === 1 ? 'tick' : 'ticks')
        : unit === 'pips'
            ? (absValue === 1 ? 'pip' : 'pips')
            : (absValue === 1 ? 'point' : 'points');
    return `${formatJournalMovementNumber(value)} ${label}`;
}

function normalizeJournalTradeRecord(trade: JournalTradeRecord, index: number): Trade {
    const numericId = Number(trade?.id ?? trade?.timestamp ?? trade?.openTimestampMs ?? index + 1);
    return {
        ...trade,
        id: Number.isFinite(numericId) ? numericId : index + 1,
        date: getJournalTradeDateKey(trade),
        symbol: String(trade?.symbol ?? '—'),
        volume: getTradeVolume(toAuthoritativeTrade(trade)),
        pnl: getJournalTradeNetPnlValue(trade) ?? 0,
        timestamp: getJournalTradeTimestampMs(trade) ?? Number(trade?.timestamp ?? 0),
        status: getJournalTradeStatus(trade),
    } as Trade;
}

function dedupeJournalTrades(trades: Trade[]): Trade[] {
    const seen = new Set<string>();
    const deduped: Trade[] = [];

    for (const trade of trades) {
        const key = getJournalTradeKey(trade as unknown as Record<string, unknown>);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(trade);
    }

    return deduped;
}

type DemoTradesOverrideLike = { enabled?: boolean; trades?: unknown[] } | null | undefined;

export const JOURNAL_DEMO_TRADE_TAG_OVERRIDES_STORAGE_KEY = 'journal.demoTradeTagOverrides.v1';

export type JournalDemoTradeTagOverrides = Record<string, string[]>;

export function normalizeJournalDemoTradeTagOverrides(value: unknown): JournalDemoTradeTagOverrides {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

    return Object.entries(value as Record<string, unknown>).reduce<JournalDemoTradeTagOverrides>((acc, [key, rawTags]) => {
        const normalizedKey = String(key ?? '').trim();
        if (!normalizedKey) return acc;

        const tags = Array.isArray(rawTags)
            ? rawTags.map((tag) => String(tag ?? '').trim()).filter(Boolean)
            : [];

        acc[normalizedKey] = Array.from(new Set(tags));
        return acc;
    }, {});
}

export function loadJournalDemoTradeTagOverrides(): JournalDemoTradeTagOverrides {
    try {
        const raw = readPrivateTradingJson('journal-demo-trade-tag-overrides', 'local', [JOURNAL_DEMO_TRADE_TAG_OVERRIDES_STORAGE_KEY]);
        if (!raw) return {};
        return normalizeJournalDemoTradeTagOverrides(JSON.parse(raw));
    } catch {
        return {};
    }
}

export function saveJournalDemoTradeTagOverrides(overrides: JournalDemoTradeTagOverrides): void {
    try {
        writePrivateTradingJson('journal-demo-trade-tag-overrides', JSON.stringify(normalizeJournalDemoTradeTagOverrides(overrides)));
    } catch {}
}

export function getJournalTradesFromDemoOverride(override: DemoTradesOverrideLike): Trade[] {
    if (!override?.enabled || !Array.isArray(override.trades)) return [];
    return dedupeJournalTrades(
        override.trades.map((trade, index) => normalizeJournalTradeRecord(trade as JournalTradeRecord, index))
    );
}

export type JournalNoteEntry = { id: string; text: string; ts: number; name?: string; symbol?: string; tradeKey?: string };

export function buildJournalDemoNotesByDate(trades: Trade[]): Record<string, JournalNoteEntry[]> {
    return trades.reduce<Record<string, JournalNoteEntry[]>>((acc, trade) => {
        const noteText = String((trade as Record<string, unknown>).notes ?? '').trim();
        if (!noteText) return acc;
        const date = String(trade.date ?? '').trim();
        if (!date) return acc;
        const tradeKey = getJournalTradeKey(trade as unknown as Record<string, unknown>);
        const timestamp = getJournalTradeTimestampMs(trade as JournalTradeRecord) ?? Date.now();
        const entry: JournalNoteEntry = {
            id: `demo-note-${tradeKey}`,
            text: noteText,
            ts: timestamp,
            name: String((trade as Record<string, unknown>).strategy ?? trade.symbol ?? 'Demo trade'),
            symbol: trade.symbol,
            tradeKey,
        };
        acc[date] = [...(acc[date] ?? []), entry];
        return acc;
    }, {});
}

function fmtTime24hWithSeconds(ms: number): string {
    if (!ms || !Number.isFinite(ms)) return '—';
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return '—';
    return formatNyDateTime(ms, 'HH:mm:ss');
}

// Use canonical `Trade` type imported from '../../types'

// ----------------------------- UI Components -----------------------------
type Density = 'compact' | 'comfortable' | 'spacious';

const DENSITY_STYLES: Record<Density, {
    monthHeaderPy: string;
    iconSize: string;
    monthHeaderText: string;
    secondaryText: string;
    dayRowPy: string;
    gap: string;
    dateBoxSize: string;
    dateBoxText: string;
    primaryText: string;
    pnlText: string;
}> = {
    compact: {
        monthHeaderPy: 'py-2',
        iconSize: 'text-sm',
        monthHeaderText: 'text-xs',
        secondaryText: 'text-xs',
        dayRowPy: 'py-1',
        gap: 'gap-2',
        dateBoxSize: 'w-8 h-8',
        dateBoxText: 'text-sm',
        primaryText: 'text-sm',
        pnlText: 'text-sm',
    },
    comfortable: {
        monthHeaderPy: 'py-3',
        iconSize: 'text-base',
        monthHeaderText: 'text-sm',
        secondaryText: 'text-sm',
        dayRowPy: 'py-2',
        gap: 'gap-3',
        dateBoxSize: 'w-10 h-10',
        dateBoxText: 'text-base',
        primaryText: 'text-base',
        pnlText: 'text-base',
    },
    spacious: {
        monthHeaderPy: 'py-4',
        iconSize: 'text-lg',
        monthHeaderText: 'text-lg',
        secondaryText: 'text-base',
        dayRowPy: 'py-3',
        gap: 'gap-4',
        dateBoxSize: 'w-12 h-12',
        dateBoxText: 'text-lg',
        primaryText: 'text-lg',
        pnlText: 'text-lg',
    },
};

type LeftPanelProps = {
    days: string[];
    tradesByDay: Record<string, Trade[]>;
    selectedDay: string | null;
    onPickDay: (day: string) => void;
    searchQuery: string;
    onChangeSearch: (q: string) => void;
    // market filter moved to TradesPage
    isDark?: boolean;
};

export function LeftPanelBodyTop(props: LeftPanelProps) {
    const { days, tradesByDay, selectedDay, onPickDay, searchQuery, onChangeSearch, isDark } = props;
    const [collapsedMonths, setCollapsedMonths] = useState<Record<string, boolean>>({});
    const density: Density = 'compact';
    const COLLAPSED_MONTHS_KEY = 'journal.collapsedMonths.v2';

    // Get current styles based on density state
    const styles = DENSITY_STYLES[density];

    useEffect(() => {
        const raw = safeGetItem(COLLAPSED_MONTHS_KEY);
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') setCollapsedMonths(parsed as Record<string, boolean>);
        } catch {}
    }, []);

    useEffect(() => {
        safeSetItem(COLLAPSED_MONTHS_KEY, JSON.stringify(collapsedMonths));
    }, [collapsedMonths]);

    useEffect(() => {
        if (!selectedDay) return;
        try {
            const d = new Date(selectedDay + 'T00:00:00');
            const monthLabel = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
            setCollapsedMonths((prev) => (prev[monthLabel] ? { ...prev, [monthLabel]: false } : prev));
        } catch {}
    }, [selectedDay]);

    const monthByDay = useMemo(() => {
        const out: Record<string, string> = {};
        for (const date of days) {
            try {
                const d = new Date(date + 'T00:00:00');
                out[date] = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
            } catch {
                out[date] = '';
            }
        }
        return out;
    }, [days]);

    const selMonthLabel = useMemo(() => {
        if (!selectedDay) return '';
        try {
            return new Date(selectedDay + 'T00:00:00').toLocaleString(undefined, { month: 'long', year: 'numeric' });
        } catch {
            return '';
        }
    }, [selectedDay]);

    const filteredDays = useMemo(() => {
        // `days` already comes from the symbol-filtered trade set, so keep the
        // sidebar aligned to those matching days instead of filtering by the
        // literal date string.
        return [...days].sort((a, b) => b.localeCompare(a));
    }, [days]);

    const groupedData = useMemo(() => {
        const groups: { label: string; days: string[]; totalPnl: number; totalTrades: number }[] = [];
        let currentLabel = '';
        let currentGroup: (typeof groups)[0] | null = null;

        for (const date of filteredDays) {
            const label = monthByDay[date] || 'Unknown';

            if (label !== currentLabel) {
                if (currentGroup) groups.push(currentGroup);
                currentLabel = label;
                currentGroup = { label, days: [], totalPnl: 0, totalTrades: 0 };
            }

            if (currentGroup) {
                currentGroup.days.push(date);
                const dailyTrades = tradesByDay[date] || [];
                currentGroup.totalTrades += dailyTrades.length;
                currentGroup.totalPnl += dailyTrades.reduce((acc, t) => acc + (getJournalTradeNetPnlValue(t as JournalTradeRecord) ?? 0), 0);
            }
        }
        if (currentGroup) groups.push(currentGroup);

        return groups;
    }, [filteredDays, monthByDay, tradesByDay]);

    const bgBase = isDark ? 'bg-[#1d1d20]' : 'bg-zinc-100';
    const bgSubtle = isDark ? 'bg-[#1d1d20]' : 'bg-white/95';
    const textPrimary = isDark ? 'text-zinc-100' : 'text-zinc-800';
    const textSecondary = isDark ? 'text-zinc-400' : 'text-zinc-500';
    const borderParams = 'border-border';

    return (
        <div className={`w-full h-full flex flex-col min-h-0 ${bgBase} ${textPrimary}`}>
            <div className={`pl-5 pr-3 py-3 border-b flex-shrink-0 ${borderParams} space-y-3`}>
                <div className="h-2" />
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <InlineSearch value={searchQuery} onChange={onChangeSearch} isDark={isDark} />
                    </div>
                    {/* market filter buttons moved to TradesPage */}
                </div>
                <div className="sr-only">Density: Min</div>
                <div className="h-1" />
            </div>

            <nav className="scrollable flex-1 overflow-y-auto min-h-0 relative scroll-smooth">
                {groupedData.length === 0 && (
                    <div className={`p-8 text-center text-sm ${textSecondary}`}>No days found matching your search.</div>
                )}

                {groupedData.map((group) => {
                    const isCollapsed = !!collapsedMonths[group.label];
                    const isSelectedMonth = selMonthLabel === group.label;
                    return (
                        <div key={group.label} className="relative">
                            <div
                                className={`sticky top-0 z-10 flex items-center justify-between pl-5 pr-3 ${styles.monthHeaderPy} cursor-pointer backdrop-blur-sm transition-colors border-b ${
                                    isDark ? 'border-zinc-800/50 hover:bg-zinc-800/50' : 'border-zinc-100 hover:bg-zinc-50'
                                } ${bgSubtle}`}
                                onClick={() => setCollapsedMonths((prev) => ({ ...prev, [group.label]: !prev[group.label] }))}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`${styles.iconSize} transform transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'} ${textSecondary}`}>▼</span>
                                    <div className="flex flex-col">
                                        <span className={`${styles.monthHeaderText} font-bold uppercase tracking-wider ${isSelectedMonth ? 'text-blue-500' : textSecondary}`}>{group.label}</span>
                                        <span className={`${styles.secondaryText} text-xs font-medium ${isSelectedMonth ? 'text-blue-400' : textSecondary}`}>{group.totalTrades} {group.totalTrades === 1 ? 'Trade' : 'Trades'}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`${styles.secondaryText} font-mono font-medium ${clsPnL(group.totalPnl)}`}>{group.totalPnl > 0 ? '+' : ''}{fmtMoney(group.totalPnl)}</span>
                                </div>
                            </div>

                            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}>
                                <div className="pl-4 pr-2 py-1 space-y-0">
                                    {group.days.map((date) => {
                                        const dayTrades = tradesByDay[date] || [];
                                        const dailyPnl = dayTrades.reduce((acc, t) => acc + (getJournalTradeNetPnlValue(t as JournalTradeRecord) ?? 0), 0);
                                        const tradeCount = dayTrades.length;
                                        const isSelected = selectedDay === date;
                                        const isLastDay = group.days[group.days.length - 1] === date;
                                        return (
                                            <button
                                                key={date}
                                                onClick={() => onPickDay(date)}
                                                className={`group w-full flex items-center justify-between px-3 ${styles.dayRowPy} rounded-none text-left transition-all ${
                                                    isSelected
                                                        ? 'bg-blue-600 border border-blue-600 shadow-sm'
                                                        : isDark
                                                            ? `border-b border-zinc-800/70 border-x-0 border-t-0 ${isLastDay ? 'border-b-0' : ''} hover:bg-zinc-800/50`
                                                            : `border-b border-border border-x-0 border-t-0 ${isLastDay ? 'border-b-0' : ''} hover:bg-zinc-50`
                                                }`}
                                            >
                                                <div className={`flex items-center ${styles.gap} min-w-0`}>
                                                    <div className={`flex flex-col items-center justify-center ${styles.dateBoxSize} rounded-lg border ${styles.dateBoxText} font-medium ${
                                                        isSelected ? 'bg-blue-500 border-blue-400 text-white' : isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-400' : 'bg-white border-zinc-200 text-zinc-500'
                                                    }`}>
                                                        <span>{fmtDayNum(date)}</span>
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className={`${styles.primaryText} font-medium truncate ${isSelected ? 'text-white' : textPrimary}`}>{(() => { const s = new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long' }); return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(); })()}</span>
                                                        <span className={`${styles.secondaryText} ${isSelected ? 'text-blue-200' : textSecondary}`}>{tradeCount} {tradeCount === 1 ? 'trade' : 'trades'}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end min-w-[80px]">
                                                    <span className={`${styles.pnlText} font-mono font-medium ${isSelected ? 'text-white' : clsPnL(dailyPnl)}`}>{dailyPnl > 0 ? '+' : ''}{fmtMoney(dailyPnl)}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="h-4"></div>
                            </div>
                        </div>
                    );
                })}
            </nav>
        </div>
    );
}
function InlineSearch({ value, onChange, isDark }: { value: string; onChange: (q: string) => void; isDark?: boolean }) {
    const [local, setLocal] = useState(value);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        const active = document.activeElement;
        if (inputRef.current && active && inputRef.current.contains(active)) return;
        if (value !== local) setLocal(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (timerRef.current) window.clearTimeout(timerRef.current as unknown as number);
        timerRef.current = window.setTimeout(() => {
            onChange(local);
            timerRef.current = null;
        }, 300) as unknown as number;
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current as unknown as number);
            timerRef.current = null;
        };
    }, [local, onChange]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (timerRef.current) window.clearTimeout(timerRef.current as unknown as number);
            onChange(local);
            timerRef.current = null;
        }
    };

    const onBlur = () => {
        if (timerRef.current) window.clearTimeout(timerRef.current as unknown as number);
        onChange(local);
        timerRef.current = null;
    };

    return (
        <input
            ref={inputRef}
            type="text"
            placeholder="Search by symbol..."
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            className={`${isDark ? 'w-full h-10 rounded-lg border bg-zinc-900 border-zinc-700 pl-9 pr-3 text-sm text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500' : 'w-full h-10 rounded-lg border bg-white border-zinc-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
        />
    );
}



const RichTextEditor = React.forwardRef(function RichTextEditor(
    { value = '', placeholder, isDark, onChange, suspendExternalSync = false }: { value?: string; placeholder?: string; isDark?: boolean; onChange?: (html: string) => void; suspendExternalSync?: boolean },
    ref: React.Ref<{ getHTML: () => string; setHTML?: (html: string) => void } | null>
) {
    const [activeFormats, setActiveFormats] = useState(new Set<string>());
    const [currentColor, setCurrentColor] = useState<string | undefined>(undefined);
    const editorRef = useRef<HTMLDivElement>(null);
    const editableRef = useRef<HTMLDivElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    // local HTML buffer to avoid immediate parent-driven re-renders from clobbering caret
    const [localHTML, setLocalHTML] = useState<string>(value || '');

    const mountCounterRef = useRef(0);
    useEffect(() => {
        mountCounterRef.current += 1;
        return () => {};
    }, []);

    // keep local buffer in sync with incoming prop when changed externally
    useEffect(() => {
        if (suspendExternalSync) return; // do not clobber while Save modal is open
        const root = editableRef.current;
        const active = document.activeElement;
        if (root && active && root.contains(active)) {
            // if focused, do not clobber
            return;
        }
        setLocalHTML(value || '');
    }, [value, suspendExternalSync]);

    // Render localHTML into the DOM when it changes, but don't clobber while user is typing
    useEffect(() => {
        if (!editableRef.current) return;
        const current = (editableRef.current.innerHTML || '');
        if (current === localHTML) return;
        const active = document.activeElement;
        if (active && editableRef.current.contains(active)) return;
        editableRef.current.innerHTML = localHTML;
    }, [localHTML]);

    const colorToHex = (value: string): string | undefined => {
        try {
            if (!value) return undefined;
            // If already hex-like
            if (value.startsWith('#')) {
                // Normalize #rgb to #rrggbb
                if (value.length === 4) {
                    const r = value[1], g = value[2], b = value[3];
                    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
                }
                return value.toLowerCase();
            }
            // Resolve named or rgb/rgba to rgb via a temp element
            const el = document.createElement('span');
            el.style.color = value;
            document.body.appendChild(el);
            const rgb = getComputedStyle(el).color; // e.g., rgb(255, 0, 0)
            if (el.parentNode) document.body.removeChild(el);
            const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
            if (!m) return undefined;
            const r = Number(m[1]).toString(16).padStart(2, '0');
            const g = Number(m[2]).toString(16).padStart(2, '0');
            const b = Number(m[3]).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`.toLowerCase();
        } catch { return undefined; }
    };

    const savedRangeRef = useRef<Range | null>(null);
    const restoreSelectionIntoEditor = useCallback(() => {
        const editor = editableRef.current;
        const r = savedRangeRef.current;
        if (!editor || !r) return;
        try {
            const sel = window.getSelection();
            if (!sel) return;
            sel.removeAllRanges();
            sel.addRange(r);
            editor.focus();
        } catch {}
    }, []);

    const updateActiveFromSelection = useCallback(() => {
        const sel = document.getSelection();
        const root = editableRef.current;
        if (!sel || !root) return;
        const anchor = sel.anchorNode;
        if (!anchor || !root.contains(anchor)) return;
        const next = new Set<string>();
        try { if (document.queryCommandState('bold')) next.add('bold'); } catch {}
        try { if (document.queryCommandState('italic')) next.add('italic'); } catch {}
        try { if (document.queryCommandState('underline')) next.add('underline'); } catch {}
        try { if (document.queryCommandState('strikeThrough')) next.add('strikeThrough'); } catch {}
    try { if (document.queryCommandState('insertUnorderedList')) next.add('insertUnorderedList'); } catch {}
    try { if (document.queryCommandState('insertOrderedList')) next.add('insertOrderedList'); } catch {}
        try { if (document.queryCommandState('justifyLeft')) next.add('justifyLeft'); } catch {}
        try { if (document.queryCommandState('justifyCenter')) next.add('justifyCenter'); } catch {}
        try { if (document.queryCommandState('justifyRight')) next.add('justifyRight'); } catch {}
        setActiveFormats(next);
        // Update current color swatch
        try {
            const val = document.queryCommandValue('foreColor') as string;
            const hex = colorToHex(val);
            setCurrentColor(hex);
        } catch {}
        // Save current range when selection is inside editor
        try {
            if (sel && sel.rangeCount > 0) {
                const r = sel.getRangeAt(0);
                if (root.contains(r.startContainer) || root.contains(r.endContainer)) {
                    savedRangeRef.current = r.cloneRange();
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        const onSel = () => updateActiveFromSelection();
        document.addEventListener('selectionchange', onSel);
        return () => document.removeEventListener('selectionchange', onSel);
    }, [updateActiveFromSelection]);

    // Global click handler to hide all image handles when clicking outside
    useEffect(() => {
        const handleGlobalClick = (e: MouseEvent) => {
            const root = editableRef.current;
            if (!root) return;

            // Find all image wrappers
            const wrappers = root.querySelectorAll('.rte-img-box');
            wrappers.forEach((wrapper) => {
                // Check if click is outside this wrapper
                if (!wrapper.contains(e.target as Node)) {
                    // Hide handles for this wrapper
                    const handles = wrapper.querySelectorAll('.rte-img-handle');
                    handles.forEach((h) => {
                        (h as HTMLElement).style.display = 'none';
                    });
                }
            });
        };

        document.addEventListener('click', handleGlobalClick);
        return () => document.removeEventListener('click', handleGlobalClick);
    }, []);

    const exec = (cmd: string, value?: string) => {
        try {
            document.execCommand(cmd, false, value);
            updateActiveFromSelection();
            // don't force focus here; let browser keep caret naturally
            // after command, sync HTML out
            const html = editableRef.current?.innerHTML ?? '';
            setLocalHTML(html);
            if (onChange) onChange(html);
        } catch {}
    };

    // Image utilities
    const ensureImageWrapped = (img: HTMLImageElement): HTMLElement => {
        // If already wrapped, return wrapper
        const parent = img.parentElement;
        if (parent && parent.classList.contains('rte-img-box')) return parent as HTMLElement;
        // Create wrapper with CSS resize handles
        const wrap = document.createElement('span');
        wrap.className = 'rte-img-box';
        wrap.style.display = 'block';
        wrap.style.position = 'relative';
        wrap.style.maxWidth = '100%';
        wrap.style.lineHeight = '0';
        wrap.style.marginLeft = 'auto';
        wrap.style.marginRight = 'auto';
        // Normalize image styles
        img.style.display = 'block';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        // Insert wrapper
        if (parent) {
            parent.replaceChild(wrap, img);
        }
        wrap.appendChild(img);

        const handles: HTMLElement[] = [];
        const addHandle = (cursor: string, pos: 'nw'|'ne'|'sw'|'se') => {
            const h = document.createElement('span');
            h.className = `rte-img-handle rte-img-h-${pos}`;
            h.style.position = 'absolute';
            h.style.width = '10px';
            h.style.height = '10px';
            h.style.background = '#fff';
            h.style.border = '1px solid #3b82f6';
            h.style.boxShadow = '0 0 0 1px rgba(59,130,246,0.5)';
            h.style.borderRadius = '2px';
            h.style.cursor = cursor;
            if (pos === 'nw') { h.style.left = '-6px'; h.style.top = '-6px'; }
            if (pos === 'ne') { h.style.right = '-6px'; h.style.top = '-6px'; }
            if (pos === 'sw') { h.style.left = '-6px'; h.style.bottom = '-6px'; }
            if (pos === 'se') { h.style.right = '-6px'; h.style.bottom = '-6px'; }
            h.style.display = 'none';
            wrap.appendChild(h);
            handles.push(h);
            let startX = 0, startW = 0, startH = 0;
            const onDown = (ev: MouseEvent) => {
                ev.preventDefault(); ev.stopPropagation();
                const rect = img.getBoundingClientRect();
                startX = ev.clientX; startW = rect.width; startH = rect.height;
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            };
            const onMove = (ev: MouseEvent) => {
                ev.preventDefault();
                const dx = ev.clientX - startX;
                const ratio = startW / (startH || 1);

                // Calculate new width based on corner position and drag direction
                let newW = startW;
                if (pos === 'se' || pos === 'ne') {
                    // Right side corners: drag right increases width
                    newW = startW + dx;
                } else {
                    // Left side corners: drag left increases width
                    newW = startW - dx;
                }

                // Enforce minimum size
                if (newW < 50) newW = 50;

                // Maintain aspect ratio
                const newH = newW / ratio;

                // Apply size to image
                img.style.width = `${newW}px`;
                img.style.height = `${newH}px`;
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                // Trigger change sync
                const html = editableRef.current?.innerHTML ?? '';
                setLocalHTML(html);
                if (onChange) onChange(html);
            };
            h.addEventListener('mousedown', onDown);
        };

        addHandle('nwse-resize', 'nw');
        addHandle('nesw-resize', 'ne');
        addHandle('nesw-resize', 'sw');
        addHandle('nwse-resize', 'se');

        const showHandles = () => { handles.forEach(h => h.style.display = 'block'); };

        // Show handles when image is clicked
        img.addEventListener('click', (ev) => {
            ev.stopPropagation();
            showHandles();

            // Select the image wrapper so keyboard delete works
            try {
                const sel = window.getSelection();
                const range = document.createRange();
                range.selectNode(wrap);
                sel?.removeAllRanges();
                sel?.addRange(range);
            } catch {}
        });
        return wrap;
    };

    const closestImageOrWrapper = (node: Node | null): { img: HTMLImageElement | null; wrapper: HTMLElement | null } => {
        let el = node instanceof Element ? node : node && node.parentElement;
        while (el) {
            if (el.tagName === 'IMG') return { img: el as HTMLImageElement, wrapper: el.parentElement && el.parentElement.classList.contains('rte-img-box') ? el.parentElement : null };
            if (el.classList && el.classList.contains('rte-img-box')) return { img: (el.querySelector('img') as HTMLImageElement) || null, wrapper: el as HTMLElement };
            el = el.parentElement as HTMLElement | null;
        }
        return { img: null, wrapper: null };
    };

    const handleImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            if (!dataUrl) return;
            // Ensure selection/caret is inside the editor before inserting
            const root = editableRef.current;
            if (root) {
                try {
                    const sel = window.getSelection();
                    const range = document.createRange();
                    // If editor has content, place caret at end; otherwise ensure it can accept nodes
                    if (root.childNodes.length > 0) {
                        range.selectNodeContents(root);
                        range.collapse(false);
                    } else {
                        // create a zero-width space to anchor caret if empty
                        const spacer = document.createTextNode('\u200B');
                        root.appendChild(spacer);
                        range.setStartAfter(spacer);
                        range.collapse(true);
                    }
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                    // focus the editor so execCommand targets it
                    root.focus();
                } catch {}
            }
            let inserted = false;
            try {
                inserted = document.execCommand('insertImage', false, dataUrl);
            } catch {
                inserted = false;
            }
            if (!inserted) {
                // Fallback manual insertion at caret
                const sel = window.getSelection();
                const selNode = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).commonAncestorContainer : null;
                const insideEditor = root && selNode instanceof Node && root.contains(selNode);
                if (sel && sel.rangeCount > 0 && insideEditor) {
                    const range = sel.getRangeAt(0);
                    const img = document.createElement('img');
                    img.src = dataUrl;
                    img.alt = '';
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    range.insertNode(img);
                    // wrap for resizing and center by default
                    const box = ensureImageWrapped(img);
                    box.style.display = 'block';
                    box.style.marginLeft = 'auto';
                    box.style.marginRight = 'auto';
                    box.style.maxWidth = '100%';
                    // move caret after the image
                    range.setStartAfter(img);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                } else if (root) {
                    // append at end
                    const img = document.createElement('img');
                    img.src = dataUrl;
                    img.alt = '';
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    root.appendChild(img);
                    const box = ensureImageWrapped(img);
                    box.style.display = 'block';
                    box.style.marginLeft = 'auto';
                    box.style.marginRight = 'auto';
                    box.style.maxWidth = '100%';
                }
            }
            if (inserted) {
                // Try to find the last inserted image and wrap it
                const root2 = editableRef.current;
                if (root2) {
                    const imgs = Array.from(root2.querySelectorAll('img')) as HTMLImageElement[];
                    const last = imgs[imgs.length - 1];
                    if (last) {
                        const box = ensureImageWrapped(last);
                        box.style.display = 'block';
                        box.style.marginLeft = 'auto';
                        box.style.marginRight = 'auto';
                        box.style.maxWidth = '100%';
                    }
                }
            }
            // update local HTML buffer
            setLocalHTML(editableRef.current?.innerHTML || '');
            // clear input value so selecting the same file again works
            e.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    const handleCommand = (command: string) => {
        const [cmd, arg] = command.split('|');
        switch (cmd) {
            case 'bold':
            case 'italic':
            case 'underline':
            case 'strikeThrough':
            case 'justifyLeft':
            case 'justifyCenter':
            case 'justifyRight': {
                // If selection is on an image, align the image instead of paragraph
                const sel = window.getSelection();
                const anchor = sel && (sel.anchorNode || null);
                const { img, wrapper } = closestImageOrWrapper(anchor as Node | null);
                if (img) {
                    const box = wrapper || ensureImageWrapped(img);
                    // Make it block-level and align by margins
                    (box as HTMLElement).style.display = 'block';
                    if (cmd === 'justifyLeft') {
                        box.style.marginLeft = '0';
                        box.style.marginRight = 'auto';
                    } else if (cmd === 'justifyCenter') {
                        box.style.marginLeft = 'auto';
                        box.style.marginRight = 'auto';
                    } else {
                        box.style.marginLeft = 'auto';
                        box.style.marginRight = '0';
                    }
                    const html = editableRef.current?.innerHTML ?? '';
                    setLocalHTML(html);
                    if (onChange) onChange(html);
                    try { editableRef.current?.focus(); } catch {}
                    return;
                }
                restoreSelectionIntoEditor();
                exec(cmd);
                return;
            }
            case 'undo':
            case 'redo':
                restoreSelectionIntoEditor();
                exec(cmd);
                return;
            case 'insertUnorderedList':
                restoreSelectionIntoEditor();
                exec(cmd);
                // Fallback for when execCommand fails to create a list in an empty block
                setTimeout(() => {
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0) {
                        const range = sel.getRangeAt(0);
                        const block = range.startContainer.parentElement;
                        const editor = editableRef.current;
                        if (editor && block && editor.contains(block) && block.tagName !== 'LI' && block.textContent?.trim() === '') {
                            document.execCommand('insertHTML', false, '<ul><li><br></li></ul>');
                        }
                    }
                }, 50);
                return;
            case 'insertOrderedList':
                restoreSelectionIntoEditor();
                exec(cmd);
                // Fallback for empty block similar to unordered list
                setTimeout(() => {
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0) {
                        const range = sel.getRangeAt(0);
                        const block = range.startContainer.parentElement;
                        const editor = editableRef.current;
                        if (editor && block && editor.contains(block) && block.tagName !== 'LI' && block.textContent?.trim() === '') {
                            document.execCommand('insertHTML', false, '<ol><li><br></li></ol>');
                        }
                    }
                }, 50);
                return;
            case 'fontSize':
                if (arg) { restoreSelectionIntoEditor(); exec('fontSize', arg); }
                return;
            case 'foreColor':
                if (arg) { restoreSelectionIntoEditor(); exec('foreColor', arg); }
                return;
            case 'createLink': {
                const url = window.prompt('Enter URL', 'https://') || '';
                if (!url) return; restoreSelectionIntoEditor(); exec('createLink', url); return;
            }
            case 'insertImage': {
                // Open file picker to insert local image as data URL
                imageInputRef.current?.click();
                return;
            }
            default:
                return;
        }
    };

    const isSyncingRef = useRef(false);
    const handleInput = () => {
        const html = editableRef.current?.innerHTML ?? '';
        setLocalHTML(html);
        if (!isSyncingRef.current && onChange) onChange(html);
        updateActiveFromSelection();
        // autosave removed: do not call parent on every input
    };

    useEffect(() => {
        // Observe changes inside editor (e.g., image resize via CSS resize handle) and sync out
        const root = editableRef.current;
        if (!root) return;
        const obs = new MutationObserver(() => {
            // prevent re-entrant calls during user typing
            isSyncingRef.current = true;
            const html = root.innerHTML;
            setLocalHTML(html);
            if (onChange) onChange(html);
            // small timeout lets browser settle selection before we allow other syncs
            setTimeout(() => { isSyncingRef.current = false; }, 0);
        });
        obs.observe(root, { attributes: true, childList: true, subtree: true, characterData: true });
        return () => obs.disconnect();
    }, [onChange]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Handle Enter key to ensure new line is created
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            // Check if we're inside an image wrapper
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                const container = range.commonAncestorContainer;
                const { wrapper } = closestImageOrWrapper(container);

                if (wrapper) {
                    // We're in an image wrapper, move cursor after it and create new line
                    e.preventDefault();
                    const br = document.createElement('br');
                    const parent = wrapper.parentNode;
                    if (parent) {
                        // Insert line break after wrapper
                        if (wrapper.nextSibling) {
                            parent.insertBefore(br, wrapper.nextSibling);
                        } else {
                            parent.appendChild(br);
                        }
                        // Move cursor after the br
                        const newRange = document.createRange();
                        newRange.setStartAfter(br);
                        newRange.collapse(true);
                        sel.removeAllRanges();
                        sel.addRange(newRange);
                    }
                    return;
                }
            }
            // Otherwise let browser handle Enter normally
        }

        // Delete image when selected and Backspace/Delete pressed
        if (e.key === 'Backspace' || e.key === 'Delete') {
            const root = editableRef.current;
            const sel = window.getSelection();
            if (root && sel && sel.rangeCount > 0) {
                // Check if an image is in the current selection or is the focused element
                const range = sel.getRangeAt(0);
                const container = range.commonAncestorContainer;

                // Try to find image from selection
                let result = closestImageOrWrapper(container);

                // If not found, check if the selection contains an image
                if (!result.img && range.startContainer === range.endContainer) {
                    const node = range.startContainer;
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const imgChild = (node as Element).querySelector('img');
                        if (imgChild) {
                            result = closestImageOrWrapper(imgChild);
                        }
                    }
                }

                const { img, wrapper } = result;
                if (img && root.contains(img)) {
                    e.preventDefault();
                    const target: Node = (wrapper as Node) || (img as Node);
                    const parent = target.parentNode;
                    let caretNode: Node | null = null;
                    let caretAfter = false;
                    if (parent) {
                        const next = target.nextSibling;
                        const prev = target.previousSibling;
                        if (parent && target && target.parentNode === parent) parent.removeChild(target);
                        if (next) { caretNode = next; caretAfter = false; }
                        else if (prev) { caretNode = prev; caretAfter = true; }
                        else { caretNode = parent; caretAfter = true; }
                    }
                    try {
                        const range = document.createRange();
                        if (caretNode) {
                            if (caretNode.nodeType === Node.ELEMENT_NODE) {
                                range.selectNodeContents(caretNode as Element);
                                range.collapse(caretAfter);
                            } else {
                                if (caretAfter) range.setStartAfter(caretNode); else range.setStartBefore(caretNode);
                                range.collapse(!caretAfter);
                            }
                        } else {
                            range.selectNodeContents(root);
                            range.collapse(false);
                        }
                        sel.removeAllRanges();
                        sel.addRange(range);
                    } catch {}
                    const html = root.innerHTML;
                    setLocalHTML(html);
                    if (onChange) onChange(html);
                    return;
                }
            }
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') { e.preventDefault(); exec('bold'); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') { e.preventDefault(); exec('italic'); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') { e.preventDefault(); exec('underline'); }
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); exec('undo'); }
        if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) { e.preventDefault(); exec('redo'); }
    };

    const isEmpty = () => {
        const el = editableRef.current; if (!el) return true; return (el.textContent || '').trim().length === 0 && (el.innerHTML || '').indexOf('<img') === -1;
    };

    useImperativeHandle(ref, () => ({
        getHTML: () => editableRef.current?.innerHTML ?? '',
        setHTML: (html: string) => {
            setLocalHTML(html);
            if (editableRef.current) editableRef.current.innerHTML = html;
        }
    }), []);
    const isDarkMode = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    return (
    <div ref={editorRef} data-rte-instance={mountCounterRef.current} className={`relative rounded-xl focus-within:ring-2 focus-within:ring-blue-500/50 transition-shadow ${isDarkMode ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200'}`}>
    <RichTextToolbar onCommand={handleCommand} activeFormats={activeFormats} isDark={isDarkMode} currentColor={currentColor} compactSpacing={true} />
            <div className="relative">
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    aria-label="Insert image"
                    title="Insert image"
                    className="hidden"
                    onChange={handleImageSelected}
                />
                {isEmpty() && (
                    <div className="pointer-events-none absolute top-3 left-4 text-sm text-zinc-400">{placeholder}</div>
                )}
                <div
                    ref={editableRef}
                    contentEditable
                    tabIndex={0}
                    aria-label={placeholder || 'Editor'}
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onMouseUp={() => { const html = editableRef.current?.innerHTML ?? ''; setLocalHTML(html); if (onChange) onChange(html); }}
                    className={`min-h-[20rem] p-4 text-sm content-editable-area ${isDarkMode ? 'text-zinc-100' : 'text-zinc-900'} focus:outline-none pointer-events-auto`}
                />
            </div>
        </div>
    );
});

// Removed unused components (Stat) to satisfy strict noUnusedLocals build.

// ----------------------------- Custom Filters (header dropdown) -----------------------------
type Filters = {
    assetClasses: Array<'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO'>;
    // Days/Time
    daysOfWeek: number[]; // 0-6 (Sun-Sat)
    months: number[]; // 0-11
    timeMin?: string; // 'HH:MM'
    timeMax?: string; // 'HH:MM'
    // Price/Volume
    volumeMin?: number;
    volumeMax?: number;
    // Win/Loss Trades
    pnlMin?: number;
    pnlMax?: number;
    // Placeholders for future filters (activated UI, not yet applied)
    durationMin?: string;
    durationMax?: string;
    priceMin?: number;
    priceMax?: number;
    inTradePriceMin?: number;
    inTradePriceMax?: number;
    execCountMin?: number;
    execCountMax?: number;
    gapMinPct?: number;
    gapMaxPct?: number;
    rvolMin?: number;
    rvolMax?: number;
    atrMin?: number;
    atrMax?: number;
    dayType?: string;
    entryPctAtrMin?: number;
    entryPctAtrMax?: number;
    moveIntraPctMin?: number;
    moveIntraPctMax?: number;
    priorDayRvolMin?: number;
    priorDayRvolMax?: number;
    dayResult?: string; // All | Winning | Losing
    pnlPctMin?: number;
    pnlPctMax?: number;
    pnlRMin?: number;
    pnlRMax?: number;
    tagWinPctMin?: number;
    excludedTags?: string;
    tagProfitFactorMin?: number;
    tagPnlMin?: number;
    tagPnlMax?: number;
    tagMfePosMin?: number;
    tagMfePosMax?: number;
    tagCountMin?: number;
    tagCountMax?: number;
    tagMaePosMin?: number;
    tagMaePosMax?: number;
    tagVolumeMin?: number;
    tagVolumeMax?: number;
    sma5MinPct?: number; sma5MaxPct?: number;
    sma10MinPct?: number; sma10MaxPct?: number;
    sma20MinPct?: number; sma20MaxPct?: number;
    sma50MinPct?: number; sma50MaxPct?: number;
    sma100MinPct?: number; sma100MaxPct?: number;
    sma200MinPct?: number; sma200MaxPct?: number;
    bestExitPnlMin?: number; bestExitPnlMax?: number;
    posMaeMin?: number; posMaeMax?: number;
    priceMaeMin?: number; priceMaeMax?: number;
    timeToPosMaeMin?: number; timeToPosMaeMax?: number;
    timeToPriceMaeMin?: number; timeToPriceMaeMax?: number;
    bestExitRMin?: number; bestExitRMax?: number;
    posMfeMin?: number; posMfeMax?: number;
    priceMfeMin?: number; priceMfeMax?: number;
    timeToPosMfeMin?: number; timeToPosMfeMax?: number;
    timeToPriceMfeMin?: number; timeToPriceMfeMax?: number;
    initialRiskMin?: number; initialRiskMax?: number;
    exitEfficiencyMinPct?: number; exitEfficiencyMaxPct?: number;
    commissionsMin?: number; commissionsMax?: number;
    pnlPctOfMaeMin?: number; pnlPctOfMaeMax?: number;
    pnlPctOfMfeMin?: number; pnlPctOfMfeMax?: number;
};
const CustomFilters = ({
    isDark,
    value,
    onApply,
    assetVisibility,
}: {
    isDark?: boolean;
    value: Filters;
    onApply: (next: Filters) => void;
    assetVisibility: AssetClassVisibility;
}) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<Filters>(value);
    const ref = useRef<HTMLDivElement | null>(null);
    const suppressToggleRef = useRef(false);
    const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const assetClassOptions: Array<{ value: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO'; label: string }> = useMemo(() => (
        [
            { value: 'STOCKS' as const, label: 'Stocks' },
            { value: 'FUTURES' as const, label: 'Futures' },
            { value: 'FOREX' as const, label: 'Forex' },
            { value: 'CRYPTO' as const, label: 'Crypto' },
        ].filter((option) => Boolean(assetVisibility[option.value]))
    ), [assetVisibility]);

    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            // If the click is inside the TimeField portal popover, do NOT close the Filters menu.
            // The time picker popover is rendered via portal with the class 'filters-time-pop'.
            const targetEl = e.target as Element | null;
            if (targetEl && typeof targetEl.closest === 'function') {
                const inTimePopover = targetEl.closest('.filters-time-pop');
                if (inTimePopover) return;
            }
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocDown);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDocDown); document.removeEventListener('keydown', onKey); };
    }, []);

    useEffect(() => {
        if (!open) return;
        setDraft({
            ...value,
            assetClasses: (value.assetClasses || []).filter((assetClass) => Boolean(assetVisibility[assetClass])),
        });
    }, [assetVisibility, open, value]);

    const activeCount = (
        ((value.assetClasses?.length || 0) > 0 ? 1 : 0) +
        ((value.daysOfWeek?.length || 0) > 0 ? 1 : 0) +
        ((value.months?.length || 0) > 0 ? 1 : 0) +
        (value.timeMin ? 1 : 0) +
        (value.timeMax ? 1 : 0) +
        (value.volumeMin != null ? 1 : 0) +
        (value.volumeMax != null ? 1 : 0) +
        (value.pnlMin != null ? 1 : 0) +
        (value.pnlMax != null ? 1 : 0) +
        (value.dayResult ? 1 : 0)
    );
    const isActive = activeCount > 0;

    // Collapsible sections for advanced filters
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
        asset: true,
        days: true,
        price: true,
        instrument: true,
        winloss: true,
        tags: true,
        sma: true,
        stats: true,
    });
    const toggle = (k: keyof typeof collapsed) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));

    const emptyLocal = (): Filters => ({
        assetClasses: [],
        daysOfWeek: [],
        months: [],
        timeMin: undefined,
        timeMax: undefined,
        volumeMin: undefined,
        volumeMax: undefined,
        pnlMin: undefined,
        pnlMax: undefined,
    } as Filters);

    const FieldLabel = ({ children }: { children: React.ReactNode }) => (
        <div className="text-sm whitespace-nowrap pr-3 text-zinc-700 dark:text-zinc-300">{children}</div>
    );
    const NumInput = ({ value, onChange, placeholder }: { value?: number; onChange: (v?: number) => void; placeholder?: string }) => {
        const step = 1;
        const parseNum = (v: unknown): number | undefined => {
            if (v === '' || v === null || v === undefined) return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
        };
        const bump = (delta: number) => {
            const cur = parseNum(value ?? '');
            const next = (cur ?? 0) + delta;
            onChange(next);
        };
        return (
            <div className="relative inline-flex w-full items-center">
                <input
                    type="number"
                    inputMode="decimal"
                    value={value ?? ''}
                    onChange={(e) => {
                        const v = e.target.value.trim();
                        onChange(v === '' ? undefined : Number(v));
                    }}
                    placeholder={placeholder}
                    className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 pr-10 pl-3 text-sm text-zinc-100 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 pr-10 pl-3 text-sm text-zinc-900 placeholder-zinc-400'} num-no-spin`}
                />
                <div
                    className={`${dark ? 'absolute right-0.5 top-0.5 bottom-0.5 rounded-md border border-zinc-700 bg-zinc-800/60' : 'absolute right-0.5 top-0.5 bottom-0.5 rounded-md border border-zinc-300 bg-zinc-100/70'} overflow-hidden flex flex-col w-8`}
                    role="group" aria-label="Adjust value"
                >
                    <button
                        type="button"
                        aria-label="Increment"
                        title="Increment"
                        onClick={(e) => { e.preventDefault(); bump(step); }}
                        className={`${dark ? 'flex-1 grid place-items-center hover:bg-zinc-700/70 active:bg-zinc-700 text-zinc-200' : 'flex-1 grid place-items-center hover:bg-zinc-200 active:bg-zinc-300 text-zinc-700'} relative`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
                        <span className={`${dark ? 'absolute bottom-0 left-1 right-1 h-px bg-zinc-700' : 'absolute bottom-0 left-1 right-1 h-px bg-zinc-300'}`} aria-hidden></span>
                    </button>
                    <button
                        type="button"
                        aria-label="Decrement"
                        title="Decrement"
                        onClick={(e) => { e.preventDefault(); bump(-step); }}
                        className={`${dark ? 'flex-1 grid place-items-center hover:bg-zinc-700/70 active:bg-zinc-700 text-zinc-200' : 'flex-1 grid place-items-center hover:bg-zinc-200 active:bg-zinc-300 text-zinc-700'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                </div>
            </div>
        );
    };
    const MinMax = ({ left = 'min', right = 'max', min, max, setMin, setMax }: { left?: string; right?: string; min?: number; max?: number; setMin?: (v?: number) => void; setMax?: (v?: number) => void }) => (
        <div className="flex gap-2 items-center flex-1">
            {setMin ? (
                <NumInput value={min} onChange={setMin} placeholder={left} />
            ) : (
                <input disabled placeholder={left} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-400 placeholder-zinc-400'}`} />
            )}
            {setMax ? (
                <NumInput value={max} onChange={setMax} placeholder={right} />
            ) : (
                <input disabled placeholder={right} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-400 placeholder-zinc-400'}`} />
            )}
        </div>
    );
    const Row = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
        <div className="flex items-center gap-3">
            <FieldLabel>{label}</FieldLabel>
            {children}
        </div>
    );
    const Section = ({ id, title, children }: { id: keyof typeof collapsed; title: string; children: React.ReactNode }) => (
        <div className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} border-t pt-3 first:border-t-0 first:pt-0`}>
            <button
                type="button"
                aria-expanded={!collapsed[id]}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(id);
                }}
                className={`w-full flex items-center justify-between rounded-md px-1 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${dark ? 'text-zinc-100 hover:bg-zinc-800/50' : 'text-zinc-800 hover:bg-zinc-50'}`}
            >
                <div className={`text-sm font-semibold ${dark ? 'text-zinc-100' : 'text-zinc-800'}`}>{title}</div>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${collapsed[id] ? '' : 'rotate-180'}`} />
            </button>
            {!collapsed[id] && (
                <div className={`mt-3 space-y-3 rounded-lg border relative ${dark ? 'border-blue-600/40 bg-zinc-900/40' : 'border-blue-600/30 bg-zinc-50'}`}>
                    {/* subtle left accent stripe */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${dark ? 'bg-blue-600/50' : 'bg-blue-600/30'}`} aria-hidden></div>
                    {/* faint inner ring for depth */}
                    <div className={`pointer-events-none absolute inset-0 rounded-lg ${dark ? 'ring-1 ring-blue-500/10' : 'ring-1 ring-blue-500/10'}`} aria-hidden></div>
                    <div className="p-3 space-y-3">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
    const WeekdaySelector = () => {
        const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        return (
            <div className="flex flex-wrap gap-1">
                {days.map((d, idx) => {
                    const selected = draft.daysOfWeek.includes(idx);
                    return (
                        <button
                            key={d}
                            type="button"
                            className={`px-2 h-8 rounded-md text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'bg-blue-600 text-white border-blue-600' : (dark ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')}`}
                            onClick={() => setDraft(prev => ({ ...prev, daysOfWeek: selected ? prev.daysOfWeek.filter(x => x !== idx) : [...prev.daysOfWeek, idx] }))}>
                            {d}
                        </button>
                    );
                })}
            </div>
        );
    };
    const AssetClassSelector = () => (
        <div className="flex flex-wrap gap-1">
            {assetClassOptions.map((option) => {
                const selected = draft.assetClasses.includes(option.value);
                return (
                    <button
                        key={option.value}
                        type="button"
                        className={`px-2 h-8 rounded-md text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'bg-blue-600 text-white border-blue-600' : (dark ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')}`}
                        onClick={() =>
                            setDraft((prev) => ({
                                ...prev,
                                assetClasses: selected
                                    ? prev.assetClasses.filter((assetClass) => assetClass !== option.value)
                                    : [...prev.assetClasses, option.value],
                            }))
                        }
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
    const MonthSelector = () => {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return (
            <div className="flex flex-wrap gap-1">
                {months.map((m, idx) => {
                    const selected = draft.months.includes(idx);
                    return (
                        <button
                            key={m}
                            type="button"
                            className={`px-2 h-8 rounded-md text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'bg-blue-600 text-white border-blue-600' : (dark ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')}`}
                            onClick={() => setDraft(prev => ({ ...prev, months: selected ? prev.months.filter(x => x !== idx) : [...prev.months, idx] }))}>
                            {m}
                        </button>
                    );
                })}
            </div>
        );
    };

    // Theme-aware custom time picker
    // - Stores value as 24h 'HH:MM'
    // - Supports display/selection in 12h AM/PM or 24h based on 'format'
    const TimeField = ({ value, onChange, placeholder = '--:--' }: { value?: string; onChange: (v?: string) => void; placeholder?: string; format?: '12h' | '24h' }) => {
        const [open, setOpen] = useState(false);
        const btnRef = useRef<HTMLButtonElement | null>(null);
        const popRef = useRef<HTMLDivElement | null>(null);
        const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
        // Temporary selection while the popover is open; only committed on Apply
        const [tempH24, setTempH24] = useState<number | null>(null);
        const [tempM, setTempM] = useState<number | null>(null);
        const updatePosition = useCallback(() => {
            const b = btnRef.current?.getBoundingClientRect();
            if (!b) return;
            const MIN_LEFT = 8;
            const MARGIN = 6;
            const desiredLeft = b.left + window.scrollX;
            // clamp within viewport allowing for min width ~260
            const maxLeft = Math.max(MIN_LEFT, window.innerWidth - MIN_LEFT - 260);
            const left = Math.min(Math.max(desiredLeft, MIN_LEFT), maxLeft);
            const top = b.bottom + window.scrollY + MARGIN;
            setPos({ left, top });
        }, []);
        // Do not close on outside clicks; only the Apply button closes the popover
        useEffect(() => {
            const onDoc = () => {
                if (!open) return;
                // Intentionally do nothing to keep popover open until Apply is pressed
            };
            document.addEventListener('mousedown', onDoc);
            return () => document.removeEventListener('mousedown', onDoc);
        }, [open]);
        useEffect(() => {
            if (!open) return;
            updatePosition();
            const onResize = () => updatePosition();
            const onScroll = () => updatePosition();
            window.addEventListener('resize', onResize);
            // capture true to respond to any ancestor element scrolls
            document.addEventListener('scroll', onScroll, true);
            return () => {
                window.removeEventListener('resize', onResize);
                document.removeEventListener('scroll', onScroll, true);
            };
        }, [open, updatePosition]);
        // Parse canonical 24h HH:MM string
        const parse24 = (v?: string): { h24: number; m: number } | null => {
            if (!v || !/^\d{2}:\d{2}$/.test(v)) return null;
            const [hh, mm] = v.split(':');
            const h24 = Math.max(0, Math.min(23, Number(hh)));
            const m = Math.max(0, Math.min(59, Number(mm)));
            return { h24, m };
        };
        const canon = parse24(value || undefined);
        // Initialize temporary selection from current value when opening
        useEffect(() => {
            if (open) {
                setTempH24(canon ? canon.h24 : null);
                setTempM(canon ? canon.m : null);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);
        // Display string according to chosen format
        const display = (() => {
            if (!canon) return '';
            const mm = String(canon.m).padStart(2, '0');
            const hh = String(canon.h24).padStart(2, '0');
            return `${hh}:${mm}`;
        })();
        // Commit happens only when pressing Apply; selections update temporary state
        const commitApply = () => {
            if (tempH24 == null || tempM == null) {
                onChange(undefined);
            } else {
                const hh = String(tempH24).padStart(2, '0');
                const mm = String(tempM).padStart(2, '0');
                onChange(`${hh}:${mm}`);
            }
            setOpen(false);
        };
    const selH24: number | null = tempH24;
    const selM: number | null = tempM;
        return (
            <div className="relative">
                <button
                    ref={btnRef}
                    type="button"
                    onClick={() => { if (!open) setOpen(true); }}
                    className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100 hover:bg-zinc-800' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900 hover:bg-zinc-100'} flex items-center justify-between`}
                >
                    <span className={`${display ? '' : (dark ? 'text-zinc-500' : 'text-zinc-400')}`}>{display || placeholder}</span>
                    <span className={`${dark ? 'text-zinc-500' : 'text-zinc-400'}`} aria-hidden>🕒</span>
                </button>
                {open && ReactDOM.createPortal(
                    <div ref={popRef} className={`absolute z-[999999] p-2 rounded-md shadow-lg border ${dark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} filters-time-pop`} style={{ minWidth: 260, left: pos.left, top: pos.top, position: 'absolute' }}>
                        <div className="flex gap-2">
                                <div className={`${dark ? 'border-zinc-700' : 'border-zinc-200'} w-20 max-h-48 overflow-auto rounded-md border`}>
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <button key={h} type="button" className={`w-full text-left px-3 py-1 text-sm ${h===selH24 ? 'bg-blue-600 text-white' : (dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-800 hover:bg-zinc-100')}`} onClick={() => setTempH24(h)}>
                                            {String(h).padStart(2, '0')}
                                        </button>
                                    ))}
                                </div>
                                <div className={`${dark ? 'border-zinc-700' : 'border-zinc-200'} w-20 max-h-48 overflow-auto rounded-md border`}>
                                    {Array.from({ length: 60 }, (_, m) => (
                                        <button key={m} type="button" className={`w-full text-left px-3 py-1 text-sm ${m===selM ? 'bg-blue-600 text-white' : (dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-800 hover:bg-zinc-100')}`} onClick={() => setTempM(m)}>
                                            {String(m).padStart(2, '0')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        <div className="mt-2 flex justify-between">
                            <button type="button" className={`${dark ? 'text-zinc-200 hover:text-white' : 'text-zinc-700 hover:text-zinc-900'} text-xs`} onClick={() => { setTempH24(null); setTempM(null); onChange(undefined); }}>Clear</button>
                            <button type="button" className="text-xs text-blue-600 hover:underline" onClick={commitApply}>Apply</button>
                        </div>
                    </div>, document.body)
                }
            </div>
        );
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => { if (suppressToggleRef.current) return; setOpen((v) => !v); }}
                className={`${(open || isActive)
                    ? 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl bg-blue-600 text-white hover:bg-blue-500'
                    : (dark
                        ? 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl border bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800'
                        : 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl border bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')
                } transition-colors`}
            >
                {/* Sliders icon to match previous button */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <line x1="21" y1="4" x2="14" y2="4"></line>
                    <line x1="10" y1="4" x2="3" y2="4"></line>
                    <line x1="21" y1="12" x2="12" y2="12"></line>
                    <line x1="8" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="20" x2="16" y2="20"></line>
                    <line x1="12" y1="20" x2="3" y2="20"></line>
                    <circle cx="12" cy="4" r="2"></circle>
                    <circle cx="8" cy="12" r="2"></circle>
                    <circle cx="16" cy="20" r="2"></circle>
                </svg>
                <span className="text-sm font-medium">Filters</span>
                {isActive && (
                    <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold ${(open || isActive) ? 'bg-white/20 text-white' : (dark ? 'bg-zinc-800 text-zinc-100' : 'bg-zinc-200 text-zinc-800')}`}>
                        {activeCount}
                    </span>
                )}
                <ChevronDownIcon className={`w-4 h-4 opacity-80 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className={`filters-panel absolute right-0 top-full w-[440px] max-h-[70vh] z-[999] rounded-xl shadow-lg border ${dark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} flex flex-col overflow-hidden`}>
                    <div className="p-3 space-y-3 text-sm overflow-auto flex-1">
                        {/* Removed: Only winning/losing checkboxes */}
                        {/* Removed: Hide fees and Group by symbol */}

                        {/* Advanced sections */}
                        <Section id="asset" title="Asset Class">
                            <Row label="Asset Class:">
                                <AssetClassSelector />
                            </Row>
                        </Section>

                        <Section id="days" title="Days/Time">
                            <Row label="Day of The Week:">
                                <WeekdaySelector />
                            </Row>
                            <Row label="Time of Day:">
                                <div className="flex gap-2 items-center flex-1">
                                    <TimeField value={draft.timeMin} onChange={(v) => setDraft(prev => ({ ...prev, timeMin: v }))} />
                                    <TimeField value={draft.timeMax} onChange={(v) => setDraft(prev => ({ ...prev, timeMax: v }))} />
                                </div>
                            </Row>
                            <Row label="Month:">
                                <MonthSelector />
                            </Row>
                            <Row label="Duration:">
                                <div className="flex gap-2 items-center flex-1">
                                    <TimeField format="24h" value={draft.durationMin} onChange={(v) => setDraft(prev => ({ ...prev, durationMin: v }))} />
                                    <TimeField format="24h" value={draft.durationMax} onChange={(v) => setDraft(prev => ({ ...prev, durationMax: v }))} />
                                </div>
                            </Row>
                        </Section>

                        <Section id="price" title="Price/Volume">
                            <Row label="Price ($):"><MinMax min={draft.priceMin} max={draft.priceMax} setMin={(v) => setDraft(prev => ({ ...prev, priceMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, priceMax: v }))} /></Row>
                            <Row label="Volume Traded:"><MinMax min={draft.volumeMin} max={draft.volumeMax} setMin={(v) => setDraft(prev => ({ ...prev, volumeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, volumeMax: v }))} /></Row>
                            <Row label="In-trade Price Range:"><MinMax min={draft.inTradePriceMin} max={draft.inTradePriceMax} setMin={(v) => setDraft(prev => ({ ...prev, inTradePriceMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, inTradePriceMax: v }))} /></Row>
                            <Row label="Executions:"><MinMax min={draft.execCountMin} max={draft.execCountMax} setMin={(v) => setDraft(prev => ({ ...prev, execCountMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, execCountMax: v }))} /></Row>
                        </Section>

                        <Section id="instrument" title="Instrument">
                            <Row label="Gap %:"><MinMax left="% min" right="% max" min={draft.gapMinPct} max={draft.gapMaxPct} setMin={(v) => setDraft(prev => ({ ...prev, gapMinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, gapMaxPct: v }))} /></Row>
                            <Row label="RVOL:"><MinMax min={draft.rvolMin} max={draft.rvolMax} setMin={(v) => setDraft(prev => ({ ...prev, rvolMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, rvolMax: v }))} /></Row>
                            <Row label="ATR:"><MinMax min={draft.atrMin} max={draft.atrMax} setMin={(v) => setDraft(prev => ({ ...prev, atrMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, atrMax: v }))} /></Row>
                            <Row label="Day Type:">
                                <div className="flex-1">
                                    <select aria-label="Day Type" value={draft.dayType ?? ''} onChange={(e) => setDraft(prev => ({ ...prev, dayType: e.target.value || undefined }))} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900'}`}>
                                        <option value="">All</option>
                                        <option value="trend">Trend</option>
                                        <option value="range">Range</option>
                                    </select>
                                </div>
                            </Row>
                            <Row label="Entry % of ATR:"><MinMax left="% min" right="% max" min={draft.entryPctAtrMin} max={draft.entryPctAtrMax} setMin={(v) => setDraft(prev => ({ ...prev, entryPctAtrMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, entryPctAtrMax: v }))} /></Row>
                            <Row label="% Move Intraday:"><MinMax left="% min" right="% max" min={draft.moveIntraPctMin} max={draft.moveIntraPctMax} setMin={(v) => setDraft(prev => ({ ...prev, moveIntraPctMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, moveIntraPctMax: v }))} /></Row>
                            <Row label="Prior Day RVOL:"><MinMax min={draft.priorDayRvolMin} max={draft.priorDayRvolMax} setMin={(v) => setDraft(prev => ({ ...prev, priorDayRvolMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, priorDayRvolMax: v }))} /></Row>
                        </Section>

                        <Section id="winloss" title="Win/Loss Trades">
                            <Row label="Trade Result:">
                                <div className="inline-flex rounded-md overflow-hidden border border-zinc-300 dark:border-zinc-700 text-sm">
                                    {(['All','Winning','Losing'] as const).map((opt) => {
                                        const isSel = (opt === 'All' && !draft.dayResult) || (draft.dayResult === opt);
                                        return (
                                            <button key={opt} type="button" onClick={() => {
                                                setDraft((prev) => ({ ...prev, dayResult: opt === 'All' ? undefined : opt }));
                                            }} className={`${isSel ? (dark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white') : (dark ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-zinc-700')} px-3 h-9`}>
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Row>
                            <Row label="Day Result:">
                                <div className="flex-1">
                                    <select aria-label="Day Result" disabled className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-500'}`}>
                                        <option>All</option>
                                    </select>
                                </div>
                            </Row>
                            <Row label="P&L % Gain:"><MinMax left="% min" right="% max" min={draft.pnlPctMin} max={draft.pnlPctMax} setMin={(v) => setDraft(prev => ({ ...prev, pnlPctMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, pnlPctMax: v }))} /></Row>
                            <Row label="P&L (R):"><MinMax min={draft.pnlRMin} max={draft.pnlRMax} setMin={(v) => setDraft(prev => ({ ...prev, pnlRMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, pnlRMax: v }))} /></Row>
                            <Row label="P&L ($):"><MinMax min={draft.pnlMin} max={draft.pnlMax} setMin={(v) => setDraft(prev => ({ ...prev, pnlMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, pnlMax: v }))} /></Row>
                        </Section>

                        <Section id="tags" title="Tags">
                            <Row label="Tag Win %:">
                                <div className="flex-1">
                                    <NumInput value={draft.tagWinPctMin} onChange={(v) => setDraft(prev => ({ ...prev, tagWinPctMin: v }))} placeholder="% minimum" />
                                </div>
                            </Row>
                            <Row label="Excluded Tags:">
                                <div className="flex-1">
                                    <input value={draft.excludedTags ?? ''} onChange={(e) => setDraft(prev => ({ ...prev, excludedTags: e.target.value }))} placeholder="Select tags" className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900 placeholder-zinc-400'}`} />
                                </div>
                            </Row>
                            <Row label="Tag Profit Factor:"><div className="flex-1"><NumInput value={draft.tagProfitFactorMin} onChange={(v) => setDraft(prev => ({ ...prev, tagProfitFactorMin: v }))} placeholder="(positive)" /></div></Row>
                            <Row label="Tag P&L:"><MinMax min={draft.tagPnlMin} max={draft.tagPnlMax} setMin={(v) => setDraft(prev => ({ ...prev, tagPnlMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, tagPnlMax: v }))} /></Row>
                            <Row label="Tag avg. pos. MFE:"><MinMax min={draft.tagMfePosMin} max={draft.tagMfePosMax} setMin={(v) => setDraft(prev => ({ ...prev, tagMfePosMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, tagMfePosMax: v }))} /></Row>
                            <Row label="Tag Count:"><MinMax min={draft.tagCountMin} max={draft.tagCountMax} setMin={(v) => setDraft(prev => ({ ...prev, tagCountMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, tagCountMax: v }))} /></Row>
                            <Row label="Tag avg. pos. MAE:"><MinMax min={draft.tagMaePosMin} max={draft.tagMaePosMax} setMin={(v) => setDraft(prev => ({ ...prev, tagMaePosMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, tagMaePosMax: v }))} /></Row>
                            <Row label="Tag Volume:"><MinMax min={draft.tagVolumeMin} max={draft.tagVolumeMax} setMin={(v) => setDraft(prev => ({ ...prev, tagVolumeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, tagVolumeMax: v }))} /></Row>
                        </Section>

                        <Section id="sma" title="Entry Distance to Daily SMA">
                            <Row label={`5 SMA:`}><MinMax left="% min" right="% max" min={draft.sma5MinPct} max={draft.sma5MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma5MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma5MaxPct: v }))} /></Row>
                            <Row label={`10 SMA:`}><MinMax left="% min" right="% max" min={draft.sma10MinPct} max={draft.sma10MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma10MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma10MaxPct: v }))} /></Row>
                            <Row label={`20 SMA:`}><MinMax left="% min" right="% max" min={draft.sma20MinPct} max={draft.sma20MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma20MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma20MaxPct: v }))} /></Row>
                            <Row label={`50 SMA:`}><MinMax left="% min" right="% max" min={draft.sma50MinPct} max={draft.sma50MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma50MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma50MaxPct: v }))} /></Row>
                            <Row label={`100 SMA:`}><MinMax left="% min" right="% max" min={draft.sma100MinPct} max={draft.sma100MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma100MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma100MaxPct: v }))} /></Row>
                            <Row label={`200 SMA:`}><MinMax left="% min" right="% max" min={draft.sma200MinPct} max={draft.sma200MaxPct} setMin={(v) => setDraft(prev => ({ ...prev, sma200MinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, sma200MaxPct: v }))} /></Row>
                        </Section>

                        <Section id="stats" title="Statistics">
                            <Row label="Best Exit P&L ($):"><MinMax min={draft.bestExitPnlMin} max={draft.bestExitPnlMax} setMin={(v) => setDraft(prev => ({ ...prev, bestExitPnlMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, bestExitPnlMax: v }))} /></Row>
                            <Row label="Position MAE:"><MinMax min={draft.posMaeMin} max={draft.posMaeMax} setMin={(v) => setDraft(prev => ({ ...prev, posMaeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, posMaeMax: v }))} /></Row>
                            <Row label="Price MAE:"><MinMax min={draft.priceMaeMin} max={draft.priceMaeMax} setMin={(v) => setDraft(prev => ({ ...prev, priceMaeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, priceMaeMax: v }))} /></Row>
                            <Row label="Time to pos. MAE:"><MinMax min={draft.timeToPosMaeMin} max={draft.timeToPosMaeMax} setMin={(v) => setDraft(prev => ({ ...prev, timeToPosMaeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, timeToPosMaeMax: v }))} /></Row>
                            <Row label="Time to Price MAE:"><MinMax min={draft.timeToPriceMaeMin} max={draft.timeToPriceMaeMax} setMin={(v) => setDraft(prev => ({ ...prev, timeToPriceMaeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, timeToPriceMaeMax: v }))} /></Row>
                            <Row label="Best Exit P&L (R):"><MinMax min={draft.bestExitRMin} max={draft.bestExitRMax} setMin={(v) => setDraft(prev => ({ ...prev, bestExitRMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, bestExitRMax: v }))} /></Row>
                            <Row label="Position MFE:"><MinMax min={draft.posMfeMin} max={draft.posMfeMax} setMin={(v) => setDraft(prev => ({ ...prev, posMfeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, posMfeMax: v }))} /></Row>
                            <Row label="Price MFE:"><MinMax min={draft.priceMfeMin} max={draft.priceMfeMax} setMin={(v) => setDraft(prev => ({ ...prev, priceMfeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, priceMfeMax: v }))} /></Row>
                            <Row label="Time to pos. MFE:"><MinMax min={draft.timeToPosMfeMin} max={draft.timeToPosMfeMax} setMin={(v) => setDraft(prev => ({ ...prev, timeToPosMfeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, timeToPosMfeMax: v }))} /></Row>
                            <Row label="Time to Price MFE:"><MinMax min={draft.timeToPriceMfeMin} max={draft.timeToPriceMfeMax} setMin={(v) => setDraft(prev => ({ ...prev, timeToPriceMfeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, timeToPriceMfeMax: v }))} /></Row>
                            <Row label="Initial Risk:"><MinMax min={draft.initialRiskMin} max={draft.initialRiskMax} setMin={(v) => setDraft(prev => ({ ...prev, initialRiskMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, initialRiskMax: v }))} /></Row>
                            <Row label="Exit Efficiency (%):"><MinMax left="% min" right="% max" min={draft.exitEfficiencyMinPct} max={draft.exitEfficiencyMaxPct} setMin={(v) => setDraft(prev => ({ ...prev, exitEfficiencyMinPct: v }))} setMax={(v) => setDraft(prev => ({ ...prev, exitEfficiencyMaxPct: v }))} /></Row>
                            <Row label={`Comissions & Fee's:`}><MinMax min={draft.commissionsMin} max={draft.commissionsMax} setMin={(v) => setDraft(prev => ({ ...prev, commissionsMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, commissionsMax: v }))} /></Row>
                            <Row label={`P&L % of MAE:`}><MinMax left="% min" right="% max" min={draft.pnlPctOfMaeMin} max={draft.pnlPctOfMaeMax} setMin={(v) => setDraft(prev => ({ ...prev, pnlPctOfMaeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, pnlPctOfMaeMax: v }))} /></Row>
                            <Row label={`P&L % of MFE:`}><MinMax left="% min" right="% max" min={draft.pnlPctOfMfeMin} max={draft.pnlPctOfMfeMax} setMin={(v) => setDraft(prev => ({ ...prev, pnlPctOfMfeMin: v }))} setMax={(v) => setDraft(prev => ({ ...prev, pnlPctOfMfeMax: v }))} /></Row>
                        </Section>
                    </div>
                    <div className={`p-3 flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-zinc-800 sticky bottom-0 ${dark ? 'bg-zinc-900' : 'bg-white'}`}>
                        <button
                            className={`${dark ? 'px-3 h-9 rounded-lg text-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700' : 'px-3 h-9 rounded-lg text-sm bg-zinc-100 text-zinc-800 hover:bg-zinc-200'}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const empty = emptyLocal();
                                setDraft(empty);
                                onApply(empty);
                                suppressToggleRef.current = true;
                                setOpen(false);
                                setTimeout(() => { suppressToggleRef.current = false; }, 0);
                            }}
                        >
                            Reset
                        </button>
                        <div className="flex items-center gap-2">
                            <button
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); suppressToggleRef.current = true; setOpen(false); setTimeout(() => { suppressToggleRef.current = false; }, 0); }}
                                className={`${dark ? 'px-3 h-9 rounded-lg border bg-zinc-900 border-zinc-700 text-sm text-zinc-200 hover:bg-zinc-800' : 'px-3 h-9 rounded-lg border bg-white border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-100'}`}
                            >
                                Cancel
                            </button>
                             <button
                                 onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onApply(draft); suppressToggleRef.current = true; setOpen(false); setTimeout(() => { suppressToggleRef.current = false; }, 0); }}
                                className="px-3 h-9 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-500"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ----------------------------- Date Range Picker Component -----------------------------
const PRESET_RANGES = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month', 'Last Month', 'YTD', 'Custom Range'] as const;

const DateRangePicker = ({ onApply, initialRange, isDark }: { onApply: (start: Date, end: Date) => void; initialRange: { start: Date; end: Date }; isDark?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    // No preset button selected by default; the page/calendar will still use today's range
    const [selectedPreset, setSelectedPreset] = useState<string>('');
    const pickerRef = useRef<HTMLDivElement>(null);
    const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    // Helpers for day-level normalization and comparisons
    const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
    const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
    const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const [draftStartDate, setDraftStartDate] = useState<Date | null>(initialRange.start);
    const [draftEndDate, setDraftEndDate] = useState<Date | null>(initialRange.end);
    const [viewDate, setViewDate] = useState<Date>(initialRange.end || new Date());

    const goPrev = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    const goNext = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            // Normalize drafts to start-of-day so calendar cell equality matches and endpoints are circled
            const calStart = startOfDay(initialRange.start);
            const calEnd = startOfDay(initialRange.end);
            setDraftStartDate(calStart);
            setDraftEndDate(calEnd);
            setViewDate(initialRange.end || new Date());
            // Helper: detect preset by comparing calendar days
            const detectPresetFromRange = (range: { start: Date; end: Date }): string => {
                for (const preset of PRESET_RANGES) {
                    if (preset === 'Custom Range') continue;
                    const { start, end } = calculatePresetRange(preset);
                    if (sameDay(range.start, start) && sameDay(range.end, end)) return preset;
                }
                return 'Custom Range';
            };
            // Highlight the matching preset in the left column, if any
            setSelectedPreset(detectPresetFromRange({ start: startOfDay(initialRange.start), end: startOfDay(initialRange.end) }));
        }
    }, [isOpen, initialRange]);

    const calculatePresetRange = (preset: string) => {
        const end = new Date();
        const start = new Date();
        end.setHours(23, 59, 59, 999);
        start.setHours(0, 0, 0, 0);

        switch (preset) {
            case 'Today':
                start.setTime(end.getTime());
                start.setHours(0, 0, 0, 0);
                break;
            case 'Yesterday':
                start.setDate(start.getDate() - 1);
                end.setDate(end.getDate() - 1);
                break;
            case 'Last 7 Days':
                start.setDate(start.getDate() - 6);
                break;
            case 'Last 30 Days':
                start.setDate(start.getDate() - 29);
                break;
            case 'This Month':
                start.setDate(1);
                break;
            case 'Last Month':
                start.setMonth(start.getMonth() - 1, 1);
                end.setDate(0);
                break;
            case 'YTD':
                start.setMonth(0, 1);
                break;
        }
        return { start, end };
    };

    const handlePresetClick = (preset: string) => {
        setSelectedPreset(preset);
        const { start, end } = calculatePresetRange(preset);
        // Populate drafts with start-of-day for consistent calendar equality/circles
        const calStart = startOfDay(start);
        const calEnd = startOfDay(end);
        setDraftStartDate(calStart);
        setDraftEndDate(calEnd);
        // Show the end month in the calendar view for convenience
        setViewDate(end || new Date());
        // For non-custom presets, apply full-day range immediately and close
        if (preset !== 'Custom Range') {
            onApply(startOfDay(start), endOfDay(end));
            setIsOpen(false);
        }
    };

    // Removed duplicated auto-apply effect to avoid double-apply; handlePresetClick applies directly

    const handleApplyCustomRange = () => {
        if (draftStartDate && draftEndDate) {
            // Apply full-day range for filtering inclusively
            onApply(startOfDay(draftStartDate), endOfDay(draftEndDate));
            setIsOpen(false);
        }
    };

    const handleDateClick = (day: Date) => {
        if (!draftStartDate || (draftStartDate && draftEndDate)) {
            setDraftStartDate(day);
            setDraftEndDate(null);
            setSelectedPreset('Custom Range');
        } else if (draftStartDate && !draftEndDate) {
            if (day < draftStartDate) {
                setDraftEndDate(draftStartDate);
                setDraftStartDate(day);
            } else {
                setDraftEndDate(day);
            }
        }
    };

    const getMonthDays = (date: Date): (Date | null)[] => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const startDayOfWeek = firstDayOfMonth.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = Array(startDayOfWeek).fill(null) as (Date | null)[];
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const renderCalendar = (date: Date) => {
        const monthDays = getMonthDays(date);
        const monthName = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();

        return (
            <div>
                <div className="text-center font-semibold mb-2">{monthName} {year}</div>
                {/* Weekday header: 7 equal columns; match horizontal gap with days grid */}
                <div className="grid grid-cols-7 gap-x-2 text-center text-sm tracking-wide text-zinc-500 justify-items-center">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                        <div key={d} className="py-1 h-9 flex items-center justify-center">{d}</div>
                    ))}
                </div>
                {/* Days grid: 7 equal columns with symmetric gaps */}
                <div className="grid grid-cols-7 gap-x-2 gap-y-3 mt-2 text-center justify-items-center">
                    {monthDays.map((dayDate, index) => {
                        if (!dayDate) return <div key={index} className="h-9 w-9" />;
                        const dayTime = dayDate.getTime();
                        const isStartDate = draftStartDate && dayTime === draftStartDate.getTime();
                        const isEndDate = draftEndDate && dayTime === draftEndDate.getTime();
                        const isInRange = draftStartDate && draftEndDate && dayTime > draftStartDate.getTime() && dayTime < draftEndDate.getTime();

                        // Base button: consistent size for all days to align the pill properly
                        let buttonClasses = 'h-9 w-9 text-sm flex items-center justify-center transition-colors relative';
                        // Start or end of selection: circular badge that sits above the connector
                        if (isStartDate || isEndDate) {
                            // Keep the button minimal; we'll draw the circle as an inner element above the connector
                            buttonClasses += ' rounded-full text-white flex items-center justify-center';
                        } else if (isInRange) {
                            // In-range connector: draw as an underlay span, not via button background (keeps size stable)
                            // Keep the button class minimal to avoid increasing visual size
                            buttonClasses += '';
                        } else {
                            // Normal day: subtle hover
                            buttonClasses += (dark ? ' hover:bg-brand-blue hover:text-white/90 rounded-full' : ' hover:bg-zinc-200 rounded-full');
                        }

                        return (
                            <button key={index} onClick={() => handleDateClick(dayDate)} className={buttonClasses}>
                                {/* Draw half-connector under start/end circles; tuck under by ~1px to avoid seams */}
                                {isStartDate && draftEndDate && dayTime !== draftEndDate.getTime() && (
                                    <span aria-hidden="true" className={`absolute top-[2px] bottom-[2px] left-[calc(50%-1px)] right-0 ${dark ? 'bg-brand-blue' : 'bg-blue-200'} z-0`} />
                                )}
                                {isEndDate && draftStartDate && dayTime !== draftStartDate.getTime() && (
                                    <span aria-hidden="true" className={`absolute top-[2px] bottom-[2px] left-0 right-[calc(50%-1px)] ${dark ? 'bg-brand-blue' : 'bg-blue-200'} z-0`} />
                                )}
                                {isInRange && !isStartDate && !isEndDate && (
                                    <span aria-hidden="true" className={`absolute top-[2px] bottom-[2px] left-0 right-0 ${dark ? 'bg-brand-blue' : 'bg-blue-200'} z-0 rounded-none`} />
                                )}
                                {/* Circle layer above connector for start/end */}
                                {(isStartDate || isEndDate) && (
                                    <span aria-hidden="true" className={`absolute inset-[2px] rounded-full ${dark ? 'bg-blue-600 ring-1 ring-white/80' : 'bg-blue-600 ring-1 ring-white/90'} z-10 flex items-center justify-center`} />
                                )}
                                <span className={`relative ${isStartDate || isEndDate ? 'z-20' : 'z-10'} leading-none font-semibold`}>{dayDate.getDate()}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
    <div className="relative" ref={pickerRef}>
    <button onClick={() => setIsOpen(!isOpen)} className={`${(isOpen || selectedPreset === 'Custom Range') ? 'flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold font-sans bg-blue-600 text-white' : (dark ? 'flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-semibold font-sans bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800' : 'flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-semibold font-sans bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')} transition-colors`}>
            <CalendarIcon className={`${(isOpen || selectedPreset === 'Custom Range') ? 'w-4 h-4 text-white' : (dark ? 'w-4 h-4 text-zinc-400' : 'w-4 h-4 text-zinc-500')}`} />
                <span>Custom Range</span>
            <ChevronDownIcon className={`${(isOpen || selectedPreset === 'Custom Range') ? 'w-4 h-4 text-white' : (dark ? 'w-4 h-4 text-zinc-400' : 'w-4 h-4 text-zinc-500')} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div
                    className={`absolute top-full right-0 mt-2 w-[720px] ${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white border border-zinc-200'} rounded-lg shadow-lg z-10 grid gap-3 p-4 pl-2 sm:grid-cols-[10rem,2rem,2fr,1.7rem] grid-cols-1 items-stretch`}
                >
                    {/* Column 1: Presets */}
                    <div className={`${dark ? 'border-r border-zinc-800 p-2' : 'border-r border-zinc-200 p-2'} col-[1]`}>
                        {PRESET_RANGES.map((preset) => (
                            <button key={preset} onClick={() => handlePresetClick(preset)} className={`w-full text-left px-3 py-2 text-sm rounded-md flex justify-between items-center ${selectedPreset === preset ? 'bg-blue-600 text-white' : (dark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100')}`}>
                                {preset}
                                {selectedPreset === preset && <CheckSmallIcon className="w-4 h-4 text-white" />}
                            </button>
                        ))}
                    </div>

                    {/* Column 2: Prev button */}
                    <div className="col-[2] grid items-center justify-start -ml-2">
                        <button
                            type="button"
                            aria-label="Previous month"
                            onClick={goPrev}
                            className="h-9 w-9 rounded-full bg-blue-600 text-white shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed grid place-items-center"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Column 3: Calendars and actions */}
                    <div className="col-[3] flex flex-col gap-4" tabIndex={0} onKeyDown={(e) => { if (e.key === 'ArrowLeft') goPrev(); if (e.key === 'ArrowRight') goNext(); }}>
                        <div className={`flex justify-end items-center gap-2 pb-4 ${dark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                            <button onClick={() => setIsOpen(false)} className={`${dark ? 'px-4 py-2 rounded-lg text-sm bg-zinc-800 text-zinc-200 hover:bg-zinc-700' : 'px-4 py-2 rounded-lg text-sm bg-zinc-100 hover:bg-zinc-200'}`}>Cancel</button>
                            <button onClick={handleApplyCustomRange} className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700">Apply</button>
                        </div>
                            <div className="flex-1 relative pt-4">
                            <div className="flex-grow grid grid-cols-2 gap-12">
                                {renderCalendar(viewDate)}
                                {renderCalendar(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                            </div>
                        </div>
                    </div>

                    {/* Column 4: Next button */}
                    <div className="col-[4] grid items-center justify-end -mr-2">
                        <button
                            type="button"
                            aria-label="Next month"
                            onClick={goNext}
                            className="h-9 w-9 rounded-full bg-blue-600 text-white shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed grid place-items-center"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ----------------------------- Column Settings Modal -----------------------------

type ColumnDef = { id: string; label: string; locked?: boolean };
const ALL_COLUMNS: ColumnDef[] = [
    { id: 'time', label: 'Time' },
    { id: 'symbol', label: 'Symbol' },
    { id: 'volume', label: 'Quantity' },
    { id: 'executionCount', label: 'Executions' },
    { id: 'side', label: 'Side' },
    { id: 'pnl', label: 'P&L' },
    { id: 'pnlR', label: 'R-Multiple', locked: true },
    { id: 'pnlPercent', label: 'Return %', locked: true },
    { id: 'pnlTicksGross', label: 'P&L Units' },
    { id: 'pnlTicksContract', label: 'Ticks/Contract', locked: true },
    { id: 'commissionAndFees', label: 'Fees' },
    { id: 'initialRisk', label: 'Initial Risk', locked: true },
    { id: 'positionMfe', label: 'Position MFE', locked: true },
    { id: 'positionMae', label: 'Position MAE', locked: true },
    { id: 'priceMfe', label: 'Price MFE', locked: true },
    { id: 'priceMae', label: 'Price MAE', locked: true },
    { id: 'bestExitPnl', label: 'Best Exit P&L', locked: true },
    { id: 'exitEfficiency', label: 'Exit Efficiency', locked: true },
    { id: 'duration', label: 'Duration' },
    { id: 'tags', label: 'Tags' }
];

// Default columns match the compact Journal layout shown in the page table.
// Order base: Time, Symbol, Side, Quantity, Fees, P&L, Duration, Tags
const DEFAULT_COLUMNS = ['time', 'symbol', 'side', 'volume', 'commissionAndFees', 'pnl', 'duration', 'tags'];
const JOURNAL_TABLE_COLUMN_ORDER_KEY = 'journal.tradeTable.columnOrder.v3';
const JOURNAL_TABLE_COLUMN_WIDTHS_KEY = 'journal.tradeTable.columnWidths.v3';
const JOURNAL_TABLE_MIN_COLUMN_WIDTH = 88;
const JOURNAL_TRADE_DETAILS_RETURN_DAY_KEY = 'journal.tradeDetails.returnDay';

type JournalDropIndicatorState = {
    index: number;
    left: number;
};

function normalizeJournalColumnOrder(input: unknown): string[] {
    const validIds = new Set(ALL_COLUMNS.map((column) => column.id));
    const incoming = Array.isArray(input) ? input : [];
    const seen = new Set<string>();
    const ordered = incoming
        .map((value) => String(value ?? '').trim())
        .filter((id) => {
            if (!validIds.has(id) || seen.has(id)) return false;
            seen.add(id);
            return true;
        });

    for (const columnId of DEFAULT_COLUMNS) {
        if (!seen.has(columnId)) ordered.push(columnId);
    }

    for (const column of ALL_COLUMNS) {
        if (!seen.has(column.id)) ordered.push(column.id);
    }

    return ordered;
}

function normalizeJournalColumnWidths(input: unknown): Record<string, number> {
    if (!input || typeof input !== 'object') return {};
    const validIds = new Set(ALL_COLUMNS.map((column) => column.id));
    const next: Record<string, number> = {};

    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
        if (!validIds.has(key)) continue;
        const width = Number(value);
        if (!Number.isFinite(width)) continue;
        next[key] = Math.max(JOURNAL_TABLE_MIN_COLUMN_WIDTH, Math.round(width));
    }

    return next;
}

function ColumnSettingsModal({ isOpen, onClose, visibleColumns, onSave, isDark }: { isOpen: boolean; onClose: () => void; visibleColumns: string[]; onSave: (cols: string[]) => void; isDark?: boolean }) {
    const [draftColumns, setDraftColumns] = useState<Set<string>>(new Set(visibleColumns));
    const [showMaxColumnMessage, setShowMaxColumnMessage] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setDraftColumns(new Set(visibleColumns));
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, visibleColumns, onClose]);

    useEffect(() => {
        if (showMaxColumnMessage) {
            const timer = setTimeout(() => {
                setShowMaxColumnMessage(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showMaxColumnMessage]);

    const handleToggle = (id: string) => {
        setDraftColumns((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else if (newSet.size < 12) {
                newSet.add(id);
            } else {
                setShowMaxColumnMessage(true);
            }
            return newSet;
        });
    };

    const handleSave = () => {
        onSave(Array.from(draftColumns));
        onClose();
    };

    const handleReset = () => {
        setDraftColumns(new Set(DEFAULT_COLUMNS));
    };

    if (!isOpen) return null;
    const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const unlockedColumns = ALL_COLUMNS.filter((col) => !col.locked);
    const pendingColumns = ALL_COLUMNS.filter((col) => col.locked);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div ref={modalRef} className={`${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white'} rounded-lg shadow-xl w-full max-w-2xl relative`}>
                <div className={`p-4 flex justify-between items-center ${dark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                    <h2 className="text-lg font-semibold">Customize Columns</h2>
                    <button aria-label="Close" onClick={onClose} className={`p-1 rounded-full ${dark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'}`}>
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4">
                    <p className={`${dark ? 'text-zinc-300' : 'text-zinc-600'} text-sm mb-4`}>Choose the columns to display in the Journal Log.</p>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                        {unlockedColumns.map((col) => {
                            const isChecked = draftColumns.has(col.id);
                            const isDisabled = !isChecked && draftColumns.size >= 12;
                            return (
                                <label key={col.id} className={`flex items-center space-x-2 ${isDisabled && !isChecked ? 'cursor-not-allowed' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggle(col.id)}
                                        disabled={isDisabled}
                                        className="h-4 w-4 rounded border-zinc-300 accent-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:accent-zinc-500"
                                    />
                                    <span className={`text-sm ${isDisabled ? 'text-zinc-400' : dark ? 'text-zinc-200' : ''}`}>{col.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="mt-5">
                        <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${dark ? 'text-zinc-400' : 'text-zinc-500'} mb-2`}>Pending Integration</div>
                        <div className={`${dark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'} pt-3`}>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                                {pendingColumns.map((col) => {
                                    const isChecked = draftColumns.has(col.id);
                                    const isDisabled = true;
                                    return (
                                        <label key={col.id} className="flex items-center space-x-2 cursor-not-allowed">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleToggle(col.id)}
                                                disabled={isDisabled}
                                                className="h-4 w-4 rounded border-zinc-300 accent-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:accent-zinc-500"
                                            />
                                            <span className={`text-sm ${dark ? 'text-zinc-200' : ''}`}>{col.label}</span>
                                            <Lock className="h-3 w-3 text-zinc-400" />
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {(showMaxColumnMessage || draftColumns.size >= 12) && (
                        <p className="text-sm text-red-500 mt-3">Maximum of 12 columns can be selected.</p>
                    )}
                </div>
                <div className={`p-4 flex justify-end items-center gap-2 ${dark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'}`}>
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Cancel</button>
                    <button onClick={handleReset} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Reset to Defaults</button>
                    <button onClick={handleSave} className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700">Save changes</button>
                </div>
            </div>
        </div>
    );
}

// Theme removed from JournalPage — global/theme handled elsewhere

const clsPnL = (v: number | null | undefined) => {
    const numeric = Number(v);
    if (!Number.isFinite(numeric) || numeric === 0) return 'text-zinc-500 dark:text-zinc-300';
    return numeric > 0 ? 'text-emerald-500' : 'text-red-500';
};

const getDefaultDateRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(end.getDate() - 6); // Last 7 days (today + previous 6 days)
    start.setHours(0, 0, 0, 0);
    return { start, end };
};

const getYtdDateRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);
    return { start, end };
};

// ----------------------------- Main Page Component -----------------------------

// TagEditor removed (unused) — tag storage and persistence hooks retained.

// List of saved note entries for a day
function NoteHistoryList({ entries, isDark, onDelete, onEdit, onPreview, highlightId, allTrades, readOnly = false }: { entries: { id: string; text: string; ts: number; name?: string; symbol?: string; tradeKey?: string }[]; isDark?: boolean; onDelete?: (id: string) => void; onEdit?: (id: string) => void; onPreview?: (id: string) => void; highlightId?: string; allTrades?: Trade[]; readOnly?: boolean }) {
    const displayTimeZone = useDisplayTimezone();
    const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!openMenuId) return;
        const onDocClick = () => setOpenMenuId(null);
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [openMenuId]);

    if (!entries.length) {
        return <p className={`text-sm text-zinc-400`}>No saved notes yet.</p>;
    }

    return (
        <div className="space-y-2">
            {entries.map((e) => (
                <div
                    key={e.id}
                    onClick={() => { if (onPreview) onPreview(e.id); else if (onEdit) onEdit(e.id); }}
                    ref={(el) => { if (el && highlightId === e.id) { try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {} } }}
                    title="Click to preview"
                    className={`${dark
                        ? 'rounded-md border border-zinc-800 bg-zinc-900/60 text-zinc-100 hover:bg-blue-500/10 hover:border-blue-500/40 hover:ring-1 hover:ring-blue-500/30 hover:shadow'
                        : 'rounded-md border border-zinc-200 bg-white hover:bg-blue-50 hover:border-blue-200 hover:ring-1 hover:ring-blue-200 hover:shadow'} px-3 py-2 relative cursor-pointer transition-all duration-150 ${highlightId === e.id ? (dark ? 'ring-2 ring-blue-500/60' : 'ring-2 ring-blue-500/70') : ''}`}
                >
                    <div className="flex items-center gap-3">
                        {(() => {
                            const s = String(e.symbol ?? '').trim();
                            const base = 'inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold border ';
                            let label = (s || '').toUpperCase();
                            let styleClasses = dark ? 'text-zinc-100 border-zinc-700 bg-zinc-800' : 'text-zinc-900 border-zinc-200 bg-zinc-100';

                            // Attempt to resolve asset class from a linked trade (tradeKey) or by matching symbol
                            let resolvedTrade: Trade | undefined;
                            try {
                                if (allTrades && e.tradeKey) {
                                    resolvedTrade = allTrades.find((t) => getJournalTradeKey(t as unknown as Record<string, unknown>) === e.tradeKey);
                                }
                                if (!resolvedTrade && allTrades && s) {
                                    resolvedTrade = allTrades.find((t) => String(t.symbol ?? '').toUpperCase() === s.toUpperCase());
                                }
                                if (resolvedTrade) {
                                    const ac = getCanonicalAssetClass(resolvedTrade);
                                    if (ac === 'FOREX') { label = 'FX'; styleClasses = 'text-white bg-sky-600 border-sky-600'; }
                                    else if (ac === 'CRYPTO') { label = 'C'; styleClasses = 'text-zinc-900 bg-amber-300 border-amber-400'; }
                                    else if (ac === 'FUTURES') { label = 'FU'; styleClasses = 'text-white bg-fuchsia-700 border-fuchsia-800'; }
                                    else if (ac === 'STOCKS') { label = 'S'; styleClasses = 'text-white bg-emerald-600 border-emerald-700'; }
                                } else {
                                    if (!label) label = 'N/A';
                                    if (label.length > 3) label = label.slice(0, 3);
                                }
                            } catch {}

                            return <span className={`${base}${styleClasses}`}>{label}</span>;
                        })()}
                        <div className="min-w-0 flex-1">
                            <div className={`text-sm font-semibold truncate ${dark ? 'text-zinc-100' : 'text-zinc-900'}`}>{e.name || 'Untitled note'}</div>
                            <div className={`text-[11px] leading-tight ${dark ? 'text-zinc-400' : 'text-zinc-500'}`}>{new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'medium', timeZone: displayTimeZone }).format(new Date(e.ts))}</div>
                        </div>
                        <div className="ml-auto relative">
                            <button
                                type="button"
                                aria-haspopup="true"
                                aria-expanded={openMenuId === e.id}
                                onClick={(ev) => { ev.stopPropagation(); setOpenMenuId(openMenuId === e.id ? null : e.id); }}
                                className={`w-7 h-7 rounded-full grid place-items-center hover:bg-zinc-100/10 ${openMenuId === e.id ? 'text-blue-600' : ''}`}
                                title="Actions"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                            {openMenuId === e.id ? (
                                <div className={`absolute top-full right-0 mt-0 w-auto origin-top-right z-50 ${dark ? 'bg-zinc-800 border border-zinc-700 text-zinc-100' : 'bg-white border border-zinc-200 text-zinc-900'} rounded-md shadow-lg p-0.5`} onClick={(ev) => ev.stopPropagation()}>
                                    <div className="flex flex-col">
                                        <button className="px-3 py-1 text-right text-sm font-sans hover:bg-zinc-100/5 rounded flex items-center justify-between gap-2" onClick={() => { setOpenMenuId(null); if (onPreview) onPreview(e.id); }}>
                                            <span>Preview</span>
                                            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                        </button>
                                        {!readOnly ? (
                                            <>
                                                <button className="px-3 py-1 text-right text-sm font-sans hover:bg-zinc-100/5 rounded flex items-center justify-between gap-2" onClick={() => { setOpenMenuId(null); if (onEdit) onEdit(e.id); }}>
                                                    <span>Edit</span>
                                                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                                                </button>
                                                <div className={`${dark ? 'border-t border-zinc-700 my-0.5' : 'border-t border-zinc-200 my-0.5'}`} />
                                                <button className="px-3 py-1 text-right text-sm font-sans text-red-600 hover:bg-zinc-100/5 rounded flex items-center justify-between gap-2" onClick={() => { setOpenMenuId(null); if (onDelete) onDelete(e.id); }}>
                                                    <span>Delete</span>
                                                    <svg className="w-4 h-4 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                                                </button>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function TradeTagsModal({
    isOpen,
    onClose,
    tags,
    onAdd,
    onRemove,
    onClear,
}: {
    isOpen: boolean;
    onClose: () => void;
    tags: string[];
    onAdd: (tag: string) => void;
    onRemove: (tag: string) => void;
    onClear: () => void;
}) {
    const [inputValue, setInputValue] = useState('');

    if (!isOpen) return null;

    const handleAdd = (event?: React.FormEvent) => {
        event?.preventDefault();
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        onAdd(trimmed);
        setInputValue('');
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-[#1a1a1c]">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Manage Tags</h3>
                </div>

                <form onSubmit={handleAdd} className="mb-4 flex gap-2">
                    <input
                        className="flex-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        aria-label="Add tag"
                        placeholder="Add a tag..."
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        autoFocus
                    />
                    <button type="submit" aria-label="Add tag" className="rounded-lg bg-blue-600 px-3 py-2 text-white transition-colors hover:bg-blue-700">
                        <Plus className="h-5 w-5" />
                    </button>
                </form>

                <div className="mb-4">
                    <div className="mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">Active Tags</div>
                    <div className="flex min-h-[40px] flex-wrap gap-2">
                        {tags.length === 0 ? (
                            <span className="text-sm italic text-zinc-400">No tags selected</span>
                        ) : (
                            tags.map((tag) => (
                                <RemovableTag key={tag} label={tag} onRemove={() => onRemove(tag)} />
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className="mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">Suggestions</div>
                    <div className="flex flex-wrap gap-2">
                        {JOURNAL_TRADE_TAG_SUGGESTIONS.map((tag) => {
                            const isLongTerm = tag === 'Long Term';
                            return (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => onAdd(tag)}
                                    disabled={tags.includes(tag)}
                                    className={`rounded-md px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${isLongTerm ? 'bg-orange-100 text-orange-900 hover:bg-orange-200 dark:bg-yellow-600/20 dark:text-yellow-300' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'}`}
                                >
                                    + {tag}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 w-28 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 transition-all hover:bg-zinc-800 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onClear();
                            setInputValue('');
                        }}
                        disabled={tags.length === 0}
                        className={`flex h-9 w-28 items-center justify-center rounded-md px-4 py-2 text-sm transition-colors ${tags.length > 0 ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-700 dark:text-white' : 'cursor-not-allowed border border-zinc-700 bg-zinc-900 text-zinc-500 opacity-50'}`}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={() => { handleAdd(); onClose(); }}
                        className="flex h-9 w-28 items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-500"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export function JournalPage({ theme }: { theme?: 'light' | 'dark' }) {
    // theme state removed; UI will follow global app theme
    const isDark = theme === 'dark';
    const importedTrades = useImportedTradesRecords();
    const demoTradesOverride = useDemoTradesOverride();
    const defaultRiskPerTrade = readTradingSettings().riskRules.defaultRiskPerTrade;

    const [baseTrades, setBaseTrades] = useState<Trade[]>([]);
    const [manualTradeRefresh, setManualTradeRefresh] = useState(0);
    useEffect(() => {
        let cancelled = false;

        const refreshBaseTrades = async () => {
            try {
                const loadedTrades = await loadTrades();
                if (cancelled) return;
                const normalizedTrades = (Array.isArray(loadedTrades) ? loadedTrades : []).map((trade, index) => normalizeJournalTradeRecord(trade as JournalTradeRecord, index));
                setBaseTrades(dedupeJournalTrades(normalizedTrades));
            } catch {
                if (!cancelled) setBaseTrades([]);
            }
        };

        void refreshBaseTrades();

        return () => {
            cancelled = true;
        };
    }, [importedTrades, manualTradeRefresh]);

    const [customTrades, setCustomTrades] = useState<Trade[]>(() => {
        return (loadCustomTrades() as JournalTradeRecord[]).map((trade, index) => normalizeJournalTradeRecord(trade, index));
    });
    useEffect(() => {
        persistCustomTrades(customTrades);
    }, [customTrades]);
    useEffect(() => {
        const reloadForIdentity = () => {
            setCustomTrades((loadCustomTrades() as JournalTradeRecord[]).map((trade, index) => normalizeJournalTradeRecord(trade, index)));
            setDemoTradeTagOverrides(loadJournalDemoTradeTagOverrides());
        };
        window.addEventListener(privateTradingScopeChangedEventName(), reloadForIdentity);
        return () => window.removeEventListener(privateTradingScopeChangedEventName(), reloadForIdentity);
    }, []);
    const demoTrades = useMemo<Trade[]>(() => getJournalTradesFromDemoOverride(demoTradesOverride), [demoTradesOverride]);
    const [demoTradeTagOverrides, setDemoTradeTagOverrides] = useState<JournalDemoTradeTagOverrides>(() => loadJournalDemoTradeTagOverrides());
    useEffect(() => {
        saveJournalDemoTradeTagOverrides(demoTradeTagOverrides);
    }, [demoTradeTagOverrides]);
    const demoTradesWithTagOverrides = useMemo(() => {
        if (demoTrades.length === 0) return demoTrades;
        return demoTrades.map((trade) => {
            const key = getJournalTradeKey(trade as unknown as Record<string, unknown>);
            if (!Object.prototype.hasOwnProperty.call(demoTradeTagOverrides, key)) return trade;
            return { ...trade, tags: demoTradeTagOverrides[key] };
        });
    }, [demoTradeTagOverrides, demoTrades]);
    const demoTradesActive = demoTrades.length > 0;
    const demoDateRange = useMemo(() => {
        if (!demoTradesActive) return null;
        const dates = demoTrades
            .map((trade) => String(trade.date ?? '').trim())
            .filter(Boolean)
            .sort();
        const first = dates[0];
        const last = dates[dates.length - 1];
        if (!first || !last) return null;
        const start = new Date(first + 'T00:00:00');
        const end = new Date(last + 'T23:59:59.999');
        return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) ? { start, end } : null;
    }, [demoTrades, demoTradesActive]);
    // Combined list used everywhere
    const allTrades = useMemo(() => {
        if (demoTradesActive) return demoTradesWithTagOverrides;
        return dedupeJournalTrades([...baseTrades, ...customTrades]);
    }, [baseTrades, customTrades, demoTradesActive, demoTradesWithTagOverrides]);

    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
        if (typeof window !== 'undefined' && (window as any).__TT_onboardingJournalYtd) {
            delete (window as any).__TT_onboardingJournalYtd;
            return getYtdDateRange();
        }
        return getDefaultDateRange();
    });
    const handleDateChange = useCallback((start: Date, end: Date) => {
        setDateRange({ start, end });
    }, []);
    useEffect(() => {
        const setOnboardingYtd = () => {
            setDateRange(getYtdDateRange());
        };
        window.addEventListener('tradeproof:onboarding-journal-ytd', setOnboardingYtd);
        return () => window.removeEventListener('tradeproof:onboarding-journal-ytd', setOnboardingYtd);
    }, []);

    useEffect(() => {
        if (!demoDateRange) return;
        const currentStart = dateRange.start.getTime();
        const currentEnd = dateRange.end.getTime();
        if (currentStart <= demoDateRange.start.getTime() && currentEnd >= demoDateRange.end.getTime()) return;
        setDateRange(demoDateRange);
    }, [dateRange, demoDateRange]);

    const [searchQuery, setSearchQuery] = useState('');
    const displayTimeZone = useDisplayTimezone();
    const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMNS);
    const [journalColumnOrder, setJournalColumnOrder] = useState<string[]>(() => {
        try {
            const raw = safeGetItem(JOURNAL_TABLE_COLUMN_ORDER_KEY);
            return normalizeJournalColumnOrder(raw ? JSON.parse(raw) : null);
        } catch {
            return normalizeJournalColumnOrder(null);
        }
    });
    const [journalColumnWidths, setJournalColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const raw = safeGetItem(JOURNAL_TABLE_COLUMN_WIDTHS_KEY);
            return normalizeJournalColumnWidths(raw ? JSON.parse(raw) : null);
        } catch {
            return {};
        }
    });
    const journalHeaderRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
    const journalTableScrollerRef = useRef<HTMLDivElement | null>(null);
    const journalResizingRef = useRef<{ id: string; startX: number; startWidth: number } | null>(null);
    const journalDragColumnIdRef = useRef<string | null>(null);
    const [journalDraggingColumnId, setJournalDraggingColumnId] = useState<string | null>(null);
    const [journalDropColumnId, setJournalDropColumnId] = useState<string | null>(null);
    const [journalDropIndicator, setJournalDropIndicator] = useState<JournalDropIndicatorState | null>(null);
    const [lastSavedNoteId, setLastSavedNoteId] = useState<string | null>(null);
    // Filters state for Custom Filters dropdown (persisted)
    const emptyFilters = (): Filters => ({
        assetClasses: [],
        daysOfWeek: [],
        months: [],
        timeMin: undefined,
        timeMax: undefined,
        volumeMin: undefined,
        volumeMax: undefined,
        pnlMin: undefined,
        pnlMax: undefined,
    });
    const FILTERS_KEY = 'journal.filters.v3';
    const [assetVisibility] = useAssetClassVisibility();
    const [filters, setFilters] = useState<Filters>(() => {
        if (typeof window === 'undefined') return emptyFilters();
        try {
            // Try new schema first
            const raw = safeGetItem(FILTERS_KEY);
            if (raw) return { ...emptyFilters(), ...(JSON.parse(raw) as Filters) };
            // Migrate from v2 or v1 if present
            const v2 = safeGetItem('journal.filters.v2');
            if (v2) {
                // carry no win/lose flags; migrate to empty baseline
                return { ...emptyFilters() };
            }
            const v1 = safeGetItem('journal.filters.v1');
            if (v1) {
                // carry no win/lose flags; migrate to empty baseline
                return { ...emptyFilters() };
            }
            return emptyFilters();
        } catch {
            return emptyFilters();
        }
    });
    useEffect(() => {
        safeSetItem(FILTERS_KEY, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        setFilters((prev) => {
            const nextAssetClasses = (prev.assetClasses || []).filter((assetClass) => Boolean(assetVisibility[assetClass]));
            if (nextAssetClasses.length === (prev.assetClasses || []).length) return prev;
            return {
                ...prev,
                assetClasses: nextAssetClasses,
            };
        });
    }, [assetVisibility]);
    useEffect(() => {
        safeSetItem(JOURNAL_TABLE_COLUMN_ORDER_KEY, JSON.stringify(journalColumnOrder));
    }, [journalColumnOrder]);

    useEffect(() => {
        safeSetItem(JOURNAL_TABLE_COLUMN_WIDTHS_KEY, JSON.stringify(journalColumnWidths));
    }, [journalColumnWidths]);

    const startJournalColumnResize = useCallback((event: React.MouseEvent, id: string) => {
        event.preventDefault();
        event.stopPropagation();
        const headerEl = journalHeaderRefs.current[id];
        const startWidth = headerEl
            ? Math.round(headerEl.getBoundingClientRect().width)
            : (journalColumnWidths[id] ?? 140);

        journalResizingRef.current = {
            id,
            startX: event.clientX,
            startWidth,
        };

        const handleMove = (moveEvent: MouseEvent) => {
            const current = journalResizingRef.current;
            if (!current) return;
            const nextWidth = Math.max(JOURNAL_TABLE_MIN_COLUMN_WIDTH, Math.round(current.startWidth + moveEvent.clientX - current.startX));
            setJournalColumnWidths((prev) => ({ ...prev, [current.id]: nextWidth }));
        };

        const handleUp = () => {
            journalResizingRef.current = null;
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    }, [journalColumnWidths]);

    const reorderJournalColumn = useCallback((activeId: string, overId: string) => {
        if (!activeId || !overId || activeId === overId) return;
        setJournalColumnOrder((prev) => {
            const visibleOrder = prev.filter((id) => visibleColumns.includes(id));
            const oldIndex = visibleOrder.indexOf(activeId);
            const newIndex = visibleOrder.indexOf(overId);
            if (oldIndex < 0 || newIndex < 0) return prev;

            const nextVisible = visibleOrder.slice();
            const [moved] = nextVisible.splice(oldIndex, 1);
            nextVisible.splice(newIndex, 0, moved);

            const visibleSet = new Set(visibleOrder);
            let nextIndex = 0;
            return prev.map((id) => {
                if (!visibleSet.has(id)) return id;
                return nextVisible[nextIndex++] ?? id;
            });
        });
    }, [visibleColumns]);

    const reorderJournalColumnAtIndex = useCallback((activeId: string, targetIndex: number) => {
        if (!activeId) return;
        setJournalColumnOrder((prev) => {
            const visibleOrder = prev.filter((id) => visibleColumns.includes(id));
            const fromIndex = visibleOrder.indexOf(activeId);
            if (fromIndex < 0) return prev;

            const withoutActive = visibleOrder.filter((id) => id !== activeId);
            const boundedIndex = Math.max(0, Math.min(targetIndex, withoutActive.length));
            const nextVisible = withoutActive.slice();
            nextVisible.splice(boundedIndex, 0, activeId);

            const visibleSet = new Set(visibleOrder);
            let nextIndex = 0;
            return prev.map((id) => {
                if (!visibleSet.has(id)) return id;
                return nextVisible[nextIndex++] ?? id;
            });
        });
    }, [visibleColumns]);

    const computeJournalDropIndicator = useCallback((overId: string, clientX: number): JournalDropIndicatorState | null => {
        const scroller = journalTableScrollerRef.current;
        if (!scroller) return null;

        const orderedIds = journalColumnOrder.filter((id) => visibleColumns.includes(id));
        const overIndex = orderedIds.indexOf(overId);
        if (overIndex < 0) return null;

        const overEl = journalHeaderRefs.current[overId];
        if (!overEl) return null;

        const overRect = overEl.getBoundingClientRect();
        const insertAfter = clientX > overRect.left + overRect.width / 2;
        const insertionIndex = insertAfter ? overIndex + 1 : overIndex;

        const scrollerRect = scroller.getBoundingClientRect();
        let left = 0;
        if (insertionIndex >= orderedIds.length) {
            const lastId = orderedIds[orderedIds.length - 1];
            const lastEl = lastId ? journalHeaderRefs.current[lastId] : null;
            if (!lastEl) return null;
            left = lastEl.getBoundingClientRect().right - scrollerRect.left + scroller.scrollLeft;
        } else {
            const targetId = orderedIds[insertionIndex];
            const targetEl = targetId ? journalHeaderRefs.current[targetId] : null;
            if (!targetEl) return null;
            left = targetEl.getBoundingClientRect().left - scrollerRect.left + scroller.scrollLeft;
        }

        return { index: insertionIndex, left: Math.round(left) };
    }, [journalColumnOrder, visibleColumns]);

    const handleJournalColumnDragStart = useCallback((event: React.DragEvent, id: string) => {
        journalDragColumnIdRef.current = id;
        setJournalDraggingColumnId(id);
        setJournalDropColumnId(null);
        setJournalDropIndicator(null);
        try {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', id);
        } catch {}
    }, []);

    const handleJournalColumnDragOver = useCallback((event: React.DragEvent, id: string) => {
        const activeId = journalDragColumnIdRef.current;
        if (!activeId || activeId === id) return;
        event.preventDefault();
        setJournalDropColumnId(id);
        setJournalDropIndicator(computeJournalDropIndicator(id, event.clientX));
    }, [computeJournalDropIndicator]);

    const handleJournalColumnDrop = useCallback((event: React.DragEvent, id: string) => {
        event.preventDefault();
        const activeId = journalDragColumnIdRef.current || event.dataTransfer.getData('text/plain');
        const indicator = computeJournalDropIndicator(id, event.clientX) ?? journalDropIndicator;
        if (indicator) reorderJournalColumnAtIndex(activeId, indicator.index);
        else reorderJournalColumn(activeId, id);
        journalDragColumnIdRef.current = null;
        setJournalDraggingColumnId(null);
        setJournalDropColumnId(null);
        setJournalDropIndicator(null);
    }, [computeJournalDropIndicator, journalDropIndicator, reorderJournalColumn, reorderJournalColumnAtIndex]);

    const handleJournalColumnDragEnd = useCallback(() => {
        journalDragColumnIdRef.current = null;
        setJournalDraggingColumnId(null);
        setJournalDropColumnId(null);
        setJournalDropIndicator(null);
    }, []);

    const filteredTrades = useMemo(() => {
        const start = new Date(dateRange.start);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);

        return allTrades.filter((trade) => {
            const tradeDate = new Date(trade.date + 'T00:00:00');
            const inDateRange = tradeDate >= start && tradeDate <= end;
            if (!inDateRange) return false;
            if (filters.assetClasses && filters.assetClasses.length > 0) {
                const assetClass = getCanonicalAssetClass(trade);
                if (!filters.assetClasses.includes(assetClass as 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO')) return false;
            }
            // Optional Trade Result segmented filter
            if (filters.dayResult === 'Winning' && !(trade.pnl > 0)) return false;
            if (filters.dayResult === 'Losing' && !(trade.pnl < 0)) return false;
            // Day of week
            if (filters.daysOfWeek && filters.daysOfWeek.length > 0) {
                const dow = tradeDate.getDay();
                if (!filters.daysOfWeek.includes(dow)) return false;
            }
            // Month filter
            if (filters.months && filters.months.length > 0) {
                const m = tradeDate.getMonth();
                if (!filters.months.includes(m)) return false;
            }
            // Time of day filter (if we have timestamp/time)
            if (filters.timeMin || filters.timeMax) {
                const tms = getTradeTimeMs(trade);
                if (tms != null) {
                    const d = new Date(tms);
                    const toHM = (hh: number, mm: number) => hh * 60 + mm;
                    const mins = toHM(d.getHours(), d.getMinutes());
                    if (filters.timeMin) {
                        const [h, m] = filters.timeMin.split(':').map(Number);
                        if (mins < toHM(h || 0, m || 0)) return false;
                    }
                    if (filters.timeMax) {
                        const [h, m] = filters.timeMax.split(':').map(Number);
                        if (mins > toHM(h || 0, m || 0)) return false;
                    }
                }
            }
            // Volume
            if (filters.volumeMin != null && !(trade.volume >= filters.volumeMin)) return false;
            if (filters.volumeMax != null && !(trade.volume <= filters.volumeMax)) return false;
            // P&L ($)
            if (filters.pnlMin != null && !(trade.pnl >= filters.pnlMin)) return false;
            if (filters.pnlMax != null && !(trade.pnl <= filters.pnlMax)) return false;

            if (searchQuery.trim() === '') return true;
            return trade.symbol.toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [allTrades, dateRange, searchQuery, filters]);

    const tradesByDay = useMemo(() => {
        return buildJournalTradesByDay(filteredTrades);
    }, [filteredTrades]);

    const days = useMemo(() => Object.keys(tradesByDay).sort((a, b) => (a < b ? 1 : -1)), [tradesByDay]);

    // Draft notes per day are kept in-memory only (no auto-persist)
    const [notesByDate, setNotesByDate] = useState<Record<string, string>>({});
    const handleNoteChange = useCallback((date: string, value: string) => {
        setNotesByDate((prev) => {
            const prevVal = prev[date] || '';
            if (prevVal === value) return prev; // avoid creating new object if unchanged
            return { ...prev, [date]: value };
        });
    }, []);

    // Notes history (append-only on explicit Save)
    // NOTE: `symbol` is for display, `tradeKey` links the note to a specific trade.
    type NoteEntry = JournalNoteEntry;
    const NOTES_HISTORY_KEY = 'journal.notes.history.v1';
    const TAGS_KEY = 'journal.tags.byDate.v1';
    const loadNotesHistory = useCallback((): Record<string, NoteEntry[]> => {
        try { const raw = safeGetItem(NOTES_HISTORY_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
    }, []);
    const saveNotesHistory = useCallback((hist: Record<string, NoteEntry[]>): boolean => {
        try { safeSetItem(NOTES_HISTORY_KEY, JSON.stringify(hist)); return true; } catch { return false; }
    }, []);
    const loadTagsByDate = useCallback((): Record<string, string[]> => {
        try { const raw = safeGetItem(TAGS_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
    }, []);
    const saveTagsByDate = useCallback((tags: Record<string, string[]>): boolean => {
        try { safeSetItem(TAGS_KEY, JSON.stringify(tags)); return true; } catch { return false; }
    }, []);
    const [notesHistoryByDate, setNotesHistoryByDate] = useState<Record<string, NoteEntry[]>>({});
    useEffect(() => setNotesHistoryByDate(loadNotesHistory()), [loadNotesHistory]);
    useEffect(() => { saveNotesHistory(notesHistoryByDate); }, [notesHistoryByDate, saveNotesHistory]);
    const demoNotesHistoryByDate = useMemo<Record<string, NoteEntry[]>>(() => (
        demoTradesActive ? buildJournalDemoNotesByDate(demoTrades) : {}
    ), [demoTrades, demoTradesActive]);
    // Track when the editor is editing an existing saved note
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingNoteName, setEditingNoteName] = useState<string>('');
    const [editingNoteSymbol, setEditingNoteSymbol] = useState<string>('');
    const [editingNoteTradeKey, setEditingNoteTradeKey] = useState<string>('');
    const tradeNoteInputRef = useRef<HTMLTextAreaElement | null>(null);
    const [editingTradeNoteKey, setEditingTradeNoteKey] = useState<string | null>(null);
    const [editingTradeNoteValue, setEditingTradeNoteValue] = useState('');
    const [isTradeTagsModalOpen, setIsTradeTagsModalOpen] = useState(false);
    const [tradeTagModalTargetKey, setTradeTagModalTargetKey] = useState<string | null>(null);
    const [noteSymbolByDate, setNoteSymbolByDate] = useState<Record<string, string>>({});
    const [noteTradeKeyByDate, setNoteTradeKeyByDate] = useState<Record<string, string>>({});
    const [activeTradeId, setActiveTradeId] = useState<number | null>(null);
    // Inline tags per day
    const [tagsByDate, setTagsByDate] = useState<Record<string, string[]>>({});
    useEffect(() => setTagsByDate(loadTagsByDate()), [loadTagsByDate]);
    useEffect(() => { saveTagsByDate(tagsByDate); }, [tagsByDate, saveTagsByDate]);

    useEffect(() => {
        if (!editingTradeNoteKey || !tradeNoteInputRef.current) return;
        tradeNoteInputRef.current.focus();
        tradeNoteInputRef.current.select();
    }, [editingTradeNoteKey]);

    const updateTradeRecord = useCallback((targetTrade: Trade, updater: (trade: Trade) => Trade) => {
        if (isJournalDemoTrade(targetTrade as unknown as Record<string, unknown>)) {
            return;
        }

        const targetKey = getJournalTradeKey(targetTrade as unknown as Record<string, unknown>);
        if ((targetTrade as Record<string, unknown>).isCustom === true && !(targetTrade as Record<string, unknown>).persistedTradeId) {
            setCustomTrades((prev) => prev.map((trade) => (
                getJournalTradeKey(trade as unknown as Record<string, unknown>) === targetKey ? updater(trade) : trade
            )));
            return;
        }

        const updatedTrade = normalizeJournalTradeRecord(updater(targetTrade), 0);

        setBaseTrades((prev) => dedupeJournalTrades(prev.map((trade) => (
            getJournalTradeKey(trade as unknown as Record<string, unknown>) === targetKey ? updatedTrade : trade
        ))));

        const existingImported = importedTrades.some((trade) => getJournalTradeKey(trade as unknown as Record<string, unknown>) === targetKey);
        const nextImported = existingImported
            ? importedTrades.map((trade) => (
                getJournalTradeKey(trade as unknown as Record<string, unknown>) === targetKey
                    ? updatedTrade
                    : trade
            ))
            : [...importedTrades, updatedTrade as unknown as typeof importedTrades[number]];

        saveImportedTrades(nextImported as unknown as Record<string, unknown>[]);
    }, [importedTrades]);

    const saveTradeNote = useCallback((targetTrade: Trade, noteValue: string) => {
        updateTradeRecord(targetTrade, (trade) => ({ ...trade, notes: noteValue }));
    }, [updateTradeRecord]);

    const applyTagToTrade = useCallback((targetTrade: Trade, tagValue: string) => {
        const trimmed = tagValue.trim();
        if (!trimmed) return;
        if (isJournalDemoTrade(targetTrade as unknown as Record<string, unknown>)) {
            const targetKey = getJournalTradeKey(targetTrade as unknown as Record<string, unknown>);
            setDemoTradeTagOverrides((prev) => {
                const currentTags = Object.prototype.hasOwnProperty.call(prev, targetKey)
                    ? (Array.isArray(prev[targetKey]) ? prev[targetKey] : [])
                    : (Array.isArray((targetTrade as Record<string, unknown>).tags)
                        ? ((targetTrade as Record<string, unknown>).tags as unknown[]).map((tag) => String(tag))
                        : []);
                if (currentTags.includes(trimmed)) return prev;
                return {
                    ...prev,
                    [targetKey]: Array.from(new Set([...currentTags, trimmed])),
                };
            });
            return;
        }
        updateTradeRecord(targetTrade, (trade) => {
            const currentTags = Array.isArray((trade as Record<string, unknown>).tags)
                ? ((trade as Record<string, unknown>).tags as unknown[]).map((tag) => String(tag))
                : [];
            if (currentTags.includes(trimmed)) return trade;
            return { ...trade, tags: [...currentTags, trimmed] };
        });
    }, [updateTradeRecord]);

    const removeTradeTag = useCallback((targetTrade: Trade, tagValue: string) => {
        const trimmed = tagValue.trim();
        if (!trimmed) return;
        if (isJournalDemoTrade(targetTrade as unknown as Record<string, unknown>)) {
            const targetKey = getJournalTradeKey(targetTrade as unknown as Record<string, unknown>);
            setDemoTradeTagOverrides((prev) => {
                const currentTags = Object.prototype.hasOwnProperty.call(prev, targetKey)
                    ? (Array.isArray(prev[targetKey]) ? prev[targetKey] : [])
                    : (Array.isArray((targetTrade as Record<string, unknown>).tags)
                        ? ((targetTrade as Record<string, unknown>).tags as unknown[]).map((tag) => String(tag))
                        : []);
                return {
                    ...prev,
                    [targetKey]: currentTags.filter((tag) => tag !== trimmed),
                };
            });
            return;
        }
        updateTradeRecord(targetTrade, (trade) => {
            const currentTags = Array.isArray((trade as Record<string, unknown>).tags)
                ? ((trade as Record<string, unknown>).tags as unknown[]).map((tag) => String(tag))
                : [];
            return { ...trade, tags: currentTags.filter((tag) => tag !== trimmed) };
        });
    }, [updateTradeRecord]);

    const clearTradeTags = useCallback((targetTrade: Trade) => {
        if (isJournalDemoTrade(targetTrade as unknown as Record<string, unknown>)) {
            const targetKey = getJournalTradeKey(targetTrade as unknown as Record<string, unknown>);
            setDemoTradeTagOverrides((prev) => ({
                ...prev,
                [targetKey]: [],
            }));
            return;
        }
        updateTradeRecord(targetTrade, (trade) => ({ ...trade, tags: [] }));
    }, [updateTradeRecord]);

    // Error message for Save actions when trade not selected
    const [noteSaveError, setNoteSaveError] = useState<string>('');

    // Symbols helper for sidebar controls
    const setNoteSymbol = useCallback((date: string, symbol: string) => {
        setNoteSymbolByDate(prev => ({ ...prev, [date]: symbol }));
    }, []);
    const setNoteTradeKey = useCallback((date: string, tradeKey: string) => {
        setNoteTradeKeyByDate(prev => ({ ...prev, [date]: tradeKey }));
    }, []);

    const htmlToPlainNote = useCallback((html: string): string => {
        try {
            const div = document.createElement('div');
            div.innerHTML = html;
            return (div.textContent || '').trimEnd();
        } catch {
            return String(html ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trimEnd();
        }
    }, []);

    // Minimal HTML cleaner (placeholder for future sanitization)
    const cleanEditorHtml = useCallback((html: string) => {
        try {
            // Normalize nbsp and trim
            const normalized = html.replace(/\u00a0/g, ' ').trim();
            return normalized;
        } catch { return html; }
    }, []);

    // Save a new note to history
    const handleSaveCurrentNote = useCallback((date: string, html: string, title: string): boolean => {
        const cleaned = cleanEditorHtml(html).trim();
        if (!cleaned) return false;
        const tradeKey = (noteTradeKeyByDate[date] || '').trim();
        const selectedTrade = tradeKey
            ? (allTrades || []).find((t) => getJournalTradeKey(t as unknown as Record<string, unknown>) === tradeKey)
            : null;
        if (isJournalDemoTrade(selectedTrade as unknown as Record<string, unknown>)) return false;
        const resolvedTitle = selectedTrade
            ? (((noteSymbolByDate[date] || '').trim()) || title.trim() || 'Untitled note')
            : (title.trim() || 'General Daily Note');
        const id = String(Date.now());
        const entry: NoteEntry = {
            id,
            text: cleaned,
            ts: Date.now(),
            name: resolvedTitle,
            symbol: (noteSymbolByDate[date] || undefined),
            tradeKey: tradeKey || undefined,
        };
        setNotesHistoryByDate(prev => ({ ...prev, [date]: [ ...(prev[date] || []), entry ] }));
        setLastSavedNoteId(id);
        // Persist the note onto the selected trade so it appears in Trade Details.
        if (selectedTrade) {
            saveTradeNote(selectedTrade as unknown as Trade, htmlToPlainNote(cleaned));
        }
        // Clear selected trade after save
        setNoteSymbol(date, '');
        setNoteTradeKey(date, '');
        return true;
    }, [allTrades, cleanEditorHtml, htmlToPlainNote, noteSymbolByDate, noteTradeKeyByDate, saveTradeNote, setNoteSymbol, setNoteTradeKey]);

    // Update an existing saved note
    const handleUpdateSavedNote = useCallback((date: string, id: string, html: string, title: string, symbol?: string, tradeKey?: string): boolean => {
        const cleaned = cleanEditorHtml(html).trim();
        if (!cleaned) return false;
        if (tradeKey) {
            const trade = (allTrades || []).find((t) => getJournalTradeKey(t as unknown as Record<string, unknown>) === tradeKey);
            if (isJournalDemoTrade(trade as unknown as Record<string, unknown>)) return false;
        }
        const resolvedTitle = (symbol || title || 'Untitled note').trim();
        let found = false;
        setNotesHistoryByDate(prev => {
            const list = prev[date] || [];
            const next = list.map(n => {
                if (n.id === id) { found = true; return { ...n, text: cleaned, name: resolvedTitle, symbol, tradeKey }; }
                return n;
            });
            return { ...prev, [date]: next };
        });
        if (found) {
            setLastSavedNoteId(id);
            if (tradeKey) {
                const trade = (allTrades || []).find((t) => getJournalTradeKey(t as unknown as Record<string, unknown>) === tradeKey);
                if (trade) {
                    saveTradeNote(trade as unknown as Trade, htmlToPlainNote(cleaned));
                }
            }
            // Clear selected trade after update
            setNoteSymbol(date, '');
            setNoteTradeKey(date, '');
        }
        return found;
    }, [allTrades, cleanEditorHtml, htmlToPlainNote, saveTradeNote, setNoteSymbol, setNoteTradeKey]);

    // (Preview editor state is declared later near the Preview modal section)

    // Listen for external requests to open a saved note for editing/preview
    useEffect(() => {
        try {
            const handler = (evt: Event) => {
                try {
                    const detail = (evt as CustomEvent).detail || {};
                    const noteId = detail.id as string | undefined;
                    const dateKey = detail.date as string | undefined;
                    // If a dateKey was provided, switch the selected day first
                    if (dateKey) {
                        setSelectedDay(dateKey);
                    }
                    // Defer to allow Journal to mount/update state
                    window.setTimeout(() => {
                        try {
                            let foundNote: NoteEntry | null = null;
                            let foundDate: string | null = dateKey || null;
                            if (noteId && dateKey && notesHistoryByDate[dateKey]) {
                                foundNote = (notesHistoryByDate[dateKey] || []).find(n => n.id === noteId) || null;
                            }
                            if (!foundNote && noteId) {
                                // Search across all dates
                                for (const [d, list] of Object.entries(notesHistoryByDate || {})) {
                                    const f = (list || []).find(n => n.id === noteId);
                                    if (f) { foundNote = f; foundDate = d; break; }
                                }
                            }
                            if (!foundNote) return;
                            const dateToUse = foundDate as string;
                            // Ensure selected day matches found date
                            setSelectedDay(dateToUse);
                            // Populate editor and set editing state similar to onEdit handler
                            try { if (editorInstanceRef.current?.setHTML) { editorInstanceRef.current.setHTML(foundNote.text); } } catch {}
                            handleNoteChange(dateToUse, foundNote.text);
                            setEditingNoteId(foundNote.id);
                            setEditingNoteName(foundNote.name || '');
                            setEditingNoteSymbol(foundNote.symbol || '');
                            setEditingNoteTradeKey(foundNote.tradeKey || '');
                            const el = document.querySelector('[contenteditable="true"]') as HTMLElement | null;
                            if (el) {
                                el.focus();
                                const range = document.createRange();
                                range.selectNodeContents(el);
                                range.collapse(false);
                                const sel = window.getSelection();
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                            }
                        } catch (err) { console.error('[Journal] openNote handler error', err); }
                    }, 60);
                } catch (err) { console.error(err); }
            };
            window.addEventListener('tt:openNote', handler as EventListener);
            return () => window.removeEventListener('tt:openNote', handler as EventListener);
        } catch {}
    }, [notesHistoryByDate, handleNoteChange]);
    const [rightOpen, setRightOpen] = useState(false); // overlay drawer for < xl
    const [leftOpen, setLeftOpen] = useState(false); // overlay drawer for < lg    // Resizable right panel (desktop xl+)
    const [leftCollapsed, setLeftCollapsed] = useState(false);
    const [rightCollapsed, setRightCollapsed] = useState(false);
    const [rightWidth, setRightWidth] = useState<number>(() => 336);
    // Resizable left panel (desktop lg+)
    const [leftWidth, setLeftWidth] = useState<number>(() => 320);
    useEffect(() => {
        document.documentElement.style.setProperty('--journal-left-width', `${leftWidth}px`);
        document.documentElement.style.setProperty('--journal-right-width', `${rightWidth}px`);
        return () => {
            document.documentElement.style.removeProperty('--journal-left-width');
            document.documentElement.style.removeProperty('--journal-right-width');
        };
    }, [leftWidth, rightWidth]);
    const leftWidthBeforeCollapseRef = useRef<number | null>(null);
    const rightWidthBeforeCollapseRef = useRef<number | null>(null);
    const leftResizingRef = useRef(false);
    const leftStartXRef = useRef<number | null>(null);
    const leftStartWidthRef = useRef<number | null>(null);
    const leftAsideRef = useRef<HTMLElement | null>(null);
    const resizingRef = useRef(false);
    const startXRef = useRef<number | null>(null);
    const startWidthRef = useRef<number | null>(null);
    const rightAsideRef = useRef<HTMLElement | null>(null);
    // Transient flag to suppress external value sync in editor during programmatic UI transitions
    // Note: '+' add-note path removed; this remains for future uses but is static false for now
    const [suspendEditorSync] = useState(false);

    // Delete confirmation context
    const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string; date: string; symbol?: string } | null>(null);
    const toggleLeftSidebar = useCallback(() => {
        setLeftCollapsed((prev) => {
            const next = !prev;
            if (next) {
                if (leftWidth > 0) leftWidthBeforeCollapseRef.current = leftWidth;
                setLeftWidth(0);
            } else {
                setLeftWidth(Math.max(200, leftWidthBeforeCollapseRef.current ?? 320));
            }
            return next;
        });
    }, [leftWidth]);
    const toggleRightSidebar = useCallback(() => {
        setRightCollapsed((prev) => {
            const next = !prev;
            if (next) {
                if (rightWidth > 0) rightWidthBeforeCollapseRef.current = rightWidth;
                setRightWidth(0);
            } else {
                setRightWidth(Math.max(220, rightWidthBeforeCollapseRef.current ?? 336));
            }
            return next;
        });
    }, [rightWidth]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!resizingRef.current) return;
            if (startXRef.current == null || startWidthRef.current == null) return;
            const dx = startXRef.current - e.clientX; // dragging left increases right panel width
            const newW = Math.max(220, Math.min(600, startWidthRef.current + dx));
            setRightWidth(newW); // Ensure newW is used to update the state
        };
        const onMouseUp = () => {
            resizingRef.current = false;
            startXRef.current = null;
            startWidthRef.current = null;
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        if (resizingRef.current) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'col-resize';
        }
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
        };
    }, []);

    // Left panel mouse handlers (lg+)
    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!leftResizingRef.current) return;
            if (leftStartXRef.current == null || leftStartWidthRef.current == null) return;
            const dx = e.clientX - leftStartXRef.current; // dragging right increases left width
            const newW = Math.max(200, Math.min(420, leftStartWidthRef.current + dx));
            setLeftWidth(newW);
        };
        const onMouseUp = () => {
            leftResizingRef.current = false;
            leftStartXRef.current = null;
            leftStartWidthRef.current = null;
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        if (leftResizingRef.current) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'col-resize';
        }
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
        };
    }, []);

    // touch support
    useEffect(() => {
        const onTouchMove = (e: TouchEvent) => {
            if (!resizingRef.current) return;
            if (startXRef.current == null || startWidthRef.current == null) return;
            const touch = e.touches[0];
            const dx = startXRef.current - touch.clientX;
            const newW = Math.max(220, Math.min(600, startWidthRef.current + dx));
            setRightWidth(newW);
        };
        const onTouchEnd = () => {
            resizingRef.current = false;
            startXRef.current = null;
            startWidthRef.current = null;
            document.body.style.cursor = '';
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
        if (resizingRef.current) {
            document.addEventListener('touchmove', onTouchMove);
            document.addEventListener('touchend', onTouchEnd);
        }
        return () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.body.style.cursor = '';
        };
    }, []);

    // touch support for left resizer
    useEffect(() => {
        const onTouchMove = (e: TouchEvent) => {
            if (!leftResizingRef.current) return;
            if (leftStartXRef.current == null || leftStartWidthRef.current == null) return;
            const touch = e.touches[0];
            const dx = touch.clientX - leftStartXRef.current;
            const newW = Math.max(200, Math.min(420, leftStartWidthRef.current + dx));
            setLeftWidth(newW);
        };
        const onTouchEnd = () => {
            leftResizingRef.current = false;
            leftStartXRef.current = null;
            leftStartWidthRef.current = null;
            document.body.style.cursor = '';
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
        if (leftResizingRef.current) {
            document.addEventListener('touchmove', onTouchMove);
            document.addEventListener('touchend', onTouchEnd);
        }
        return () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.body.style.cursor = '';
        };
    }, []);

    // Measure right panel toolbar and set default width so toolbar wraps into two lines
    useLayoutEffect(() => {
        const el = rightAsideRef.current;
        if (!el) return;
        // toolbar in editor uses class "p-2 border-b" (RichTextToolbar)
        const toolbar = el.querySelector('.p-2.border-b') as HTMLElement | null;
        if (!toolbar) return;
        const scrollW = toolbar.scrollWidth || toolbar.offsetWidth || 320;
        // set width roughly half of full toolbar width so it wraps into ~2 lines
    const desired = Math.max(240, Math.min(520, Math.ceil(scrollW / 2) + 48));
    // only override default if measured desired is larger than our default to avoid shrinking below requested default
    if (desired > 336) setRightWidth(desired);
    }, []);

    // Optional: measure left panel content to set a reasonable default width on first render
    useLayoutEffect(() => {
        const el = leftAsideRef.current;
        if (!el) return;
        const scrollW = el.scrollWidth || el.offsetWidth || 220;
        const desired = Math.max(200, Math.min(360, Math.ceil(scrollW * 0.9)));
        if (desired > 320) setLeftWidth(desired);
    }, []);

    const [selectedDay, setSelectedDay] = useState<string | null>(() => {
        try {
            const stored = String(sessionStorage.getItem(JOURNAL_TRADE_DETAILS_RETURN_DAY_KEY) ?? '').trim();
            sessionStorage.removeItem(JOURNAL_TRADE_DETAILS_RETURN_DAY_KEY);
            return stored || null;
        } catch {
            return null;
        }
    });

    // Saved note preview modal
    const [previewNote, setPreviewNote] = useState<{ id: string; title: string; html: string; ts: number; symbol?: string; tradeKey?: string } | null>(null);
    const [isPreviewEditing, setIsPreviewEditing] = useState(false);
    const [previewEditHtml, setPreviewEditHtml] = useState<string>('');
    const previewNoteReadOnly = useMemo(() => {
        if (!previewNote) return false;
        if (!previewNote.tradeKey) return demoTradesActive;
        const trade = allTrades.find((candidate) => getJournalTradeKey(candidate as unknown as Record<string, unknown>) === previewNote.tradeKey);
        return isJournalDemoTrade(trade as unknown as Record<string, unknown>);
    }, [allTrades, demoTradesActive, previewNote]);
    // Use the same rich text editor as Column 3 for Preview editing to mirror exact wiring
    const previewEditorInstanceRef = useRef<{ getHTML: () => string; setHTML?: (html: string) => void } | null>(null);

    const validSelectedDay = useMemo(() => {
        if (selectedDay && days.includes(selectedDay)) {
            return selectedDay;
        }
        return days.length > 0 ? days[0] : null;
    }, [days, selectedDay]);

    useEffect(() => {
        if (validSelectedDay !== selectedDay) {
            setSelectedDay(validSelectedDay);
        }
    }, [validSelectedDay, selectedDay]);

    // keep a ref to the latest validSelectedDay so we can provide a stable onChange handler
    const validSelectedDayRef = useRef<string | null>(validSelectedDay);
    useEffect(() => { validSelectedDayRef.current = validSelectedDay; }, [validSelectedDay]);



    // ref to the RichTextEditor imperative instance
    const editorInstanceRef = useRef<{ getHTML: () => string; setHTML?: (html: string) => void } | null>(null);

    const selectedDayTrades = useMemo(() => {
        return validSelectedDay ? (tradesByDay[validSelectedDay] ?? []) : [];
    }, [validSelectedDay, tradesByDay]);
    const TRADES_DATA = useMemo(() => {
        return selectedDayTrades.map((trade) => ({
            id: Number(trade.id ?? Number.NaN),
            symbol: String(trade.symbol ?? ''),
            time: (() => {
                const ms = (trade.openTimestampMs ?? trade.timestamp) ?? null;
                if (ms == null) return '';
                return new Intl.DateTimeFormat([], { timeZone: displayTimeZone, hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(typeof ms === 'number' ? ms : Number(ms)));
            })(),
            pnl: getJournalTradeNetPnlValue(trade as JournalTradeRecord) ?? 0,
            tradeKey: getJournalTradeKey(trade as unknown as Record<string, unknown>),
            notes: String((trade as Record<string, unknown>).notes ?? ''),
        }));
    }, [selectedDayTrades]);
    useEffect(() => {
        if (!validSelectedDay) {
            setActiveTradeId(null);
            return;
        }

        const currentTradeKey = editingNoteId
            ? (editingNoteTradeKey || '').trim()
            : (noteTradeKeyByDate[validSelectedDay] || '').trim();

        if (!currentTradeKey) {
            setActiveTradeId(null);
            return;
        }

        const matchedTrade = selectedDayTrades.find((trade) => getJournalTradeKey(trade as unknown as Record<string, unknown>) === currentTradeKey);
        const nextId = matchedTrade ? Number(matchedTrade.id) : null;
        setActiveTradeId(Number.isFinite(nextId as number) ? nextId : null);
    }, [editingNoteId, editingNoteTradeKey, noteTradeKeyByDate, selectedDayTrades, validSelectedDay]);

    const handleRowClick = useCallback((tradeId: number | null) => {
        setActiveTradeId(tradeId);
        if (!validSelectedDay) return;

        const currentHtml = editorInstanceRef.current?.getHTML?.() ?? '';
        handleNoteChange(validSelectedDay, currentHtml);

        if (tradeId == null) {
            setNoteTradeKey(validSelectedDay, '');
            setNoteSymbol(validSelectedDay, '');
            setNoteSaveError('');
            return;
        }

        const selectedTrade = selectedDayTrades.find((trade) => Number(trade.id) === tradeId) ?? null;
        const tradeKey = selectedTrade ? getJournalTradeKey(selectedTrade as unknown as Record<string, unknown>) : '';
        const sym = String(selectedTrade?.symbol ?? '');

        if (editingNoteId) {
            setEditingNoteTradeKey(tradeKey);
            setEditingNoteSymbol(sym);
        } else {
            setNoteTradeKey(validSelectedDay, tradeKey);
            setNoteSymbol(validSelectedDay, sym);
        }

        const noteText = String((selectedTrade as Record<string, unknown> | undefined)?.notes ?? '').trim();
        if (noteText) {
            handleNoteChange(validSelectedDay, noteText);
            try { editorInstanceRef.current?.setHTML?.(noteText); } catch {}
        }
        setNoteSaveError('');
    }, [editingNoteId, handleNoteChange, selectedDayTrades, setNoteSymbol, setNoteTradeKey, validSelectedDay]);
    useEffect(() => {
        if (!demoTradesActive || !validSelectedDay || selectedDayTrades.length === 0) return;
        const currentKey = (noteTradeKeyByDate[validSelectedDay] || '').trim();
        const currentTrade = currentKey
            ? selectedDayTrades.find((trade) => getJournalTradeKey(trade as unknown as Record<string, unknown>) === currentKey)
            : null;
        const nextTrade = currentTrade ?? selectedDayTrades[0];
        if (!nextTrade) return;

        const nextKey = getJournalTradeKey(nextTrade as unknown as Record<string, unknown>);
        const nextSymbol = String(nextTrade.symbol ?? '');
        const nextNote = String((nextTrade as Record<string, unknown>).notes ?? '');

        if (currentKey !== nextKey) {
            setNoteTradeKey(validSelectedDay, nextKey);
            setNoteSymbol(validSelectedDay, nextSymbol);
        }
        if ((notesByDate[validSelectedDay] ?? '') !== nextNote) {
            handleNoteChange(validSelectedDay, nextNote);
            try { editorInstanceRef.current?.setHTML?.(nextNote); } catch {}
        }
        setNoteSaveError('');
    }, [demoTradesActive, handleNoteChange, noteTradeKeyByDate, notesByDate, selectedDayTrades, setNoteSymbol, setNoteTradeKey, validSelectedDay]);

    const openTradeDetails = useCallback((trade: Trade) => {
        try {
            writePrivateTradingJson('last-view-trade', JSON.stringify(trade), 'session');
        } catch {}

        try {
            if (validSelectedDay) {
                sessionStorage.setItem(JOURNAL_TRADE_DETAILS_RETURN_DAY_KEY, validSelectedDay);
            }
        } catch {}

        const view = (window as unknown as Record<string, unknown>)['__TT_viewTrade'];
        if (typeof view === 'function') {
            try {
                (view as (t: Trade) => void)(trade);
                return;
            } catch {}
        }

        const nav = (window as unknown as Record<string, unknown>)['__TT_navigate'];
        if (typeof nav === 'function') {
            try { (nav as (s: string) => void)('Trade Details'); } catch {}
        }
    }, [validSelectedDay]);
    const tradeTagModalTrade = useMemo(() => {
        if (!tradeTagModalTargetKey) return null;
        return allTrades.find((trade) => getJournalTradeKey(trade as unknown as Record<string, unknown>) === tradeTagModalTargetKey) ?? null;
    }, [allTrades, tradeTagModalTargetKey]);
    const chartTradeRows = useMemo(() => {
        return selectedDayTrades.filter((trade) => getJournalTradeNetPnlValue(trade as JournalTradeRecord) != null);
    }, [selectedDayTrades]);
    const chartTrades = useMemo(() => {
        return chartTradeRows.map((trade) => ({
            pnl: getJournalTradeNetPnlValue(trade as JournalTradeRecord) ?? 0,
            label: trade.symbol,
        }));
    }, [chartTradeRows]);

    // Aggregated stats for the selected day, wired to canonical realized/open semantics
    const dayStats = useMemo(() => {
        let gross = 0;
        let net = 0;
        let feesSum = 0;
        const tradesCount = selectedDayTrades.length;
        let volumeTotal = 0;
        let wins = 0;
        let losses = 0;

        for (const trade of selectedDayTrades as JournalTradeRecord[]) {
            const grossPnl = getJournalTradeGrossPnlValue(trade);
            const netPnl = getJournalTradeNetPnlValue(trade);

            if (grossPnl != null) gross += grossPnl;
            if (netPnl != null) {
                net += netPnl;
                if (netPnl > 0) wins += 1;
                else if (netPnl < 0) losses += 1;
            }

            feesSum += getJournalTradeFeeTotal(trade);

            volumeTotal += getTradeVolume(toAuthoritativeTrade(trade));
        }

        const winPct = calculateWinRatePercent(wins, losses);
        const hasFees = feesSum > 0;

        return { gross, net, feesSum, tradesCount, volumeTotal, winPct, hasFees };
    }, [selectedDayTrades]);
    // Removed unused priorBalance, sortedDayTrades, and selectedDayPnl computations

    const handleDeleteCustomTrade = useCallback((id: number) => {
        setCustomTrades((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const tableColumns = useMemo(() => {
        const columnById = new Map(ALL_COLUMNS.map((column) => [column.id, column]));
        return journalColumnOrder
            .map((columnId) => (visibleColumns.includes(columnId) ? columnById.get(columnId) : null))
            .filter((column): column is ColumnDef => Boolean(column));
    }, [journalColumnOrder, visibleColumns]);

    const journalTableWidthPx = useMemo(() => {
        if (!tableColumns.some((column) => journalColumnWidths[column.id])) return null;
        return tableColumns.reduce((total, column) => {
            return total + (journalColumnWidths[column.id] ?? 140);
        }, 0);
    }, [journalColumnWidths, tableColumns]);

    type TradeRecord = Partial<Trade> & Record<string, unknown>;

    const getFirstNumber = useCallback((tradeRec: TradeRecord, keys: string[]): number | null => {
        for (const key of keys) {
            const raw = tradeRec[key];
            if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
            if (typeof raw === 'string' && raw.trim() !== '') {
                const parsed = Number(raw);
                if (Number.isFinite(parsed)) return parsed;
            }
        }
        return null;
    }, []);

    const formatDurationValue = useCallback((value: unknown): string => {
        if (typeof value === 'string' && value.trim() !== '') return value;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '—';
        const rounded = Math.round(value);
        const hours = Math.floor(rounded / 60);
        const minutes = rounded % 60;
        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h`;
        return `${minutes}m`;
    }, []);

    const renderSideBadge = useCallback((rawValue: unknown) => {
        const raw = String(rawValue ?? '').trim();
        if (!raw) return '—';
        const key = raw.toLowerCase();
        const isShort = key.startsWith('s');
        const isLong = key.startsWith('l');

        if (isShort) {
            return (
                <span className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none ${isDark ? 'dark:bg-red-900/30 dark:text-red-300 dark:border-red-700' : 'bg-red-100 text-red-700 border-red-200'} border`}>
                    <ArrowDownRight className="w-3.5 h-3.5" />
                    <span className="uppercase text-[11px] tracking-wide leading-none">SHORT</span>
                </span>
            );
        }

        if (isLong) {
            return (
                <span className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none ${isDark ? 'dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700' : 'bg-emerald-100 text-emerald-700 border-emerald-200'} border`}>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    <span className="uppercase text-[11px] tracking-wide leading-none">LONG</span>
                </span>
            );
        }

        return (
            <span className={`inline-flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none ${isDark ? 'dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700' : 'bg-zinc-100 text-zinc-700 border-zinc-200'} border`}>
                {raw}
            </span>
        );
    }, [isDark]);

    const renderJournalCell = useCallback((trade: Trade, colId: string) => {
        const tradeRec = trade as TradeRecord;
        const isCustom = Boolean(tradeRec['isCustom']) && !tradeRec['persistedTradeId'];
        const isDemoTrade = isJournalDemoTrade(tradeRec);
        const tradeStatus = getJournalTradeStatus(tradeRec);
        const tradePnl = getJournalTradeNetPnlValue(tradeRec);

        switch (colId) {
            case 'time': {
                if (tradeRec['manualDateOnly'] === true) return '—';
                const ms = getTradeTimeMs(trade);
                if (ms == null) return '—';
                const formatted = formatDateTimeInTimezone(ms, displayTimeZone);
                return formatted.timeStr || fmtTime24hWithSeconds(ms);
            }
            case 'symbol':
                return <span className={`${isDark ? 'font-semibold text-zinc-100' : 'font-semibold'}`}>{trade.symbol || '—'}</span>;
            case 'volume':
                return typeof trade.volume === 'number' && Number.isFinite(trade.volume) ? trade.volume : '—';
            case 'executionCount': {
                const executions = getFirstNumber(tradeRec, ['executions', 'executionCount']);
                return executions ?? '—';
            }
            case 'side':
                return renderSideBadge(trade.side || String(tradeRec['direction'] ?? ''));
            case 'pnl':
                return (
                    <div className="flex items-center justify-center gap-2">
                        {tradeStatus === 'open' ? (
                            <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold tracking-[0.18em] ${isDark ? 'border-teal-700 bg-teal-900/30 text-teal-300' : 'border-teal-200 bg-teal-50 text-teal-700'}`}>
                                OPEN
                            </span>
                        ) : (
                            <span className={`font-semibold ${clsPnL(tradePnl)}`}>{tradePnl != null ? fmtMoney(tradePnl) : '—'}</span>
                        )}
                        {isCustom ? (
                            <button
                                type="button"
                                title="Delete this entry"
                                aria-label="Delete this entry"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteCustomTrade(trade.id);
                                }}
                                className={`${isDark ? 'p-1 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-900/20' : 'p-1 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50'}`}
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        ) : null}
                    </div>
                );
            case 'pnlR': {
                const riskLike = {
                    initialRiskAmount: (tradeRec as Record<string, unknown>)['initialRiskAmount'],
                    initialRisk: (tradeRec as Record<string, unknown>)['initialRisk'],
                    initial_risk: (tradeRec as Record<string, unknown>)['initial_risk'],
                    riskAmount: (tradeRec as Record<string, unknown>)['riskAmount'],
                };
                const initialRisk = resolveTradeInitialRiskValue(riskLike, defaultRiskPerTrade);
                return initialRisk && initialRisk !== 0 && tradePnl != null ? `${(tradePnl / initialRisk).toFixed(2)}R` : '—';
            }
            case 'pnlPercent': {
                const pct = getFirstNumber(tradeRec, ['pnlPercent']);
                return pct != null ? <span className={clsPnL(pct)}>{pct.toFixed(2)}%</span> : '—';
            }
            case 'pnlTicksGross': {
                return formatJournalMovementWithUnit(getJournalTotalMovementValue(tradeRec), getJournalMovementUnit(tradeRec));
            }
            case 'pnlTicksContract': {
                const ticks = getFirstNumber(tradeRec, ['pnlTicksContract']);
                return formatJournalMovementWithUnit(ticks, getJournalMovementUnit(tradeRec));
            }
            case 'commission': {
                const commission = getFirstNumber(tradeRec, ['commission']);
                return commission != null ? fmtMoney(commission) : '—';
            }
            case 'commissionAndFees': {
                const totalFees = getFirstNumber(tradeRec, ['commissionAndFees', 'feesTotal', 'fees', 'commission']);
                return totalFees != null ? fmtMoney(totalFees) : '—';
            }
            case 'initialRisk': {
                const riskLike = {
                    initialRiskAmount: (tradeRec as Record<string, unknown>)['initialRiskAmount'],
                    initialRisk: (tradeRec as Record<string, unknown>)['initialRisk'],
                    initial_risk: (tradeRec as Record<string, unknown>)['initial_risk'],
                    riskAmount: (tradeRec as Record<string, unknown>)['riskAmount'],
                };
                const value = resolveTradeInitialRiskValue(riskLike, defaultRiskPerTrade);
                return value != null ? fmtMoney(value) : '—';
            }
            case 'positionMfe': {
                const value = getFirstNumber(tradeRec, ['positionMfe']);
                return value != null ? fmtMoney(value) : '—';
            }
            case 'positionMae': {
                const value = getFirstNumber(tradeRec, ['positionMae']);
                return value != null ? fmtMoney(value) : '—';
            }
            case 'priceMfe': {
                const value = getFirstNumber(tradeRec, ['priceMfe']);
                return value != null ? value.toLocaleString('en-US') : '—';
            }
            case 'priceMae': {
                const value = getFirstNumber(tradeRec, ['priceMae']);
                return value != null ? value.toLocaleString('en-US') : '—';
            }
            case 'bestExitPnl': {
                const value = getFirstNumber(tradeRec, ['bestExitPnl']);
                return value != null ? fmtMoney(value) : '—';
            }
            case 'exitEfficiency': {
                const value = getFirstNumber(tradeRec, ['exitEfficiency']);
                return value != null ? `${value.toFixed(2)}%` : '—';
            }
            case 'duration':
                return formatDurationValue(tradeRec['duration']);
            case 'shared':
                return typeof trade.shared === 'boolean' ? (trade.shared ? 'Yes' : 'No') : '—';
            case 'notes': {
                const noteKey = getJournalTradeKey(tradeRec);
                if (editingTradeNoteKey === noteKey) {
                    return (
                        <div className="flex flex-col items-center" onClick={(event) => event.stopPropagation()}>
                            <textarea
                                ref={tradeNoteInputRef}
                                value={editingTradeNoteValue}
                                onChange={(event) => setEditingTradeNoteValue(event.target.value)}
                                onKeyDown={(event) => {
                                    event.stopPropagation();
                                    if (event.key === 'Escape') {
                                        setEditingTradeNoteKey(null);
                                        setEditingTradeNoteValue('');
                                    } else if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault();
                                        saveTradeNote(trade, editingTradeNoteValue);
                                        setEditingTradeNoteKey(null);
                                        setEditingTradeNoteValue('');
                                    }
                                }}
                                rows={3}
                                className="w-full max-w-[20rem] rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                placeholder="Type a note... (Enter to save, Esc to cancel)"
                            />
                            <div className="mt-2 flex gap-2">
                                <button
                                    className="rounded-md bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-500"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        saveTradeNote(trade, editingTradeNoteValue);
                                        setEditingTradeNoteKey(null);
                                        setEditingTradeNoteValue('');
                                    }}
                                >
                                    Save
                                </button>
                                <button
                                    className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-sm text-zinc-700 hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setEditingTradeNoteKey(null);
                                        setEditingTradeNoteValue('');
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    );
                }

                const fullNote = String(tradeRec['notes'] ?? '').trim();
                if (!fullNote) {
                    if (isDemoTrade) return '—';
                    return (
                        <div className="flex items-center justify-center">
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingTradeNoteKey(noteKey);
                                    setEditingTradeNoteValue('');
                                }}
                                className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors ${isDark ? 'text-white/50 hover:bg-zinc-800 hover:text-white/70' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'}`}
                                aria-label="Add note"
                            >
                                <MessageSquarePlus className="w-4 h-4" />
                            </button>
                        </div>
                    );
                }

                const words = fullNote.split(/\s+/);
                const previewWords = 4;
                const preview = words.slice(0, previewWords).join(' ') + (words.length > previewWords ? '...' : '');
                return (
                    <span className={`block w-full text-center text-sm ${isDark ? 'text-zinc-200' : 'text-black'}`} title={fullNote}>
                        <span className="inline-block max-w-[18rem] truncate">{preview}</span>
                    </span>
                );
            }
            case 'tags': {
                const tags = Array.isArray(trade.tags) ? trade.tags.map((tag) => String(tag)) : [];
                if (tags.length > 0) {
                    return (
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            {tags.slice(0, 6).map((tag) => (
                                <RemovableTag
                                    key={tag}
                                    label={tag}
                                    onClick={isDemoTrade ? undefined : () => {
                                        setTradeTagModalTargetKey(getJournalTradeKey(tradeRec));
                                        setIsTradeTagsModalOpen(true);
                                    }}
                                    onRemove={() => removeTradeTag(trade, tag)}
                                />
                            ))}
                            {tags.length > 6 ? <span className="inline-flex items-center justify-center rounded-md border border-[#2c4476] bg-[#19233c] px-2.5 py-1.5 text-xs font-semibold text-[#93bbf6]">+{tags.length - 6}</span> : null}
                        </div>
                    );
                }

                if (isDemoTrade) return '—';
                return (
                    <div className="flex items-center justify-center">
                        <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); setTradeTagModalTargetKey(getJournalTradeKey(tradeRec)); setIsTradeTagsModalOpen(true); }}
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors ${isDark ? 'text-white/50 hover:bg-zinc-800 hover:text-white/70' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'}`}
                            aria-label="Add tags"
                        >
                            <Tag className="w-4 h-4" />
                        </button>
                    </div>
                );
            }
            default:
                return '—';
        }
    }, [editingTradeNoteKey, editingTradeNoteValue, formatDurationValue, getFirstNumber, handleDeleteCustomTrade, isDark, removeTradeTag, saveTradeNote, defaultRiskPerTrade, renderSideBadge, displayTimeZone]);

    // Clear editing state when switching days
    useEffect(() => {
        setEditingNoteId(null);
        setEditingNoteName('');
        setEditingNoteSymbol('');
        setEditingNoteTradeKey('');
        setEditingTradeNoteKey(null);
        setEditingTradeNoteValue('');
        setIsTradeTagsModalOpen(false);
        setTradeTagModalTargetKey(null);
    }, [validSelectedDay]);

    // New Journal trades use the existing server-backed manual-entry form.
    const [isCustomEntryOpen, setIsCustomEntryOpen] = useState(false);
    const [manualAccounts, setManualAccounts] = useState<CsvSourceAccountOption[]>([]);
    const [manualAccountsLoading, setManualAccountsLoading] = useState(false);
    const [manualAccountsError, setManualAccountsError] = useState('');
    const [pendingManualDraft, setPendingManualDraft] = useState(readJournalManualTradeDraft);
    const [manualRetrying, setManualRetrying] = useState(false);
    const [manualRetryError, setManualRetryError] = useState('');

    useEffect(() => {
        if (!isCustomEntryOpen) return;
        let cancelled = false;
        setManualAccountsLoading(true);
        setManualAccountsError('');
        void listCsvSourceAccounts()
            .then((accounts) => {
                if (!cancelled) setManualAccounts(accounts.filter((account) => account.status === 'active'));
            })
            .catch(() => {
                if (!cancelled) setManualAccountsError('Trading accounts could not be loaded. Close and reopen this form to retry.');
            })
            .finally(() => { if (!cancelled) setManualAccountsLoading(false); });
        return () => { cancelled = true; };
    }, [isCustomEntryOpen]);

    useEffect(() => {
        const refresh = () => setPendingManualDraft(readJournalManualTradeDraft());
        window.addEventListener(privateTradingScopeChangedEventName(), refresh);
        return () => window.removeEventListener(privateTradingScopeChangedEventName(), refresh);
    }, []);

    const confirmManualTradeSave = () => {
        setPendingManualDraft(readJournalManualTradeDraft());
        setManualRetryError('');
        setManualTradeRefresh((current) => current + 1);
        window.dispatchEvent(new CustomEvent(IMPORTED_TRADES_UPDATED_EVENT, { detail: { source: 'journal-server-manual' } }));
    };

    const createJournalManualTrade = async (payload: ManualEntryPayload) => {
        if (!payload.accountId) throw new Error('Select a trading account before saving this trade.');
        const blocked = getTenantAssetBlockedMessage({ assetType: payload.assetType });
        if (blocked) throw new Error(blocked);
        try {
            await saveJournalManualTrade({ ...payload, accountId: payload.accountId });
            confirmManualTradeSave();
        } finally {
            setPendingManualDraft(readJournalManualTradeDraft());
        }
    };

    const retryPendingManualTrade = async () => {
        if (manualRetrying) return;
        setManualRetrying(true);
        setManualRetryError('');
        try {
            await retryJournalManualTrade();
            confirmManualTradeSave();
        } catch (error) {
            setManualRetryError(error instanceof Error ? error.message : 'Save failed. Your draft is still available to retry.');
        } finally {
            setPendingManualDraft(readJournalManualTradeDraft());
            setManualRetrying(false);
        }
    };

    // Left panel is hoisted top-level to avoid remounts that cause input focus loss

    // Reusable right panel body (header tabs + content)
    const RightPanelBody = () => (
        <>
            <div className="h-5 flex-shrink-0" />
            <header className={`flex-shrink-0 px-4 pb-[30px] pt-4 ${isDark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                <div>
                    <h2 className={`text-2xl font-bold leading-tight ${isDark ? 'text-white' : 'text-zinc-900'}`}>Notes</h2>
                </div>
            </header>
            <div className="scrollable flex-1 overflow-visible overflow-y-auto p-4 space-y-4 min-h-0">
                <div className="space-y-2">
                        {/* Trade Context Selector */}
                        <div>
                            <div className="mb-4">
                                <label className="text-xs text-gray-500 mb-1 block">Context / Trade Link</label>
                                <div className="relative">
                                    <select
                                        value={activeTradeId ?? 'daily'}
                                        onChange={(event) => handleRowClick(event.target.value === 'daily' ? null : Number(event.target.value))}
                                        className="w-full appearance-none rounded-md border border-zinc-300 bg-white py-2.5 pl-3 pr-8 text-sm text-zinc-900 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer dark:border-gray-700 dark:bg-[#14151a] dark:text-white"
                                    >
                                        <option value="daily">📝 General Daily Note</option>
                                        {TRADES_DATA.map((trade) => (
                                            <option key={trade.id} value={trade.id}>
                                                {trade.symbol} | {trade.time} | {trade.pnl >= 0 ? '+' : '-'}${Math.abs(trade.pnl).toFixed(2)}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-5 top-3 h-4 w-4 text-gray-500 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-600 mt-1">Select a trade or click a row in the table.</p>
                            </div>
                            {noteSaveError ? (
                                <p className={`mt-1 text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>{noteSaveError}</p>
                            ) : null}
                        </div>
                        {/* Note Title editing removed per request */}
                        <div className="relative">
                            <RichTextEditor
                                ref={editorInstanceRef}
                                value={validSelectedDay ? (notesByDate[validSelectedDay] ?? '') : ''}
                                suspendExternalSync={suspendEditorSync}
                                isDark={isDark}
                            />
                        </div>
                        <div className={`mt-2 flex justify-end ${isDark ? '' : 'bg-zinc-100 rounded-lg p-2'}`}>
                            <button
                                type="button"
                                onClick={() => {
                                    const html = editorInstanceRef.current?.getHTML() ?? '';
                                    if (!validSelectedDay) return;
                                    if (demoTradesActive) {
                                        handleNoteChange(validSelectedDay, html);
                                        setNoteSaveError('Demo journal notes are read-only.');
                                        return;
                                    }
                                    // If editing an existing note, update it directly without requiring trade selection
                                    if (editingNoteId) {
                                        const cleaned = cleanEditorHtml(html).trim();
                                        if (!cleaned) return;
                                        // Keep the original title; editing title is disabled
                                        const currentTitle = (editingNoteSymbol || editingNoteName || 'Untitled note').trim();
                                        const ok = handleUpdateSavedNote(
                                            validSelectedDay,
                                            editingNoteId,
                                            cleaned,
                                            currentTitle,
                                            (editingNoteSymbol || undefined),
                                            (editingNoteTradeKey || undefined)
                                        );
                                        if (ok) {
                                            // Clear editor content and selected trade after saving edits
                                            handleNoteChange(validSelectedDay, '');
                                            try { if (editorInstanceRef.current?.setHTML) { editorInstanceRef.current.setHTML(''); } } catch {}
                                            // handleUpdateSavedNote already clears symbol via setNoteSymbol(date, '')
                                            setEditingNoteId(null);
                                            setEditingNoteName('');
                                            setEditingNoteSymbol('');
                                            setEditingNoteTradeKey('');
                                        }
                                        return;
                                    }
                                    const tradeKey = ((noteTradeKeyByDate[validSelectedDay] || '').trim());
                                    if (!tradeKey && activeTradeId !== null) {
                                        // Preserve current editor content so it isn't lost on re-render
                                        handleNoteChange(validSelectedDay, html);
                                        setNoteSaveError('Please select a trade before saving.');
                                        return;
                                    }
                                    const title = activeTradeId == null
                                        ? 'General Daily Note'
                                        : (((noteSymbolByDate[validSelectedDay] || '').trim()) || 'Untitled note');
                                    const ok = handleSaveCurrentNote(validSelectedDay, html, title);
                                    if (ok) {
                                        handleNoteChange(validSelectedDay, '');
                                        try { if (editorInstanceRef.current?.setHTML) { editorInstanceRef.current.setHTML(''); } } catch {}
                                        setNoteSymbol(validSelectedDay, '');
                                        setNoteTradeKey(validSelectedDay, '');
                                        setActiveTradeId(null);
                                        setNoteSaveError('');
                                    }
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                            >
                                {editingNoteId ? 'Update' : 'Save'}
                            </button>
                        </div>
                        <div className="mt-5">
                            <h4 className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>Saved Notes</h4>
                            <div className={`${isDark ? 'bg-zinc-800' : 'bg-zinc-200'} h-px w-full mt-2 mb-2`} />
                            <NoteHistoryList entries={(validSelectedDay && (demoTradesActive ? demoNotesHistoryByDate[validSelectedDay] : notesHistoryByDate[validSelectedDay])) || []} isDark={isDark} highlightId={lastSavedNoteId || undefined} allTrades={allTrades} readOnly={demoTradesActive}
                                onDelete={(id) => {
                                    if (demoTradesActive) return;
                                    if (!validSelectedDay) return;
                                    const note = (notesHistoryByDate[validSelectedDay] || []).find(n => n.id === id);
                                    if (!note) return;
                                    setDeleteConfirm({ id, title: note.name || 'Untitled note', date: validSelectedDay, symbol: note.symbol });
                                }}
                                onEdit={(id) => {
                                    if (demoTradesActive) return;
                                    if (!validSelectedDay) return;
                                    const note = (notesHistoryByDate[validSelectedDay] || []).find(n => n.id === id);
                                    if (!note) return;
                                    try { if (editorInstanceRef.current?.setHTML) { editorInstanceRef.current.setHTML(note.text); } } catch {}
                                    // ensure editor draft state is updated for the selected day
                                    handleNoteChange(validSelectedDay, note.text);
                                    setEditingNoteId(id);
                                    setEditingNoteName(note.name || '');
                                    setEditingNoteSymbol(note.symbol || '');
                                    setEditingNoteTradeKey(note.tradeKey || '');
                                    // focus the editor's contentEditable if possible
                                    const el = document.querySelector('[contenteditable="true"]') as HTMLElement | null;
                                    if (el) { el.focus();
                                        // place caret at end
                                        const range = document.createRange();
                                        range.selectNodeContents(el);
                                        range.collapse(false);
                                        const sel = window.getSelection();
                                        sel?.removeAllRanges();
                                        sel?.addRange(range);
                                    }
                                }}
                                onPreview={(id) => {
                                    if (!validSelectedDay) return;
                                    const noteList = demoTradesActive ? (demoNotesHistoryByDate[validSelectedDay] || []) : (notesHistoryByDate[validSelectedDay] || []);
                                    const note = noteList.find(n => n.id === id);
                                    if (!note) return;
                                    setPreviewNote({ id, title: note.name || 'Untitled note', html: note.text, ts: note.ts, symbol: note.symbol, tradeKey: note.tradeKey });
                                }}
                            />
                        </div>
                </div>
            </div>
        </>
    );

    return (
    <div className={`font-sans w-full max-w-none min-w-0 flex-1 min-h-screen h-full flex items-stretch ${isDark ? 'bg-zinc-900 text-zinc-100' : 'bg-zinc-100 text-zinc-900'} mt-0 pt-0 transition-colors`} data-tour-target="journal-page">
        {/* Left Sidebar: static on lg+ */}
        <aside ref={leftAsideRef} className={`${isDark ? 'hidden lg:flex flex-shrink-0 flex-col self-stretch min-h-screen h-auto bg-[#1d1d20] border-r border-zinc-800/60 text-zinc-100' : 'hidden lg:flex flex-shrink-0 flex-col self-stretch min-h-screen h-auto bg-zinc-100 border-r border-zinc-200/70 text-zinc-800'} ${leftCollapsed ? 'opacity-0 pointer-events-none overflow-hidden' : 'opacity-100'}`} style={{ width: leftCollapsed ? 0 : leftWidth }}>
                    <LeftPanelBodyTop
                        days={days}
                        tradesByDay={tradesByDay}
                        selectedDay={validSelectedDay}
                        onPickDay={(d) => { setSelectedDay(d); setLeftOpen(false); }}
                        searchQuery={searchQuery}
                        onChangeSearch={setSearchQuery}
                        isDark={isDark}
                    />
                </aside>

                {/* Left Resizer (lg+) */}
                <div
                    className="hidden lg:flex items-stretch self-stretch min-h-screen overflow-visible"
                    style={{ alignItems: 'stretch' }}
                >
                    <div
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(e) => {
                            leftResizingRef.current = true;
                            leftStartXRef.current = e.clientX;
                            leftStartWidthRef.current = leftWidth;
                            document.body.style.cursor = 'col-resize';
                            const onMouseMove = (ev: MouseEvent) => {
                                if (leftStartXRef.current == null || leftStartWidthRef.current == null) return;
                                const dx = ev.clientX - leftStartXRef.current;
                                const newW = Math.max(200, Math.min(420, leftStartWidthRef.current + dx));
                                setLeftWidth(newW);
                            };
                            const onMouseUp = () => {
                                leftResizingRef.current = false;
                                leftStartXRef.current = null;
                                leftStartWidthRef.current = null;
                                document.body.style.cursor = '';
                                document.removeEventListener('mousemove', onMouseMove);
                                document.removeEventListener('mouseup', onMouseUp);
                            };
                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                        }}
                        onTouchStart={(e) => {
                            leftResizingRef.current = true;
                            leftStartXRef.current = e.touches[0].clientX;
                            leftStartWidthRef.current = leftWidth;
                            document.body.style.cursor = 'col-resize';
                            const onTouchMove = (ev: TouchEvent) => {
                                if (leftStartXRef.current == null || leftStartWidthRef.current == null) return;
                                const touch = ev.touches[0];
                                const dx = touch.clientX - leftStartXRef.current;
                                const newW = Math.max(200, Math.min(420, leftStartWidthRef.current + dx));
                                setLeftWidth(newW);
                            };
                            const onTouchEnd = () => {
                                leftResizingRef.current = false;
                                leftStartXRef.current = null;
                                leftStartWidthRef.current = null;
                                document.body.style.cursor = '';
                                document.removeEventListener('touchmove', onTouchMove);
                                document.removeEventListener('touchend', onTouchEnd);
                            };
                            document.addEventListener('touchmove', onTouchMove);
                            document.addEventListener('touchend', onTouchEnd);
                        }}
                        className={`${isDark ? `w-1.5 cursor-col-resize hover:bg-zinc-800/70 ${leftCollapsed ? 'bg-zinc-800/50' : 'bg-transparent'}` : `w-1.5 cursor-col-resize hover:bg-zinc-200/70 ${leftCollapsed ? 'bg-zinc-200' : 'bg-transparent'}`} relative flex items-center justify-center group`}
                        style={{ userSelect: 'none' }}
                    >
                        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center overflow-visible">
                            <button
                                type="button"
                                aria-label={leftCollapsed ? 'Expand left panel' : 'Collapse left panel'}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLeftSidebar();
                                }}
                                className={`absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm transition-all ${leftCollapsed ? 'left-0 translate-x-0 opacity-50' : 'left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100'} ${isDark ? 'border-zinc-500 bg-zinc-900 text-white group-hover:bg-zinc-800' : 'border-zinc-300 bg-zinc-200 text-zinc-700 group-hover:bg-zinc-300'}`}
                            >
                                {leftCollapsed ? <ChevronRightIcon className={`h-3.5 w-3.5 ${isDark ? 'text-white' : 'text-zinc-700'}`} /> : <ChevronLeftIcon className={`h-3.5 w-3.5 ${isDark ? 'text-white' : 'text-zinc-700'}`} />}
                            </button>
                        </div>
                    </div>
                </div>

            {/* Main Content (Middle Column) */}
            <main className="flex-1 flex flex-col min-h-0 min-w-0 h-full">
                <header className={`h-[105px] flex-shrink-0 overflow-visible border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                    <div className="flex items-center justify-between gap-2 px-4 pt-0 pb-2">
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setLeftOpen(true)} className={`${isDark ? 'lg:hidden inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800' : 'lg:hidden inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'} transition-colors`}>
                                <span>Days</span>
                            </button>
                        </div>
                        <div className="flex-1 flex items-center">
                            <StandardPageHeader title="Journal" subtitle="Notes, trades and daily summaries." className="hidden md:block mt-4" showDivider={false} />
                        </div>
                        <div className="flex items-center gap-2 ml-auto translate-y-[14px]">
                            <button type="button" onClick={() => setRightOpen(true)} className={`${isDark ? 'xl:hidden inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800' : 'xl:hidden inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'} transition-colors`}>
                                <span>Notes/Tags</span>
                            </button>
                            <div className="hidden md:flex items-center gap-4">
                                <DateRangePicker onApply={handleDateChange} initialRange={dateRange} isDark={isDark} />
                                <CustomFilters isDark={isDark} value={filters} onApply={setFilters} assetVisibility={assetVisibility} />
                            </div>
                        </div>
                    </div>
                </header>
                <div className="h-2" />

                {/* Selected day title */}
                <div className={`py-4 text-center ${isDark ? 'text-zinc-200' : 'text-zinc-800'} text-2xl font-semibold`}>
                    {validSelectedDay ? fmtWeekDay(validSelectedDay, 'long') : ''}
                </div>
                {pendingManualDraft && (
                    <div role="alert" className="mx-4 mb-3 rounded-lg border border-amber-500/50 bg-amber-950/30 p-3 text-sm text-amber-100">
                        <div>Journal trade {pendingManualDraft.input.symbol} has a pending local draft. Retry safely confirms the same trade ID with the server.</div>
                        <button type="button" onClick={() => void retryPendingManualTrade()} disabled={manualRetrying} className="mt-2 rounded bg-amber-500 px-3 py-1 font-semibold text-black disabled:opacity-50">
                            {manualRetrying ? 'Retrying…' : 'Retry save'}
                        </button>
                        {manualRetryError && <p className="mt-2 text-red-300">{manualRetryError}</p>}
                    </div>
                )}
                {selectedDayTrades.length ? (
                    <div className="space-y-4 p-4">
                        {/* Top Stats Header (wired to gross/net/fees when provided) */}
                        {(() => {
                            const dividerCls = 'divide-border';
                            const labelCls = isDark ? 'text-zinc-400' : 'text-zinc-500';
                            const valueNeutral = isDark ? 'text-zinc-300' : 'text-zinc-700';
                            return (
                                <div className="overflow-hidden rounded-xl border border-border bg-white dark:bg-[#1d1d20]">
                                    <div className="p-4">
                                        <div className={`flex items-stretch ${dividerCls} divide-x`}>
                                            <div className="flex-1 px-4 text-center first:pl-0">
                                                <div className={`text-xs font-semibold tracking-wider uppercase ${labelCls}`}>Net P&L</div>
                                                <div className={`text-lg font-semibold ${clsPnL(dayStats.net)}`}>{fmtMoney(dayStats.net)}</div>
                                                <div className={`mt-1 text-xs font-medium leading-4 ${labelCls}`}>
                                                    Gross{' '}
                                                    <span className={`font-semibold ${clsPnL(dayStats.gross)}`}>
                                                        {fmtMoney(dayStats.gross)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-1 px-4 text-center">
                                                <div className={`text-xs font-semibold tracking-wider uppercase ${labelCls}`}>Trades</div>
                                                <div className={`text-lg font-semibold ${valueNeutral}`}>{dayStats.tradesCount}</div>
                                            </div>
                                            <div className="flex-1 px-4 text-center">
                                                <div className={`text-xs font-semibold tracking-wider uppercase ${labelCls}`}>Volume</div>
                                                <div className={`text-lg font-semibold ${valueNeutral}`}>{dayStats.volumeTotal.toLocaleString()}</div>
                                            </div>
                                            <div className="flex-1 px-4 text-center">
                                                <div className={`text-xs font-semibold tracking-wider uppercase ${labelCls}`}>Win %</div>
                                                <div className={`text-lg font-semibold ${valueNeutral}`}>{dayStats.winPct.toFixed(1)}%</div>
                                            </div>
                                            <div className="flex-1 px-4 text-center">
                                                <div className={`text-xs font-semibold tracking-wider uppercase ${labelCls}`}>Fees</div>
                                                <div className={`text-lg font-semibold ${dayStats.hasFees ? 'text-red-500' : valueNeutral}`}>{dayStats.hasFees ? `-${fmtMoney(Math.abs(dayStats.feesSum))}` : 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* P&L Chart */}
                        <div className="overflow-hidden rounded-xl border border-border bg-white dark:bg-[#1d1d20]">
                            <div className="p-4">
                                <h3 className={`text-sm font-semibold ${isDark ? 'text-zinc-200' : 'text-zinc-700'}`}>P&L by Trade</h3>
                                <div className="mt-2">
                                    <PnLChart
                                        trades={chartTrades}
                                        theme={isDark ? 'dark' : 'light'}
                                        onBarClick={(index) => {
                                            const t = chartTradeRows[index];
                                            if (t) openTradeDetails(t);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Divider between chart and action bar that matches other hairlines */}
                        <div className="border-t border-border" />
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {!demoTradesActive ? (
                                    <div className="relative">
                                        <button type="button" onClick={() => setIsCustomEntryOpen(true)} className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                                            <span>Custom Entry</span>
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="inline-flex items-center gap-2 pr-2 border-r border-zinc-200 dark:border-zinc-700" aria-label="Journal display timezone">
                                    <span className="text-sm text-zinc-700 dark:text-zinc-300">Time: {displayTimeZone}</span>
                                </div>
                                <div className="relative">
                                    <button type="button" onClick={() => setIsColumnSettingsOpen(true)} className={`${isDark ? 'flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800' : 'flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'} transition-colors`}>
                                        <SettingsIcon className="w-4 h-4" />
                                        <span>Column Settings</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl bg-transparent">
                            <div className="p-0">
          <div ref={journalTableScrollerRef} className={`scrollable relative z-0 overflow-x-auto rounded-xl border border-border ${isDark ? 'bg-[#1d1d20]' : 'bg-white'}`}>
                                    <table className="text-sm table-auto border-separate border-spacing-0" style={{ width: journalTableWidthPx ? `${journalTableWidthPx}px` : '100%', minWidth: '100%' }}>
                                        <colgroup>
                                            {tableColumns.map((col) => (
                                                <col key={col.id} style={journalColumnWidths[col.id] ? { width: `${journalColumnWidths[col.id]}px` } : undefined} />
                                            ))}
                                        </colgroup>
                                        <thead className="select-none">
                                            <tr className={`${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
                                                {tableColumns.map((col) => (
                                                    <th
                                                        key={col.id}
                                                        ref={(el) => { journalHeaderRefs.current[col.id] = el; }}
                                                        scope="col"
                                                        onDragOver={(event) => handleJournalColumnDragOver(event, col.id)}
                                                        onDrop={(event) => handleJournalColumnDrop(event, col.id)}
                                                        className={`relative px-4 py-3 text-center font-medium transition-colors ${journalDraggingColumnId === col.id ? 'opacity-60' : ''} ${journalDropColumnId === col.id ? (isDark ? 'bg-blue-900/30' : 'bg-blue-50') : (isDark ? 'bg-zinc-800' : 'bg-zinc-200')} ${isDark ? 'text-zinc-200 border-r border-b border-zinc-700 last:border-r-0' : 'text-zinc-700 border-r border-b border-zinc-300 last:border-r-0'}`}
                                                    >
                                                        <div
                                                            draggable
                                                            onDragStart={(event) => handleJournalColumnDragStart(event, col.id)}
                                                            onDragEnd={handleJournalColumnDragEnd}
                                                            className="flex cursor-grab items-center justify-center gap-2 min-w-0 active:cursor-grabbing"
                                                        >
                                                            <span className="truncate">{col.label.split('(')[0]}</span>
                                                        </div>
                                                        <div
                                                            onMouseDown={(event) => startJournalColumnResize(event, col.id)}
                                                            onClick={(event) => event.stopPropagation()}
                                                            role="separator"
                                                            aria-orientation="vertical"
                                                            title="Resize column"
                                                            className="absolute right-0 top-0 h-full w-3 cursor-col-resize select-none"
                                                        >
                                                            <div className="absolute right-0 top-0 h-full w-px bg-transparent" />
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDayTrades.map((trade) => (
                                                <tr
                                                    key={getJournalTradeKey(trade as unknown as Record<string, unknown>)}
                                                    onClick={() => openTradeDetails(trade)}
                                                    className={`relative cursor-pointer transition-colors ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-50'} [&:last-child>td]:border-b-0`}
                                                >
                                                    {tableColumns.map((col) => (
                                                        <td key={col.id} className={`px-4 py-3 text-center font-light border-r border-b last:border-r-0 ${isDark ? 'border-zinc-800 bg-[#1d1d20] text-zinc-100' : 'border-zinc-300 bg-white text-zinc-900'}`}>
                                                            {renderJournalCell(trade, col.id)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {journalDraggingColumnId && journalDropIndicator ? (
                                        <div
                                            className="pointer-events-none absolute top-0 bottom-0 z-30"
                                            style={{ left: journalDropIndicator.left, width: 2, background: 'rgba(59,130,246,0.9)', transition: 'left 120ms linear' }}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-4 h-full p-4">
                        <p className="text-zinc-500">No entries found for the selected date range.</p>
                        {!demoTradesActive && (
                            <button type="button" onClick={() => setIsCustomEntryOpen(true)} className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                                Custom Entry
                            </button>
                        )}
                    </div>
                )}
            </main>

            {/* Modals mounted at root level to ensure state is used */}
            <ColumnSettingsModal
                isOpen={isColumnSettingsOpen}
                onClose={() => setIsColumnSettingsOpen(false)}
                visibleColumns={visibleColumns}
                onSave={(cols) => setVisibleColumns(cols)}
                isDark={isDark}
            />
            <CreateCustomEntryModal
                open={isCustomEntryOpen}
                title="Create Journal Trade"
                onClose={() => setIsCustomEntryOpen(false)}
                onCreate={createJournalManualTrade}
                accounts={manualAccounts}
                accountsLoading={manualAccountsLoading}
                accountsError={manualAccountsError}
                requireAccount
                timeZone={displayTimeZone}
                initialDate={validSelectedDay || undefined}
            />
            <TradeTagsModal
                isOpen={isTradeTagsModalOpen}
                onClose={() => { setIsTradeTagsModalOpen(false); setTradeTagModalTargetKey(null); }}
                tags={Array.isArray(tradeTagModalTrade?.tags) ? tradeTagModalTrade.tags.map((tag) => String(tag)) : []}
                onAdd={(tag) => {
                    if (!tradeTagModalTrade) return;
                    applyTagToTrade(tradeTagModalTrade, tag);
                }}
                onRemove={(tag) => {
                    if (!tradeTagModalTrade) return;
                    removeTradeTag(tradeTagModalTrade, tag);
                }}
                onClear={() => {
                    if (!tradeTagModalTrade) return;
                    clearTradeTags(tradeTagModalTrade);
                }}
            />

            {/* Right Sidebar */}
            {/* Resizer (xl+) */}
                <div
                    className="hidden xl:flex items-stretch self-stretch min-h-screen overflow-visible"
                    style={{ alignItems: 'stretch' }}
                >
                <div
                    role="separator"
                    aria-orientation="vertical"
                    onMouseDown={(e) => {
                        resizingRef.current = true;
                        startXRef.current = e.clientX;
                        startWidthRef.current = rightWidth;
                        document.body.style.cursor = 'col-resize';
                        const onMouseMove = (ev: MouseEvent) => {
                            if (startXRef.current == null || startWidthRef.current == null) return;
                            const dx = startXRef.current - ev.clientX;
                            const newW = Math.max(220, Math.min(600, startWidthRef.current + dx));
                            setRightWidth(newW);
                        };
                        const onMouseUp = () => {
                            resizingRef.current = false;
                            startXRef.current = null;
                            startWidthRef.current = null;
                            document.body.style.cursor = '';
                            document.removeEventListener('mousemove', onMouseMove);
                            document.removeEventListener('mouseup', onMouseUp);
                        };
                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                    }}
                    onTouchStart={(e) => {
                        resizingRef.current = true;
                        startXRef.current = e.touches[0].clientX;
                        startWidthRef.current = rightWidth;
                        document.body.style.cursor = 'col-resize';
                        const onTouchMove = (ev: TouchEvent) => {
                            if (startXRef.current == null || startWidthRef.current == null) return;
                            const touch = ev.touches[0];
                            const dx = startXRef.current - touch.clientX;
                            const newW = Math.max(220, Math.min(600, startWidthRef.current + dx));
                            setRightWidth(newW);
                        };
                        const onTouchEnd = () => {
                            resizingRef.current = false;
                            startXRef.current = null;
                            startWidthRef.current = null;
                            document.body.style.cursor = '';
                            document.removeEventListener('touchmove', onTouchMove);
                            document.removeEventListener('touchend', onTouchEnd);
                        };
                        document.addEventListener('touchmove', onTouchMove);
                        document.addEventListener('touchend', onTouchEnd);
                    }}
                    className={`${isDark ? `w-1.5 cursor-col-resize hover:bg-zinc-800/70 ${rightCollapsed ? 'bg-zinc-800/50' : 'bg-transparent'}` : `w-1.5 cursor-col-resize hover:bg-zinc-200/70 ${rightCollapsed ? 'bg-zinc-200' : 'bg-transparent'}`} relative flex items-center justify-center group`}
                    style={{ userSelect: 'none' }}
                >
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center overflow-visible">
                        <button
                            type="button"
                            aria-label={rightCollapsed ? 'Expand right panel' : 'Collapse right panel'}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleRightSidebar();
                            }}
                            className={`absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm transition-all ${rightCollapsed ? 'left-auto right-0 translate-x-0 opacity-50' : 'left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100'} ${isDark ? 'border-zinc-500 bg-zinc-900 text-white group-hover:bg-zinc-800' : 'border-zinc-300 bg-zinc-200 text-zinc-700 group-hover:bg-zinc-300'}`}
                        >
                            {rightCollapsed ? <ChevronLeftIcon className={`h-3.5 w-3.5 ${isDark ? 'text-white' : 'text-zinc-700'}`} /> : <ChevronRightIcon className={`h-3.5 w-3.5 ${isDark ? 'text-white' : 'text-zinc-700'}`} />}
                        </button>
                    </div>
                </div>
                <aside ref={rightAsideRef} className={`right-panel hidden xl:flex flex-shrink-0 flex-col self-stretch min-h-screen h-auto border-l pt-2 ${isDark ? 'bg-[#1d1d20] border-zinc-800/60 text-zinc-100' : 'bg-zinc-100 border-zinc-200/70 text-zinc-800'} ${rightCollapsed ? 'opacity-0 pointer-events-none overflow-hidden' : 'opacity-100'}`} style={{ width: rightCollapsed ? 0 : rightWidth }}>
                    <RightPanelBody />
                </aside>
            </div>

            {/* Overlay drawer for < xl */}
            {rightOpen && (
                <div className="xl:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setRightOpen(false)} />
                    <div className={`${isDark ? 'absolute right-0 top-0 h-full w-[24rem] max-w-[85vw] bg-[#1d1d20] border-l border-zinc-800 text-zinc-100 shadow-xl flex flex-col' : 'absolute right-0 top-0 h-full w-[24rem] max-w-[85vw] bg-zinc-100 border-l border-zinc-200 shadow-xl flex flex-col'}`}>
                        <button
                            type="button"
                            aria-label="Close panel"
                            onClick={() => setRightOpen(false)}
                            className={`${isDark ? 'absolute top-2 right-2 p-2 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'absolute top-2 right-2 p-2 rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                        <RightPanelBody />
                    </div>
                </div>
            )}

            {/* Left overlay drawer for < lg */}
            {leftOpen && (
                <div className="lg:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setLeftOpen(false)} />
                    <div className={`${isDark ? 'absolute left-0 top-0 h-full w-64 max-w-[85vw] bg-[#1d1d20] border-r border-zinc-800 text-zinc-100 shadow-xl flex flex-col' : 'absolute left-0 top-0 h-full w-64 max-w-[85vw] bg-white border-r border-zinc-200 text-zinc-800 shadow-xl flex flex-col'}`}>
                        <button
                            type="button"
                            aria-label="Close panel"
                            onClick={() => setLeftOpen(false)}
                            className={`${isDark ? 'absolute top-2 right-2 p-2 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'absolute top-2 right-2 p-2 rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                        <LeftPanelBodyTop
                            days={days}
                            tradesByDay={tradesByDay}
                            selectedDay={validSelectedDay}
                            onPickDay={(d) => { setSelectedDay(d); setLeftOpen(false); }}
                            searchQuery={searchQuery}
                            onChangeSearch={setSearchQuery}
                            isDark={isDark}
                        />
                    </div>
                </div>
            )}
        {/* Saved Note Preview Modal */}
    {previewNote ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/50" onClick={() => setPreviewNote(null)} />
                <div className={`${isDark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white'} rounded-lg shadow-xl w-full max-w-3xl relative`}>
                    <div className={`p-4 flex justify-between items-start ${isDark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                        <div className="min-w-0">
                            <h3 className={`text-lg font-semibold break-words ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{previewNote.title}</h3>
                            <div className={`mt-1 text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                <span>{(() => {
                                    const formatted = formatDateTimeInTimezone(previewNote.ts, displayTimeZone);
                                    return `${formatted.dateStr} ${formatted.timeStr}`.trim();
                                })()}</span>
                                {previewNote.symbol ? <span className="ml-2">• {previewNote.symbol}</span> : null}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {!previewNoteReadOnly ? (
                                <>
                                    <button
                                        type="button"
                                        title="Edit"
                                        onClick={() => {
                                            if (!previewNote) return;
                                            setPreviewEditHtml(previewNote.html);
                                            setIsPreviewEditing(true);
                                        }}
                                        className={`${isDark ? 'p-2 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'p-2 rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}
                                    >
                                        <FilePenLine className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        title="Delete"
                                        onClick={() => {
                                            if (!validSelectedDay || !previewNote) return;
                                            setDeleteConfirm({ id: previewNote.id, title: previewNote.title || 'Untitled note', date: validSelectedDay, symbol: previewNote.symbol });
                                        }}
                                        className={`${isDark ? 'p-2 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'p-2 rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </>
                            ) : null}
                            <button aria-label="Close" onClick={() => setPreviewNote(null)} className={`${isDark ? 'p-2 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'p-2 rounded-md text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100'}`}>
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="scrollable p-4 overflow-auto max-h-[70vh]">
                        {isPreviewEditing ? (
                            <div className="-mt-2">
                                <RichTextEditor
                                    ref={previewEditorInstanceRef}
                                    value={previewEditHtml}
                                    isDark={isDark}
                                    onChange={(html) => setPreviewEditHtml(html)}
                                />
                            </div>
                        ) : (
                            <div className={`${isDark ? 'prose-invert' : ''} prose max-w-none`} dangerouslySetInnerHTML={{ __html: previewNote.html }} />
                        )}
                    </div>
                    <div className={`${isDark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'} p-3 flex justify-end gap-2`}>
                        {isPreviewEditing ? (
                            <>
                                <button
                                    onClick={() => { setIsPreviewEditing(false); setPreviewEditHtml(''); }}
                                    className={`${isDark ? 'px-4 py-2 rounded-lg text-sm bg-zinc-800 hover:bg-zinc-700' : 'px-4 py-2 rounded-lg text-sm bg-zinc-100 hover:bg-zinc-200'}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (!previewNote || !validSelectedDay) return;
                                        const cleaned = cleanEditorHtml(previewEditHtml).trim();
                                        if (!cleaned) return;
                                        const ok = handleUpdateSavedNote(validSelectedDay, previewNote.id, cleaned, previewNote.symbol || previewNote.title, previewNote.symbol, previewNote.tradeKey);
                                        if (ok) {
                                            setPreviewNote(prev => prev ? { ...prev, html: cleaned, title: prev.symbol || prev.title } : prev);
                                            setIsPreviewEditing(false);
                                        }
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold ${isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                >
                                    Save
                                </button>
                            </>
                        ) : (
                            <button onClick={() => setPreviewNote(null)} className={`${isDark ? 'px-4 py-2 rounded-lg text-sm bg-zinc-800 hover:bg-zinc-700' : 'px-4 py-2 rounded-lg text-sm bg-zinc-100 hover:bg-zinc-200'}`}>Close</button>
                        )}
                    </div>
                </div>
            </div>
    ) : null}

    {/* Personalized Delete Confirmation Modal */}
    {deleteConfirm ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
            <div className={`${isDark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white'} rounded-xl shadow-xl w-full max-w-md relative`}>
                <div className={`p-4 ${isDark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                    <h3 className="text-base font-semibold">Delete note?</h3>
                </div>
                <div className="p-4 space-y-2">
                    <p className={`${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>You're about to delete:</p>
                    <div className={`${isDark ? 'bg-zinc-800 border border-zinc-700' : 'bg-zinc-50 border border-zinc-200'} rounded-lg p-3`}>
                        <div className="text-sm font-semibold truncate" title={deleteConfirm.title}>{deleteConfirm.title}</div>
                        <div className="text-xs mt-1 text-zinc-500">
                            <span>{deleteConfirm.date}</span>
                            {deleteConfirm.symbol ? <span className="ml-2">• {deleteConfirm.symbol}</span> : null}
                        </div>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>This action cannot be undone.</p>
                </div>
                <div className={`p-4 flex justify-end gap-2 ${isDark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'}`}>
                    <button onClick={() => setDeleteConfirm(null)} className={`${isDark ? 'px-4 py-2 rounded-lg text-sm bg-zinc-800 hover:bg-zinc-700' : 'px-4 py-2 rounded-lg text-sm bg-zinc-100 hover:bg-zinc-200'}`}>Cancel</button>
                    <button
                        onClick={() => {
                            const ctx = deleteConfirm; if (!ctx) return;
                            setNotesHistoryByDate(prev => ({
                                ...prev,
                                [ctx.date]: (prev[ctx.date] || []).filter(x => x.id !== ctx.id)
                            }));
                            // If we were editing or previewing this note, reset those states
                            if (editingNoteId === ctx.id) {
                                setEditingNoteId(null);
                                setEditingNoteName('');
                                setEditingNoteSymbol('');
                            }
                            if (previewNote && previewNote.id === ctx.id) {
                                setPreviewNote(null);
                            }
                            setDeleteConfirm(null);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold ${isDark ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'}`}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    ) : null}
    </div>
    );
}
